import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import * as XLSX from 'xlsx';
import JudgeUI from './components/JudgeUI';
import Dashboard from './components/Dashboard';
import ScoreRanking from './components/ScoreRanking';
import { API_BASE_URL } from './config';

type ViewMode = 'judge' | 'dashboard' | 'score-ranking' | 'admin';
type AdminTab = 'temp-admin' | 'venues' | 'group-order' | 'members' | 'archives' | 'deleted';
type MemberSortTag = 'role' | 'campaign' | 'venue' | 'lock';
type CampaignGuideFormat = 'text' | 'markdown';

type Role = 'super_admin' | 'admin' | 'judge' | 'investor';

const NO_CAMPAIGN_FILTER = '__NO_CAMPAIGN__';
const TOKEN_STORAGE_KEY = 'fundthepitch_auth_token';
const USER_STORAGE_KEY = 'fundthepitch_auth_user';
const DISPLAY_NAME_SESSION_KEY = 'fundthepitch_display_name';
const LAST_ADMIN_USERNAME_STORAGE_KEY = 'fundthepitch_last_admin_username';
const LOGIN_MODE_STORAGE_KEY = 'fundthepitch_login_mode';
const AMBIENT_BOOKMARKS_STORAGE_KEY = 'fundthepitch_ambient_sound_bookmarks';
const TEMP_ADMIN_CREDENTIAL_HISTORY_STORAGE_KEY = 'fundthepitch_temp_super_admin_credentials';
const DEFAULT_AMBIENT_YOUTUBE_URL = 'https://www.youtube.com/watch?v=_dbfFJ_WcPc';
const DEFAULT_AMBIENT_SOUND_BOOKMARKS: AmbientSoundBookmark[] = [
  { id: 'default-nightcore', label: 'Gaming Nightcore Mix', url: DEFAULT_AMBIENT_YOUTUBE_URL, note: '預設' },
  { id: 'default-lofi', label: 'Lofi Hip Hop Radio', url: 'https://www.youtube.com/watch?v=jfKfPfyJRdk', note: '預設' },
  { id: 'default-epic', label: 'Epic Gaming Mix', url: 'https://www.youtube.com/watch?v=0vVzA7n6Q2g', note: '預設' },
];

const extractYouTubeVideoId = (value: string): string | null => {
  const raw = value.trim();
  if (!raw) {
    return null;
  }

  if (/^[A-Za-z0-9_-]{11}$/.test(raw)) {
    return raw;
  }

  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.toLowerCase();

    if (host === 'youtu.be') {
      const id = parsed.pathname.replace(/^\//, '').split('/')[0];
      return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
    }

    if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      if (parsed.pathname === '/watch') {
        const id = parsed.searchParams.get('v') || '';
        return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
      }

      const pathSegments = parsed.pathname.split('/').filter(Boolean);
      if ((pathSegments[0] === 'embed' || pathSegments[0] === 'shorts') && pathSegments[1]) {
        const id = pathSegments[1];
        return /^[A-Za-z0-9_-]{11}$/.test(id) ? id : null;
      }
    }
  } catch {
    return null;
  }

  return null;
};

const buildYouTubeEmbedUrl = (value: string): string | null => {
  const videoId = extractYouTubeVideoId(value);
  if (!videoId) {
    return null;
  }
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
};

const isSameYouTubeSource = (left: string, right: string): boolean => {
  const leftId = extractYouTubeVideoId(left);
  const rightId = extractYouTubeVideoId(right);
  if (leftId && rightId) {
    return leftId === rightId;
  }
  return left.trim() === right.trim();
};

interface AmbientSoundBookmark {
  id: string;
  label: string;
  url: string;
  note: string;
}

interface Venue {
  id: string;
  name: string;
  classroom: string;
  judges: string[];
  projects: string[];
}

interface AuthUser {
  user_id: string;
  identifier: string;
  role: Role;
  display_name: string;
  venue_id?: string | null;
  campaign_year?: number | null;
  campaign_id?: string | null;
  department?: string | null;
  must_complete_profile?: boolean;
  can_generate_super_admin?: boolean;
  is_root_super_admin?: boolean;
}

interface AuthResponse {
  access_token: string;
  token_type: string;
  user: AuthUser;
}

interface JudgeStatus {
  identifier: string;
  display_name: string;
  role: Role;
  assigned_venue_id?: string | null;
  is_voted: boolean;
  campaign_year?: number | null;
  campaign_id?: string | null;
  judge_panel_note_title?: string | null;
  judge_panel_note_content?: string | null;
  judge_panel_note_format?: CampaignGuideFormat | null;
}

interface AdminMember {
  identifier: string;
  display_name: string;
  role: Role;
  assigned_venue_id?: string | null;
  manager_identifier?: string | null;
  managed_campaign_id?: string | null;
  start_project_id?: string | null;
  is_voted: boolean;
  campaign_year?: number | null;
  campaign_id?: string | null;
}

interface GroupOrderProject {
  id: string;
  name: string;
}

interface VenueGroupOrderResponse {
  campaign_id: string;
  venue_id: string;
  venue_name: string;
  project_order: GroupOrderProject[];
}

interface SystemCampaign {
  id: string;
  year: number;
  label: string;
  status: 'active' | 'closed';
  started_at: string;
  closed_at?: string | null;
  close_at?: string | null;
  invite_token?: string | null;
  judge_budget?: number | null;
  investor_budget?: number | null;
  judge_panel_note_title?: string | null;
  judge_panel_note_content?: string | null;
  judge_panel_note_format?: CampaignGuideFormat | null;
  summary?: {
    overall_total_investment?: number;
    venues?: Array<{
      venue_id: string;
      venue_name: string;
      total_investment: number;
      judge_count?: number;
      locked_count?: number;
      judge_member_count?: number;
      judge_locked_count?: number;
      investor_member_count?: number;
      investor_locked_count?: number;
      projects?: Array<{
        project_id: string;
        project_name: string;
        total_investment: number;
        rank?: number;
        score_rank?: number;
        total_score?: number;
        avg_score?: number;
      }>;
    }>;
    overall_score_ranking?: Array<{
      venue_id: string;
      venue_name: string;
      project_id: string;
      project_name: string;
      total_investment: number;
      total_score?: number;
      avg_score?: number;
      score_rank?: number;
    }>;
    overall_project_ranking?: Array<{
      venue_id: string;
      venue_name: string;
      project_id: string;
      project_name: string;
      total_investment: number;
      rank?: number;
      total_score?: number;
      avg_score?: number;
    }>;
  };
}

interface RecentlyDeletedCampaign extends SystemCampaign {
  deleted_at: string;
  restore_deadline: string;
  days_remaining: number;
}

interface AdminSystemState {
  current_campaign?: SystemCampaign | null;
  active_campaigns_list?: SystemCampaign[];
  campaigns_by_year: Record<string, SystemCampaign[]>;
  recently_deleted_by_year: Record<string, RecentlyDeletedCampaign[]>;
}

interface AdminMembersResponse {
  members: AdminMember[];
  year: number;
  campaign_id?: string | null;
}

interface CampaignOption {
  id: string;
  year: number;
  label: string;
  status: 'active' | 'closed';
  started_at: string;
}

interface CampaignInviteInfo {
  id: string;
  year: number;
  label: string;
  status: 'active' | 'closed';
}

interface TempSuperAdminAccount {
  identifier: string;
  username: string;
  display_name: string;
  department?: string | null;
  access_until: string;
  first_login_completed: boolean;
  is_active: boolean;
}

interface TempSuperAdminCreateResult {
  identifier: string;
  username: string;
  password: string;
  display_name: string;
  department?: string | null;
  access_until: string;
}

interface TempSuperAdminCredentialSnapshot extends TempSuperAdminCreateResult {
  created_at: string;
}


interface VenueProject {
  id: string;
  name: string;
  total_investment: number;
}

interface VenueProjectsResponse {
  projects: VenueProject[];
  total_budget: number;
  remaining_budget: number;
}

interface JudgeEvaluationStatus {
  identifier: string;
  display_name: string;
  status: 'red' | 'yellow' | 'green';
  total_projects: number;
  invested_projects: number;
  scored_projects: number;
  is_voted: boolean;
}

interface JudgeEvaluationStatusList {
  judges: JudgeEvaluationStatus[];
  campaign_id: string;
  campaign_year: number;
}

interface MemberProjectDetail {
  id: string;
  name: string;
  invested: number;
  score: number;
}

interface MemberProjectDetailModal {
  identifier: string;
  displayName: string;
  role: string;
}

interface ImportStatus {
  tone: 'idle' | 'loading' | 'success' | 'error';
  text: string;
}

const normalizeDisplayName = (value: string): string => value.trim().replace(/\s+/g, ' ');
const buildNameIdentifier = (displayName: string): string => `name::${displayName.toLowerCase()}`;
const normalizeIdentifierForDisplay = (identifier: string): string => identifier.replace(/^name::/i, '');
const formatMemberIdentity = (identifier: string, displayName: string): string => {
  // Clean display_name by removing "name::" prefix
  const cleanedName = displayName.startsWith('name::') ? displayName.substring(6) : displayName;
  const shownIdentifier = normalizeIdentifierForDisplay(identifier).trim();
  const shownName = cleanedName.trim();

  if (!shownIdentifier) {
    return shownName;
  }
  if (!shownName) {
    return shownIdentifier;
  }

  // If identifier is the name-based key (name::xxx), avoid duplicate rendering like "gg-GG".
  if (identifier.startsWith('name::') && shownIdentifier.toLowerCase() === shownName.toLowerCase()) {
    return shownName;
  }

  return `${shownIdentifier}-${shownName}`;
};
const isAdminRole = (role?: Role | null): boolean => role === 'super_admin' || role === 'admin';
const isSuperAdmin = (role?: Role | null): boolean => role === 'super_admin';
const isInvestmentMemberRole = (role?: Role | null): boolean => role === 'judge' || role === 'investor';
const normalizeBudgetInput = (value: string | number): number => {
  const numeric = typeof value === 'number'
    ? value
    : Number(String(value).replace(/[^\d]/g, ''));

  if (!Number.isFinite(numeric) || numeric <= 0) {
    return 10000;
  }

  const rounded = Math.round(numeric / 500) * 500;
  return Math.max(1000, Math.min(1000000, rounded || 10000));
};
const roleLabel = (role?: Role | null): string => {
  if (role === 'super_admin') return '贊助者';
  if (role === 'admin') return '工作人員';
  if (role === 'investor') return '投資客';
  return '評審';
};

function App() {
  // Helper function to clean display_name by removing "name::" prefix
  const cleanDisplayName = (displayName: string | undefined): string => {
    if (!displayName) return '';
    if (displayName.startsWith('name::')) {
      return displayName.substring(6); // Remove "name::" prefix
    }
    return displayName;
  };

  const [currentView, setCurrentView] = useState<ViewMode>(() => {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    if (!raw) {
      return 'judge';
    }
    try {
      const storedUser = JSON.parse(raw) as AuthUser;
      return isAdminRole(storedUser?.role) ? 'admin' : 'judge';
    } catch {
      return 'judge';
    }
  });
  const [authToken, setAuthToken] = useState<string | null>(localStorage.getItem(TOKEN_STORAGE_KEY));
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  });
  const [judgeStatus, setJudgeStatus] = useState<JudgeStatus | null>(null);

  const [displayNameInput, setDisplayNameInput] = useState<string>(() => sessionStorage.getItem(DISPLAY_NAME_SESSION_KEY) || '');
  const [loginMode, setLoginMode] = useState<'member' | 'admin'>(() => {
    const saved = localStorage.getItem(LOGIN_MODE_STORAGE_KEY);
    return (saved === 'member' || saved === 'admin') ? saved : 'member';
  });
  const [adminLoginUsername, setAdminLoginUsername] = useState<string>(() => localStorage.getItem(LAST_ADMIN_USERNAME_STORAGE_KEY) || '');
  const [adminLoginPassword, setAdminLoginPassword] = useState('');
  const [isShowAdminPassword, setIsShowAdminPassword] = useState(false);
  const [isShowFirstLoginPassword, setIsShowFirstLoginPassword] = useState(false);
  const [isShowFirstLoginConfirmPassword, setIsShowFirstLoginConfirmPassword] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(false);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'ready' | 'error'>('checking');

  const [venues, setVenues] = useState<Venue[]>([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [dashboardVenueId, setDashboardVenueId] = useState('');
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isScorePresentationMode, setIsScorePresentationMode] = useState(false);

  const [members, setMembers] = useState<AdminMember[]>([]);
  const [newVenueName, setNewVenueName] = useState('');
  const [campaignLabelInput, setCampaignLabelInput] = useState('');
  const [campaignBudgetInput, setCampaignBudgetInput] = useState('10000');
  const [campaignInvestorBudgetInput, setCampaignInvestorBudgetInput] = useState('');
  const [campaignCloseInHours, setCampaignCloseInHours] = useState<string>('48');
  const [campaignBudgetDrafts, setCampaignBudgetDrafts] = useState<Record<string, string>>({});
  const [campaignInvestorBudgetDrafts, setCampaignInvestorBudgetDrafts] = useState<Record<string, string>>({});
  const [editingCampaignGuide, setEditingCampaignGuide] = useState<SystemCampaign | null>(null);
  const [campaignGuideTitleDraft, setCampaignGuideTitleDraft] = useState('');
  const [campaignGuideContentDraft, setCampaignGuideContentDraft] = useState('');
  const [campaignGuideFormatDraft, setCampaignGuideFormatDraft] = useState<CampaignGuideFormat>('text');
  const [isSavingCampaignGuide, setIsSavingCampaignGuide] = useState(false);
  const [selectedMemberCampaignId, setSelectedMemberCampaignId] = useState<string>('');
  const [adminSystemState, setAdminSystemState] = useState<AdminSystemState>({
    active_campaigns_list: [],
    campaigns_by_year: {},
    recently_deleted_by_year: {}
  });
  const [adminTab, setAdminTab] = useState<AdminTab>('members');
  const [isHeroPanelOpen, setIsHeroPanelOpen] = useState(true);
  const [isAdminProgressOpen, setIsAdminProgressOpen] = useState(true);
  const [isAdminProgressRefreshing, setIsAdminProgressRefreshing] = useState(false);
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [importStatus, setImportStatus] = useState<ImportStatus>({
    tone: 'idle',
    text: '請先下載範本，填寫後再上傳。'
  });
  const [venueImportFileLabel, setVenueImportFileLabel] = useState('未選擇任何檔案');
  const [memberEditName, setMemberEditName] = useState<Record<string, string>>({});
  const [memberEditRole, setMemberEditRole] = useState<Record<string, Role>>({});
  const [setupVenueNameDraft, setSetupVenueNameDraft] = useState('');
  const [isVenueSetupOpen, setIsVenueSetupOpen] = useState(false);
  const [isQuickstartOpen, setIsQuickstartOpen] = useState(false);
  const [setupVenueId, setSetupVenueId] = useState('');
  const [setupProjectDraft, setSetupProjectDraft] = useState('');
  const [setupSelectedMemberIds, setSetupSelectedMemberIds] = useState<string[]>([]);
  const [setupNewMemberDraft, setSetupNewMemberDraft] = useState('');
  const [isSavingVenueSetup, setIsSavingVenueSetup] = useState(false);
  const [pendingArchiveDelete, setPendingArchiveDelete] = useState<SystemCampaign | null>(null);
  const [isDeletingArchive, setIsDeletingArchive] = useState(false);
  const [downloadingReportCampaignId, setDownloadingReportCampaignId] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isJoiningVenue, setIsJoiningVenue] = useState(false);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isLoadingAdminData, setIsLoadingAdminData] = useState(false);
  const [isCreatingVenue, setIsCreatingVenue] = useState(false);
  const [deletingVenueId, setDeletingVenueId] = useState<string | null>(null);
  const [pendingVenueDelete, setPendingVenueDelete] = useState<Venue | null>(null);
  const [selectedHistoryCampaign, setSelectedHistoryCampaign] = useState<SystemCampaign | null>(null);
  const [isStartingCampaign, setIsStartingCampaign] = useState(false);
  const [updatingCampaignBudgetId, setUpdatingCampaignBudgetId] = useState<string | null>(null);
  const [closingCampaignId, setClosingCampaignId] = useState<string | null>(null);
  const [pendingCloseCampaign, setPendingCloseCampaign] = useState<SystemCampaign | null>(null);
  const [restoringCampaignId, setRestoringCampaignId] = useState<string | null>(null);
  const [permanentDeletingCampaignId, setPermanentDeletingCampaignId] = useState<string | null>(null);
  const [pendingPermanentDelete, setPendingPermanentDelete] = useState<RecentlyDeletedCampaign | null>(null);
  const [pendingMemberDelete, setPendingMemberDelete] = useState<AdminMember | null>(null);
  const [viewVenueId, setViewVenueId] = useState<string | null>(null);
  const [qrInviteCampaign, setQrInviteCampaign] = useState<SystemCampaign | null>(null);
  const [venueViewProjects, setVenueViewProjects] = useState<VenueProject[]>([]);
  const [isLoadingVenueView, setIsLoadingVenueView] = useState(false);
  const [updatingMemberId, setUpdatingMemberId] = useState<string | null>(null);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [unlockingMemberId, setUnlockingMemberId] = useState<string | null>(null);
  const [lockingAllCampaignId, setLockingAllCampaignId] = useState<string | null>(null);
  const [pendingLockAllCampaignId, setPendingLockAllCampaignId] = useState<string | null>(null);
  const [isResettingRound, setIsResettingRound] = useState(false);
  const [pendingResetRound, setPendingResetRound] = useState(false);
  const [assigningMemberId, setAssigningMemberId] = useState<string | null>(null);
  const [draggingCampaignMemberId, setDraggingCampaignMemberId] = useState<string | null>(null);
  const [draggingJudgeId, setDraggingJudgeId] = useState<string | null>(null);
  const [pendingMoveJudge, setPendingMoveJudge] = useState<{ judge: AdminMember; managerIdentifier: string | null; managerName: string } | null>(null);
  const [loadingMoveJudge, setLoadingMoveJudge] = useState<AdminMember | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [isEditingMember, setIsEditingMember] = useState(false);
  const [newMemberName, setNewMemberName] = useState('');
  const [newMemberRole, setNewMemberRole] = useState<'judge' | 'investor' | 'admin'>('judge');
  const [newMemberVenueId, setNewMemberVenueId] = useState<string>('');
  const [newMemberCampaignId, setNewMemberCampaignId] = useState<string>('');
  const [isCreatingMember, setIsCreatingMember] = useState(false);
  const [isCreateMemberModalOpen, setIsCreateMemberModalOpen] = useState(false);
  const [memberEditManager, setMemberEditManager] = useState<Record<string, string>>({});
  const [memberEditStartProject, setMemberEditStartProject] = useState<Record<string, string>>({});
  const [memberSortTag, setMemberSortTag] = useState<MemberSortTag>('role');
  const [tempAdminValidHours, setTempAdminValidHours] = useState('720');
  const [isCreatingTempAdmin, setIsCreatingTempAdmin] = useState(false);
  const [tempSuperAdminAccounts, setTempSuperAdminAccounts] = useState<TempSuperAdminAccount[]>([]);
  const [updatingTempAdminId, setUpdatingTempAdminId] = useState<string | null>(null);
  const [deletingTempAdminId, setDeletingTempAdminId] = useState<string | null>(null);
  const [resettingTempAdminId, setResettingTempAdminId] = useState<string | null>(null);
  const [tempAdminExtendHours, setTempAdminExtendHours] = useState<Record<string, string>>({});
  const [judgeEvaluationStatusMap, setJudgeEvaluationStatusMap] = useState<Record<string, JudgeEvaluationStatus>>({});
  const judgeStatusRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [pendingProgressUnlock, setPendingProgressUnlock] = useState<{ identifier: string; displayName: string; role: string } | null>(null);
  const [progressDetailModal, setProgressDetailModal] = useState<MemberProjectDetailModal | null>(null);
  const [progressDetailProjects, setProgressDetailProjects] = useState<MemberProjectDetail[]>([]);
  const [isLoadingProgressDetail, setIsLoadingProgressDetail] = useState(false);
  const [latestCreatedTempAdmin, setLatestCreatedTempAdmin] = useState<TempSuperAdminCreateResult | null>(null);
  const [tempAdminCredentialHistory, setTempAdminCredentialHistory] = useState<TempSuperAdminCredentialSnapshot[]>(() => {
    if (typeof window === 'undefined') {
      return [];
    }

    const raw = window.localStorage.getItem(TEMP_ADMIN_CREDENTIAL_HISTORY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return [];
      }
      return parsed
        .map((row) => ({
          identifier: typeof row?.identifier === 'string' ? row.identifier : '',
          username: typeof row?.username === 'string' ? row.username : '',
          password: typeof row?.password === 'string' ? row.password : '',
          display_name: typeof row?.display_name === 'string' ? row.display_name : '',
          department: typeof row?.department === 'string' ? row.department : null,
          access_until: typeof row?.access_until === 'string' ? row.access_until : '',
          created_at: typeof row?.created_at === 'string' ? row.created_at : '',
        }))
        .filter((row) => row.identifier && row.username && row.password && row.created_at)
        .slice(0, 20);
    } catch {
      return [];
    }
  });
  const [pendingTempPasswordReset, setPendingTempPasswordReset] = useState<TempSuperAdminAccount | null>(null);
  const [firstLoginSetupUsername, setFirstLoginSetupUsername] = useState('');
  const [firstLoginSetupPassword, setFirstLoginSetupPassword] = useState('');
  const [firstLoginSetupConfirmPassword, setFirstLoginSetupConfirmPassword] = useState('');
  const [firstLoginSetupDisplayName, setFirstLoginSetupDisplayName] = useState('');
  const [firstLoginSetupDepartment, setFirstLoginSetupDepartment] = useState('');
  const [isSavingFirstLoginSetup, setIsSavingFirstLoginSetup] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isAdminTabsOpen, setIsAdminTabsOpen] = useState(false);
  const [groupOrderVenueId, setGroupOrderVenueId] = useState<string>('');
  const [groupOrderProjects, setGroupOrderProjects] = useState<GroupOrderProject[]>([]);
  // 新增：記錄原始順序
  const [originalGroupOrderProjects, setOriginalGroupOrderProjects] = useState<GroupOrderProject[]>([]);
  const [draggingGroupOrderProjectId, setDraggingGroupOrderProjectId] = useState<string | null>(null);
  const [isLoadingGroupOrder, setIsLoadingGroupOrder] = useState(false);
  const [isSavingGroupOrder, setIsSavingGroupOrder] = useState(false);
  const [venueProjectOrderCache, setVenueProjectOrderCache] = useState<Record<string, GroupOrderProject[]>>({});
  const [ambientSoundInput, setAmbientSoundInput] = useState(DEFAULT_AMBIENT_YOUTUBE_URL);
  const [ambientSoundEmbedUrl, setAmbientSoundEmbedUrl] = useState<string | null>(
    buildYouTubeEmbedUrl(DEFAULT_AMBIENT_YOUTUBE_URL)
  );
  const [ambientSoundSourceUrl, setAmbientSoundSourceUrl] = useState(DEFAULT_AMBIENT_YOUTUBE_URL);
  const [ambientSoundBookmarks, setAmbientSoundBookmarks] = useState<AmbientSoundBookmark[]>(() => {
    if (typeof window === 'undefined') {
      return DEFAULT_AMBIENT_SOUND_BOOKMARKS;
    }

    const raw = window.localStorage.getItem(AMBIENT_BOOKMARKS_STORAGE_KEY);
    if (!raw) {
      return DEFAULT_AMBIENT_SOUND_BOOKMARKS;
    }

    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return DEFAULT_AMBIENT_SOUND_BOOKMARKS;
      }
      const normalized = parsed
        .map((item) => ({
          id: typeof item?.id === 'string' ? item.id : '',
          label: typeof item?.label === 'string' ? item.label.trim() : '',
          url: typeof item?.url === 'string' ? item.url.trim() : '',
          note: typeof item?.note === 'string' ? item.note.trim() : '',
        }))
        .filter((item) => item.id && item.label && item.url && buildYouTubeEmbedUrl(item.url));

      return normalized.length > 0 ? normalized : DEFAULT_AMBIENT_SOUND_BOOKMARKS;
    } catch {
      return DEFAULT_AMBIENT_SOUND_BOOKMARKS;
    }
  });
  const [isAmbientBookmarkModalOpen, setIsAmbientBookmarkModalOpen] = useState(false);
  const [draggingAmbientBookmarkId, setDraggingAmbientBookmarkId] = useState<string | null>(null);
  const [ambientBookmarkForm, setAmbientBookmarkForm] = useState({
    label: '',
    url: '',
    note: '',
  });
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profileDisplayNameDraft, setProfileDisplayNameDraft] = useState('');
  const [profileDepartmentDraft, setProfileDepartmentDraft] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Invite-link state: populated when the user arrives via ?c=TOKEN
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteCampaign, setInviteCampaign] = useState<CampaignInviteInfo | null>(null);

  // Refs that always reflect the latest render values, used inside useCallbacks
  // to avoid those callbacks being recreated on every state change (infinite loop prevention).
  const selectedMemberCampaignIdRef = useRef<string>('');
  const activeCampaignIdRef = useRef<string | undefined>(undefined);
  const profileBackdropPressStartedRef = useRef(false);
  const isCampaignActiveRef = useRef<boolean>(false);
  const adminMembersSectionRef = useRef<HTMLElement | null>(null);
  const adminVenuesSectionRef = useRef<HTMLElement | null>(null);
  const userCampaignIdRef = useRef<string | undefined>(undefined);
  const inviteTokenRef = useRef<string | null>(null);
  const lastLoadedMembersCampaignIdRef = useRef<string>('');

  const authHeaders = useMemo(() => {
    if (!authToken) {
      return undefined;
    }
    return { Authorization: `Bearer ${authToken}` };
  }, [authToken]);

  const selectedVenue = useMemo(
    () => venues.find((venue) => venue.id === selectedVenueId),
    [venues, selectedVenueId]
  );

  const activeCampaign = useMemo<SystemCampaign | null>(() => {
    if (!isAdminRole(authUser?.role)) {
      return null;
    }
    return adminSystemState.current_campaign
      ?? adminSystemState.active_campaigns_list?.[0]
      ?? null;
  }, [adminSystemState.current_campaign, adminSystemState.active_campaigns_list, authUser?.role]);
  const adminHasActiveCampaign = Boolean(activeCampaign);
  const isSuperAdminUser = isSuperAdmin(authUser?.role);
  const currentUserCampaignId = judgeStatus?.campaign_id || authUser?.campaign_id || null;
  const isJudgeCampaignActive = Boolean(currentUserCampaignId || inviteCampaign?.status === 'active');
  const isCampaignActive = isAdminRole(authUser?.role) ? adminHasActiveCampaign : isJudgeCampaignActive;
  const hasConfiguredVenue = venues.length > 0;
  const disableTopCampaignTabs = !isCampaignActive || !hasConfiguredVenue;
  const canBrowseVenues = isCampaignActive || isSuperAdminUser;
  const canManageVenues = isCampaignActive || isSuperAdminUser;
  const canStartAnotherCampaign = isSuperAdmin(authUser?.role)
    && (Boolean(authUser?.is_root_super_admin) || !adminHasActiveCampaign);
  // Keep refs in sync so callbacks always read the latest values.
  selectedMemberCampaignIdRef.current = selectedMemberCampaignId;
  activeCampaignIdRef.current = activeCampaign?.id;
  isCampaignActiveRef.current = adminHasActiveCampaign;
  userCampaignIdRef.current = currentUserCampaignId || undefined;
  inviteTokenRef.current = inviteToken;
  const campaignOptions = useMemo(() => {
    const rows: CampaignOption[] = [];
    const seen = new Set<string>();

    (adminSystemState.active_campaigns_list ?? []).forEach((campaign) => {
      if (!seen.has(campaign.id)) {
        rows.push(campaign);
        seen.add(campaign.id);
      }
    });

    if (adminSystemState.current_campaign?.id) {
      if (!seen.has(adminSystemState.current_campaign.id)) {
        rows.push(adminSystemState.current_campaign);
        seen.add(adminSystemState.current_campaign.id);
      }
    }

    Object.values(adminSystemState.campaigns_by_year).forEach((campaigns) => {
      campaigns.forEach((campaign) => {
        if (!seen.has(campaign.id)) {
          rows.push(campaign);
          seen.add(campaign.id);
        }
      });
    });

    return rows.sort((a, b) => {
      const aTime = new Date(a.started_at).getTime();
      const bTime = new Date(b.started_at).getTime();
      return bTime - aTime;
    });
  }, [adminSystemState]);

  const displayedActiveCampaigns = useMemo(() => {
    const allActive = adminSystemState.active_campaigns_list ?? [];
    if (isSuperAdmin(authUser?.role)) {
      return allActive;
    }
    if (!activeCampaign) {
      return [];
    }
    return allActive.filter((campaign) => campaign.id === activeCampaign.id);
  }, [adminSystemState.active_campaigns_list, authUser?.role, activeCampaign]);

  const venueCampaignOptions = useMemo(() => {
    const activeById = new Map((adminSystemState.active_campaigns_list ?? []).map((campaign) => [campaign.id, campaign]));
    if (!isSuperAdmin(authUser?.role)) {
      return activeCampaign ? [activeCampaign] : [];
    }
    return Array.from(activeById.values()).sort(
      (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
    );
  }, [adminSystemState.active_campaigns_list, authUser?.role, activeCampaign]);

  const groupOrderCampaignId = useMemo(() => {
    if (selectedMemberCampaignId && selectedMemberCampaignId !== NO_CAMPAIGN_FILTER) {
      return selectedMemberCampaignId;
    }
    return activeCampaign?.id || venueCampaignOptions[0]?.id || '';
  }, [selectedMemberCampaignId, activeCampaign?.id, venueCampaignOptions]);

  const activeGroupOrderVenue = useMemo(
    () => venues.find((venue) => venue.id === groupOrderVenueId) || null,
    [groupOrderVenueId, venues]
  );

  const judgeMembers = members.filter((member) => isInvestmentMemberRole(member.role));
  const judgeOnlyMembers = members.filter((member) => member.role === 'judge');
  const investorMembers = members.filter((member) => member.role === 'investor');
  const adminMembers = members.filter((member) => member.role === 'admin');
  const scoreRankingJudgeIdentifiers = useMemo(() => {
    if (!dashboardVenueId) {
      return [] as string[];
    }
    return members
      .filter((member) => member.role === 'judge' && member.assigned_venue_id === dashboardVenueId)
      .map((member) => member.identifier);
  }, [dashboardVenueId, members]);
  const isRootSponsor = Boolean(authUser?.role === 'super_admin' && authUser?.is_root_super_admin);
  const sponsorAccountCount = useMemo(() => {
    if (!isRootSponsor) {
      return 0;
    }
    return tempSuperAdminAccounts.length + 1;
  }, [isRootSponsor, tempSuperAdminAccounts.length]);
  const adminHeroMetrics = useMemo(() => {
    if (isRootSponsor) {
      return [
        { label: '進行中場次', value: displayedActiveCampaigns.length },
        { label: '會議廳數量', value: venues.length },
        { label: '贊助者人數', value: sponsorAccountCount },
      ];
    }
    return [
      { label: '目前會議廳', value: venues.length },
      { label: '目前評審', value: judgeOnlyMembers.length },
      { label: '目前投資者', value: investorMembers.length },
    ];
  }, [displayedActiveCampaigns.length, investorMembers.length, isRootSponsor, judgeOnlyMembers.length, sponsorAccountCount, venues.length]);
  const progressDashboardCampaignId = useMemo(() => {
    if (selectedMemberCampaignId && selectedMemberCampaignId !== NO_CAMPAIGN_FILTER) {
      return selectedMemberCampaignId;
    }
    return displayedActiveCampaigns[0]?.id || activeCampaign?.id || '';
  }, [selectedMemberCampaignId, displayedActiveCampaigns, activeCampaign?.id]);
  const adminProgressRows = useMemo(() => {
    const targetCampaignId = progressDashboardCampaignId;

    const scopedMembers = judgeMembers.filter((member) => {
      if (!targetCampaignId) {
        return true;
      }
      return member.campaign_id === targetCampaignId;
    });

    return scopedMembers
      .map((member) => {
        const evalStatus = judgeEvaluationStatusMap[member.identifier];
        const totalProjects = evalStatus?.total_projects ?? 0;
        const investedProjects = evalStatus?.invested_projects ?? 0;
        const scoredProjects = evalStatus?.scored_projects ?? 0;

        const investedRatio = totalProjects > 0 ? investedProjects / totalProjects : 0;
        const scoredRatio = totalProjects > 0 ? scoredProjects / totalProjects : 0;
        const fallbackProgress = member.is_voted ? 100 : 0;
        const computedProgress = member.role === 'judge'
          ? (totalProjects > 0 ? ((investedRatio + scoredRatio) / 2) * 100 : fallbackProgress)
          : (totalProjects > 0 ? investedRatio * 100 : fallbackProgress);
        const progress = Math.max(0, Math.min(100, Math.round(computedProgress)));
        const progressTone = progress >= 85 ? 'green' : progress >= 50 ? 'yellow' : 'red';

        const progressDetail = member.role === 'judge'
          ? (totalProjects > 0
            ? `投資 ${investedProjects}/${totalProjects} ｜ 評分 ${scoredProjects}/${totalProjects}`
            : (member.is_voted ? '已鎖定送出' : '尚未開始'))
          : (totalProjects > 0
            ? `投資 ${investedProjects}/${totalProjects}`
            : (member.is_voted ? '已鎖定送出' : '尚未開始'));

        return {
          identifier: member.identifier,
          displayName: cleanDisplayName(member.display_name),
          role: member.role,
          progress,
          progressTone,
          progressDetail,
          isLocked: member.is_voted,
        };
      })
      .sort((a, b) => {
        if (a.progress !== b.progress) {
          return b.progress - a.progress;
        }
        return a.displayName.localeCompare(b.displayName, 'zh-Hant');
      });
  }, [
    progressDashboardCampaignId,
    judgeMembers,
    judgeEvaluationStatusMap,
  ]);
  const adminJudgeProgressRows = useMemo(
    () => adminProgressRows.filter((row) => row.role === 'judge'),
    [adminProgressRows]
  );
  const adminInvestorProgressRows = useMemo(
    () => adminProgressRows.filter((row) => row.role === 'investor'),
    [adminProgressRows]
  );
  const shouldShowAdminProgressDashboard = displayedActiveCampaigns.length > 0 && adminProgressRows.length > 0;
  const managerNameByIdentifier = useMemo(() => {
    const rows: Record<string, string> = {};
    adminMembers.forEach((member) => {
      rows[member.identifier] = member.display_name;
    });
    return rows;
  }, [adminMembers]);
  const venueJudgeGroups = useMemo(() => {
    const grouped: Record<string, AdminMember[]> = {};
    judgeMembers.forEach((member) => {
      const key = member.assigned_venue_id || '__unassigned__';
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(member);
    });
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort((a, b) => a.display_name.localeCompare(b.display_name, 'zh-Hant'));
    });
    return grouped;
  }, [judgeMembers]);
  const campaignMemberStats = useMemo(() => {
    const stats: Record<string, { managers: number; judges: number }> = {};
    members.forEach((member) => {
      const key = member.role === 'admin'
        ? (member.managed_campaign_id || '')
        : (member.campaign_id || '');
      if (!key) {
        return;
      }
      if (!stats[key]) {
        stats[key] = { managers: 0, judges: 0 };
      }
      if (member.role === 'admin') {
        stats[key].managers += 1;
      } else if (isInvestmentMemberRole(member.role)) {
        stats[key].judges += 1;
      }
    });
    return stats;
  }, [members]);

  const venueMemberStats = useMemo(() => {
    const stats: Record<string, number> = {};
    venues.forEach((venue) => {
      stats[venue.id] = (venueJudgeGroups[venue.id] || []).length;
    });
    return stats;
  }, [venues, venueJudgeGroups]);

  const memberSortChoices = useMemo<MemberSortTag[]>(() => {
    if (isSuperAdmin(authUser?.role)) {
      return ['role', 'campaign', 'venue', 'lock'];
    }
    return ['venue', 'lock'];
  }, [authUser?.role]);

  const memberSortLabelMap: Record<MemberSortTag, string> = {
    role: '角色標籤',
    campaign: '場次標籤',
    venue: '會場標籤',
    lock: '鎖定狀態標籤'
  };

  const cycleMemberSortTag = useCallback(() => {
    const currentIndex = memberSortChoices.indexOf(memberSortTag);
    const nextIndex = currentIndex >= 0
      ? (currentIndex + 1) % memberSortChoices.length
      : 0;
    setMemberSortTag(memberSortChoices[nextIndex]);
  }, [memberSortChoices, memberSortTag]);

  const nextMemberSortTag = useMemo<MemberSortTag>(() => {
    const currentIndex = memberSortChoices.indexOf(memberSortTag);
    const nextIndex = currentIndex >= 0
      ? (currentIndex + 1) % memberSortChoices.length
      : 0;
    return memberSortChoices[nextIndex];
  }, [memberSortChoices, memberSortTag]);

  const sortedMembersByTag = useMemo(() => {
    const tagValue = (member: AdminMember): string => {
      if (memberSortTag === 'role') {
        if (member.role === 'admin') {
          return '1-admin';
        }
        if (member.role === 'judge') {
          return '2-judge';
        }
        if (member.role === 'investor') {
          return '3-investor';
        }
        return '9-other';
      }
      if (memberSortTag === 'campaign') {
        const campaignTag = member.role === 'admin'
          ? (member.managed_campaign_id || '')
          : (member.campaign_id || '');
        return campaignTag || 'zz-unassigned';
      }
      if (memberSortTag === 'venue') {
        return member.assigned_venue_id || 'zz-unassigned';
      }
      return member.is_voted ? '1-locked' : '2-editable';
    };

    const visibleMembers = members.filter((member) => {
      if (!authUser || !isAdminRole(authUser.role)) {
        return true;
      }
      return member.identifier !== authUser.identifier;
    });

    return [...visibleMembers].sort((a, b) => {
      const aTag = tagValue(a);
      const bTag = tagValue(b);
      if (aTag !== bTag) {
        return aTag.localeCompare(bTag, 'zh-Hant');
      }
      return a.display_name.localeCompare(b.display_name, 'zh-Hant');
    });
  }, [members, memberSortTag, authUser]);
  const setupVenue = venues.find((venue) => venue.id === setupVenueId) || null;
  const parseAxiosError = useCallback((err: unknown): string => {
    if (axios.isAxiosError(err)) {
      const detail = err.response?.data?.detail;
      if (detail) return String(detail);
      const status = err.response?.status;
      if (status === 401) return '登入已失效，請重新登入';
      if (status === 403) return '權限不足';
      if (status === 404) return '找不到資源';
      if (status) return `伺服器錯誤 (${status})`;
      return '無法連線至伺服器，請確認後端是否啟動';
    }
    return '發生錯誤，請聯絡系統管理員';
  }, []);

  const loadVenues = useCallback(async (campaignId?: string): Promise<Venue[]> => {
    try {
      const preferredCampaignId = campaignId
        || activeCampaignIdRef.current
        || userCampaignIdRef.current
        || selectedMemberCampaignIdRef.current
        || undefined;
      const response = await axios.get<Venue[]>(`${API_BASE_URL}/api/venues`, {
        params: preferredCampaignId ? { campaign_id: preferredCampaignId } : undefined,
      });
      setVenues(response.data);
      const firstVenueId = response.data[0]?.id;
      setSelectedVenueId((prev) => {
        if (prev && response.data.some((venue) => venue.id === prev)) {
          return prev;
        }
        return firstVenueId || '';
      });
      setDashboardVenueId((prev) => {
        if (prev && response.data.some((venue) => venue.id === prev)) {
          return prev;
        }
        return firstVenueId || '';
      });
      return response.data;
    } catch (err: unknown) {
      setError('讀取會場資料失敗：' + parseAxiosError(err));
      return [];
    }
  }, [parseAxiosError]);

  const loadVenueGroupOrder = useCallback(async (venueId?: string, campaignId?: string) => {
    if (!authHeaders || !isAdminRole(authUser?.role)) {
      return;
    }

    const targetVenueId = venueId || groupOrderVenueId || venues[0]?.id || '';
    const targetCampaignId = campaignId || groupOrderCampaignId;
    if (!targetVenueId || !targetCampaignId) {
      setGroupOrderProjects([]);
      return;
    }

    try {
      setIsLoadingGroupOrder(true);
      const response = await axios.get<VenueGroupOrderResponse>(
        `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(targetVenueId)}/group-order`,
        {
          headers: authHeaders,
          params: { campaign_id: targetCampaignId },
        }
      );
      setGroupOrderProjects(response.data.project_order || []);
      setOriginalGroupOrderProjects(response.data.project_order || []); // 記錄原始順序
      setVenueProjectOrderCache((prev) => ({ ...prev, [targetVenueId]: response.data.project_order || [] }));
      setGroupOrderVenueId(targetVenueId);
    } catch (err: unknown) {
      setError('讀取組別排序失敗：' + parseAxiosError(err));
    } finally {
      setIsLoadingGroupOrder(false);
    }
  }, [authHeaders, authUser?.role, groupOrderVenueId, venues, groupOrderCampaignId, parseAxiosError]);

  const saveVenueGroupOrder = useCallback(async (orderedProjects: GroupOrderProject[]) => {
    if (!authHeaders || !groupOrderVenueId || !groupOrderCampaignId) {
      return;
    }
    try {
      setIsSavingGroupOrder(true);
      await axios.patch(
        `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(groupOrderVenueId)}/group-order`,
        { project_ids: orderedProjects.map((project) => project.id) },
        {
          headers: authHeaders,
          params: { campaign_id: groupOrderCampaignId },
        }
      );
      setMessage('組別順序已更新');
      setOriginalGroupOrderProjects(orderedProjects); // 儲存後同步原始順序
    } catch (err: unknown) {
      setError('儲存組別排序失敗：' + parseAxiosError(err));
      await loadVenueGroupOrder(groupOrderVenueId, groupOrderCampaignId);
    } finally {
      setIsSavingGroupOrder(false);
    }
  }, [authHeaders, groupOrderVenueId, groupOrderCampaignId, parseAxiosError, loadVenueGroupOrder]);

  const ensureVenueProjectOptions = useCallback(async (venueId: string, campaignId: string) => {
    if (!authHeaders || !venueId || !campaignId) {
      return;
    }
    if (venueProjectOrderCache[venueId]?.length) {
      return;
    }
    try {
      const response = await axios.get<VenueGroupOrderResponse>(
        `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(venueId)}/group-order`,
        {
          headers: authHeaders,
          params: { campaign_id: campaignId },
        }
      );
      setVenueProjectOrderCache((prev) => ({ ...prev, [venueId]: response.data.project_order || [] }));
    } catch {
      // Ignore: this cache is best-effort and should not block member edit flow.
    }
  }, [authHeaders, venueProjectOrderCache]);

  useEffect(() => {
    loadVenues();
  }, [loadVenues]);

  useEffect(() => {
    if (!authUser || isAdminRole(authUser.role)) {
      return;
    }
    const scopedCampaignId = judgeStatus?.campaign_id || authUser.campaign_id || undefined;
    loadVenues(scopedCampaignId);
  }, [authUser, judgeStatus?.campaign_id, loadVenues]);

  useEffect(() => {
    const initInviteFromUrl = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = (params.get('c') || '').trim();
      if (!token) {
        return;
      }

      setInviteToken(token);
      try {
        const response = await axios.get<CampaignInviteInfo>(`${API_BASE_URL}/api/campaign/invite/${encodeURIComponent(token)}`);
        setInviteCampaign(response.data);
      } catch (err: unknown) {
        setError('邀請連結載入失敗：' + parseAxiosError(err));
      }
    };

    initInviteFromUrl();
  }, [parseAxiosError]);

  const refreshJudgeStatus = useCallback(async (headers = authHeaders) => {
    if (!headers) {
      return;
    }

    try {
      const response = await axios.get<JudgeStatus>(`${API_BASE_URL}/api/judges/status`, {
        headers
      });
      setJudgeStatus(response.data);

      const nextVenueId = response.data.assigned_venue_id || null;
      if (nextVenueId) {
        setDashboardVenueId((prev) => prev || nextVenueId);
        if (isInvestmentMemberRole(response.data.role)) {
          setCurrentView((prev) => (prev === 'judge' ? 'judge' : prev));
        }
      }
      setAuthUser((prev) => {
        if (!prev) {
          return prev;
        }
        const nextCampaignYear = response.data.campaign_year ?? prev.campaign_year ?? null;
        const nextCampaignId = response.data.campaign_id ?? prev.campaign_id ?? null;
        if (prev.venue_id === nextVenueId && prev.campaign_year === nextCampaignYear && prev.campaign_id === nextCampaignId) {
          return prev; // nothing changed; avoid creating a new object reference
        }
        const nextUser = {
          ...prev,
          venue_id: nextVenueId,
          campaign_year: nextCampaignYear,
          campaign_id: nextCampaignId,
        };
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
        return nextUser;
      });
    } catch (err: unknown) {
      setError('讀取評審狀態失敗：' + parseAxiosError(err));
    }
  }, [authHeaders, parseAxiosError]);

  useEffect(() => {
    if (!authHeaders) {
      return;
    }
    refreshJudgeStatus(authHeaders);
  }, [authHeaders, refreshJudgeStatus]);

  useEffect(() => {
    if (!isAdminRole(authUser?.role)) {
      return;
    }
    if (disableTopCampaignTabs && currentView !== 'admin') {
      setCurrentView('admin');
      return;
    }
    if (!isSuperAdmin(authUser?.role) && currentView === 'judge') {
      setCurrentView('admin');
    }
  }, [authUser?.role, currentView, disableTopCampaignTabs]);

  useEffect(() => {
    if (!authUser || isAdminRole(authUser.role)) {
      return;
    }
    if (currentView === 'dashboard' || currentView === 'score-ranking' || currentView === 'admin') {
      setCurrentView('judge');
    }
  }, [authUser, currentView]);

  const clearAuth = useCallback((clearDisplayName: boolean) => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    if (clearDisplayName) {
      sessionStorage.removeItem(DISPLAY_NAME_SESSION_KEY);
      localStorage.removeItem(LOGIN_MODE_STORAGE_KEY);
      setDisplayNameInput('');
    }
    setAuthToken(null);
    setAuthUser(null);
    setJudgeStatus(null);
    setIsMobileNavOpen(false);
    setCurrentView('judge');
  }, []);

  useEffect(() => {
    if (!inviteCampaign && loginMode !== 'admin') {
      setLoginMode('admin');
    }
  }, [inviteCampaign, loginMode]);

  useEffect(() => {
    localStorage.setItem(LOGIN_MODE_STORAGE_KEY, loginMode);
  }, [loginMode]);

  const saveAuth = useCallback((auth: AuthResponse, showMessage = true) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, auth.access_token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(auth.user));
    sessionStorage.setItem(DISPLAY_NAME_SESSION_KEY, auth.user.display_name);
    setDisplayNameInput(auth.user.display_name);
    setAuthToken(auth.access_token);
    setAuthUser(auth.user);
    setIsMobileNavOpen(false);
    setMessage(showMessage ? `登入成功，歡迎${roleLabel(auth.user.role)} ${cleanDisplayName(auth.user.display_name)}` : null);
    setError(null);
    if (isAdminRole(auth.user.role)) {
      setCurrentView('admin');
    } else if (auth.user.venue_id) {
      setCurrentView('judge');
      setDashboardVenueId((prev) => prev || auth.user.venue_id || '');
    } else {
      setCurrentView('judge');
    }
  }, []);

  const loginWithDisplayName = useCallback(async (rawName: string, showMessage = true): Promise<boolean> => {
    const normalizedInput = rawName.trim();
    if (!normalizedInput) {
      if (showMessage) {
        setError('請輸入員編或姓名');
      }
      return false;
    }
    if (!inviteTokenRef.current) {
      if (showMessage) {
        setError('請使用對應場次連結進入');
      }
      return false;
    }

    try {
      const response = await axios.post<AuthResponse>(`${API_BASE_URL}/api/judges/login`, {
        display_name: normalizedInput,
        invite_token: inviteTokenRef.current || undefined,
      });
      saveAuth(response.data, showMessage);
      return true;
    } catch (err: unknown) {
      if (showMessage) {
        setError('登入失敗。' + parseAxiosError(err));
      }
      return false;
    }
  }, [parseAxiosError, saveAuth]);

  const loginWithName = async () => {
    if (isLoggingIn) {
      return;
    }

    setError(null);
    setMessage(null);
    setIsLoggingIn(true);

    try {
      await loginWithDisplayName(displayNameInput, true);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const loginAsAdmin = async () => {
    if (isLoggingIn) {
      return;
    }
    const username = adminLoginUsername.trim();
    const password = adminLoginPassword;
    if (!username || !password) {
      setError('請輸入贊助者帳號與密碼');
      return;
    }

    setError(null);
    setMessage(null);
    setIsLoggingIn(true);
    try {
      const response = await axios.post<AuthResponse>(`${API_BASE_URL}/api/admin/auth/login`, {
        username,
        password,
      });
      saveAuth(response.data, true);
      setAdminLoginPassword('');
    } catch (err: unknown) {
      const msg = parseAxiosError(err);
      if (msg.includes('已過期')) {
        setError(
          <span>
            贊助者登入失敗：{msg}，請寄信至{' '}
            <a href="mailto:40273089w@gmail.com" style={{ color: 'inherit', textDecoration: 'underline' }}>40273089w@gmail.com</a>
            {' '}申請延期。
          </span>
        );
      } else {
        setError('贊助者登入失敗：' + msg);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const submitFirstLoginSetup = async () => {
    if (!authHeaders || !authUser || isSavingFirstLoginSetup) {
      return;
    }
    const username = firstLoginSetupUsername.trim();
    const password = firstLoginSetupPassword;
    const displayName = normalizeDisplayName(firstLoginSetupDisplayName);
    const department = firstLoginSetupDepartment.trim();
    if (!username || !password) {
      setError('請填寫帳號與密碼');
      return;
    }
    if (password !== firstLoginSetupConfirmPassword) {
      setError('兩次輸入的密碼不一致');
      return;
    }
    const resolvedDisplayName = displayName || authUser.display_name || username;
    const resolvedDepartment = department || authUser.department || '未設定';
    try {
      setIsSavingFirstLoginSetup(true);
      const response = await axios.post<AuthResponse>(
        `${API_BASE_URL}/api/admin/auth/complete-profile`,
        {
          username,
          password,
          display_name: resolvedDisplayName,
          department: resolvedDepartment,
        },
        { headers: authHeaders },
      );
      saveAuth(response.data, false);
      setFirstLoginSetupPassword('');
      setFirstLoginSetupConfirmPassword('');
      setMessage('贊助者資料已完成設定');
    } catch (err: unknown) {
      setError('儲存贊助者資料失敗：' + parseAxiosError(err));
    } finally {
      setIsSavingFirstLoginSetup(false);
    }
  };

  const openProfileModal = useCallback(() => {
    if (!authUser || !isAdminRole(authUser.role)) {
      return;
    }

    setProfileDisplayNameDraft(authUser.display_name || '');
    setProfileDepartmentDraft(
      authUser.department && authUser.department !== '未設定'
        ? authUser.department
        : ''
    );
    setIsProfileModalOpen(true);
    setError(null);
  }, [authUser]);

  const submitProfileUpdate = useCallback(async () => {
    if (!authHeaders || !authUser || !isAdminRole(authUser.role) || isSavingProfile) {
      return;
    }

    const nextDisplayName = normalizeDisplayName(profileDisplayNameDraft);
    const nextDepartment = profileDepartmentDraft.trim();

    if (!nextDisplayName) {
      setError('請輸入姓名');
      return;
    }

    try {
      setIsSavingProfile(true);
      const response = await axios.patch<AuthResponse>(
        `${API_BASE_URL}/api/auth/profile`,
        {
          display_name: nextDisplayName,
          department: nextDepartment || null,
        },
        { headers: authHeaders },
      );
      // Update only the user info in state/storage without changing the token,
      // to avoid triggering authHeaders change and unnecessary data reloads.
      const updatedUser = response.data.user;
      setAuthUser(updatedUser);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(updatedUser));
      sessionStorage.setItem(DISPLAY_NAME_SESSION_KEY, updatedUser.display_name);
      setDisplayNameInput(updatedUser.display_name);
      setMessage('個人資料已更新');
      setIsProfileModalOpen(false);
      setError(null);
    } catch (err: unknown) {
      setError('更新個人資料失敗：' + parseAxiosError(err));
    } finally {
      setIsSavingProfile(false);
    }
  }, [authHeaders, authUser, isSavingProfile, parseAxiosError, profileDepartmentDraft, profileDisplayNameDraft]);

  useEffect(() => {
    if (!authUser?.must_complete_profile) {
      return;
    }
    setFirstLoginSetupUsername('');
    setFirstLoginSetupPassword('');
    setFirstLoginSetupConfirmPassword('');
    setFirstLoginSetupDisplayName('');
    setFirstLoginSetupDepartment('');
  }, [authUser]);

  useEffect(() => {
    const restoreSession = async () => {
      if (isLoggingIn || isRestoringSession) {
        return;
      }

      const storedName = sessionStorage.getItem(DISPLAY_NAME_SESSION_KEY) || '';

      if (authToken) {
        try {
          await axios.get<AuthUser>(`${API_BASE_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          return;
        } catch (err: unknown) {
          if (!axios.isAxiosError(err) || err.response?.status !== 401) {
            return;
          }
          clearAuth(false);
        }
      }

      if (!storedName || authUser) {
        return;
      }

      setIsRestoringSession(true);
      const restored = await loginWithDisplayName(storedName, false);
      if (!restored) {
        clearAuth(false);
      }
      setIsRestoringSession(false);
    };

    restoreSession();
  }, [authToken, authUser, clearAuth, isLoggingIn, isRestoringSession, loginWithDisplayName]);

  const logout = async () => {
    if (isLoggingOut) {
      return;
    }
    setIsLoggingOut(true);
    if (authHeaders) {
      try {
        await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, { headers: authHeaders });
      } catch {
        // Ignore logout network failures.
      }
    }

    clearAuth(true);
    setMessage('已登出');
    setIsLoggingOut(false);
  };

  const joinVenue = async () => {
    if (!authHeaders || !selectedVenueId || isJoiningVenue) {
      return;
    }
    setError(null);
    setMessage(null);

    try {
      setIsJoiningVenue(true);
      await axios.post(
        `${API_BASE_URL}/api/judges/join-venue`,
        { venue_id: selectedVenueId },
        { headers: authHeaders }
      );

      const nextUser: AuthUser = {
        ...(authUser as AuthUser),
        venue_id: selectedVenueId
      };
      setAuthUser(nextUser);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(nextUser));
      setDashboardVenueId(selectedVenueId);
      await refreshJudgeStatus();
      setCurrentView('judge');
      setMessage('加入會場成功');
    } catch (err: unknown) {
      setError('加入會場失敗：' + parseAxiosError(err));
    } finally {
      setIsJoiningVenue(false);
    }
  };

  const loadMembers = useCallback(async (campaignIdOverride?: string, forceRefresh = false) => {
    if (!authHeaders || !isAdminRole(authUser?.role)) {
      return;
    }
    const requestedCampaignId = campaignIdOverride ?? selectedMemberCampaignIdRef.current ?? activeCampaignIdRef.current ?? '';
    const isNoCampaignFilter = requestedCampaignId === NO_CAMPAIGN_FILTER;
    const campaignId = isNoCampaignFilter ? '' : requestedCampaignId;
    
    // 去重檢查：如果是相同的 campaign_id 且不是強制刷新，則跳過 API 調用
    if (!forceRefresh && lastLoadedMembersCampaignIdRef.current === campaignId) {
      return;
    }
    
    // super_admin can fetch members without a campaign (returns global admin members)
    if (!campaignId && !isSuperAdmin(authUser?.role)) {
      setMembers([]);
      return;
    }
    try {
      setIsLoadingMembers(true);
      const response = await axios.get<AdminMembersResponse>(`${API_BASE_URL}/api/admin/members`, {
        headers: authHeaders,
        params: campaignId ? { campaign_id: campaignId } : undefined
      });
      const loadedMembers = isNoCampaignFilter
        ? response.data.members.filter((member) => {
          if (member.role === 'admin') {
            return !member.managed_campaign_id;
          }
          return !member.campaign_id;
        })
        : response.data.members;
      setMembers(loadedMembers);
      // 記錄已加載的 campaign_id
      lastLoadedMembersCampaignIdRef.current = campaignId;
      if (!isSuperAdmin(authUser?.role) && response.data.campaign_id) {
        setSelectedMemberCampaignId(response.data.campaign_id);
      } else if (isSuperAdmin(authUser?.role) && !isCampaignActiveRef.current) {
        // super_admin fetched global admins — no campaign_id to set
      }
      const names: Record<string, string> = {};
      const roles: Record<string, Role> = {};
      const managers: Record<string, string> = {};
      const startProjects: Record<string, string> = {};
      for (const member of loadedMembers) {
        names[member.identifier] = member.display_name;
        roles[member.identifier] = member.role;
        managers[member.identifier] = member.manager_identifier || '';
        startProjects[member.identifier] = member.start_project_id || '';
      }
      setMemberEditName(names);
      setMemberEditRole(roles);
      setMemberEditManager(managers);
      setMemberEditStartProject(startProjects);
    } catch (err: unknown) {
      setError('讀取成員失敗：' + parseAxiosError(err));
    } finally {
      setIsLoadingMembers(false);
    }
  }, [authHeaders, authUser?.role, parseAxiosError]);

  const loadJudgeEvaluationStatus = useCallback(async (campaignId?: string) => {
    if (!authHeaders || !isAdminRole(authUser?.role)) {
      return;
    }
    const targetCampaignId = campaignId ?? selectedMemberCampaignId;
    if (!targetCampaignId) {
      setJudgeEvaluationStatusMap({});
      return;
    }
    try {
      const response = await axios.get<JudgeEvaluationStatusList>(`${API_BASE_URL}/api/admin/judges-evaluation-status`, {
        headers: authHeaders,
        params: { campaign_id: targetCampaignId }
      });
      const statusMap: Record<string, JudgeEvaluationStatus> = {};
      for (const judge of response.data.judges) {
        statusMap[judge.identifier] = judge;
      }
      setJudgeEvaluationStatusMap(statusMap);
    } catch (err: unknown) {
      // Silently fail for evaluation status (not critical)
      console.debug('Failed to load judge evaluation status:', err);
    }
  }, [authHeaders, authUser?.role, selectedMemberCampaignId]);

  const loadSystemState = useCallback(async () => {
    if (!authHeaders || !isAdminRole(authUser?.role)) {
      return undefined;
    }
    try {
      const response = await axios.get<AdminSystemState>(`${API_BASE_URL}/api/admin/system-state`, {
        headers: authHeaders
      });
      const normalizedState: AdminSystemState = {
        current_campaign: response.data.current_campaign ?? null,
        active_campaigns_list: response.data.active_campaigns_list ?? [],
        campaigns_by_year: response.data.campaigns_by_year ?? {},
        recently_deleted_by_year: response.data.recently_deleted_by_year ?? {}
      };
      setAdminSystemState(normalizedState);
      setCampaignBudgetDrafts((prev) => {
        const next = { ...prev };
        const activeRows = normalizedState.active_campaigns_list ?? [];
        activeRows.forEach((campaign) => {
          next[campaign.id] = String(campaign.judge_budget ?? 10000);
        });
        if (normalizedState.current_campaign?.id) {
          next[normalizedState.current_campaign.id] = String(normalizedState.current_campaign.judge_budget ?? 10000);
        }
        return next;
      });
      setCampaignInvestorBudgetDrafts((prev) => {
        const next = { ...prev };
        const activeRows = normalizedState.active_campaigns_list ?? [];
        activeRows.forEach((campaign) => {
          next[campaign.id] = campaign.investor_budget != null ? String(campaign.investor_budget) : '';
        });
        if (normalizedState.current_campaign?.id) {
          next[normalizedState.current_campaign.id] = normalizedState.current_campaign.investor_budget != null
            ? String(normalizedState.current_campaign.investor_budget)
            : '';
        }
        return next;
      });
      const fallbackActiveCampaign = normalizedState.current_campaign ?? normalizedState.active_campaigns_list?.[0] ?? null;
      const activeYear = fallbackActiveCampaign?.year;
      const activeId = fallbackActiveCampaign?.id;
      if (activeYear) {
        if (activeId) {
          setSelectedMemberCampaignId(activeId);
        }
        setAdminTab((prev) => ((prev === 'archives' || prev === 'deleted' || prev === 'venues' || prev === 'group-order') ? prev : 'members'));
      } else {
        setSelectedMemberCampaignId('');
        setMembers([]);
        setAdminTab((prev) => ((prev === 'archives' || prev === 'deleted' || prev === 'venues' || prev === 'group-order') ? prev : 'members'));
      }
      return normalizedState;
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const detail = String(err.response?.data?.detail || '');
        if (status === 403 && detail.includes('請先完成首次登入資料設定')) {
          return undefined;
        }
      }
      setError('讀取場次狀態失敗：' + parseAxiosError(err));
      return undefined;
    }
  }, [authHeaders, authUser?.role, parseAxiosError]);

  const waitForSystemState = useCallback(
    async (
      isDone: (state: AdminSystemState) => boolean,
      retries = 10,
      delayMs = 350
    ): Promise<AdminSystemState | undefined> => {
      for (let i = 0; i < retries; i += 1) {
        const state = await loadSystemState();
        if (state && isDone(state)) {
          return state;
        }
        if (i < retries - 1) {
          await new Promise((resolve) => window.setTimeout(resolve, delayMs));
        }
      }
      return undefined;
    },
    [loadSystemState]
  );

  const loadAdminData = useCallback(async () => {
    try {
      setIsLoadingAdminData(true);
      const systemState = await loadSystemState();
      const preferredCampaignId = systemState?.current_campaign?.id
        || systemState?.active_campaigns_list?.[0]?.id
        || '';
      const fetchedVenues = await loadVenues(preferredCampaignId);
      // If no venues configured yet, land on venue management tab by default
      if (fetchedVenues.length === 0) {
        setAdminTab((prev) => (prev === 'archives' || prev === 'deleted' ? prev : 'venues'));
      }
      if (preferredCampaignId) {
        await Promise.all([
          loadMembers(preferredCampaignId),
          loadJudgeEvaluationStatus(preferredCampaignId),
        ]);
      } else {
        setMembers([]);
      }
    } finally {
      setIsLoadingAdminData(false);
    }
  }, [loadMembers, loadSystemState, loadVenues, loadJudgeEvaluationStatus]);

  const refreshAdminProgressDashboard = useCallback(async () => {
    if (!progressDashboardCampaignId || isAdminProgressRefreshing) {
      return;
    }
    try {
      setIsAdminProgressRefreshing(true);
      await Promise.all([
        loadMembers(progressDashboardCampaignId, true),
        loadJudgeEvaluationStatus(progressDashboardCampaignId),
      ]);
    } finally {
      setIsAdminProgressRefreshing(false);
    }
  }, [progressDashboardCampaignId, isAdminProgressRefreshing, loadMembers, loadJudgeEvaluationStatus]);

  const fetchProgressDetailProjects = useCallback(async (row: { identifier: string; displayName: string; role: string }) => {
    if (!authHeaders) return;
    setIsLoadingProgressDetail(true);
    try {
      const res = await axios.get<{ projects: MemberProjectDetail[] }>(
        `${API_BASE_URL}/api/admin/members/${encodeURIComponent(row.identifier)}/project-details`,
        { headers: authHeaders, params: { campaign_id: progressDashboardCampaignId } }
      );
      const isJudge = row.role === 'judge';
      const sorted = [...res.data.projects].sort((a, b) => {
        const priority = (p: MemberProjectDetail): number => {
          const hasInvest = p.invested > 0;
          const hasScore = p.score > 0;
          if (isJudge) {
            if (!hasScore && !hasInvest) return 0;
            if (hasScore && !hasInvest) return 1;
            if (!hasScore && hasInvest) return 0;
            return 2;
          } else {
            return hasInvest ? 2 : 0;
          }
        };
        const pa = priority(a);
        const pb = priority(b);
        if (pa !== pb) return pa - pb;
        return a.name.localeCompare(b.name, 'zh-Hant');
      });
      setProgressDetailProjects(sorted);
    } catch {
      setProgressDetailProjects([]);
    } finally {
      setIsLoadingProgressDetail(false);
    }
  }, [authHeaders, progressDashboardCampaignId]);

  const openProgressDetail = useCallback(async (row: { identifier: string; displayName: string; role: string }) => {
    setProgressDetailModal({ identifier: row.identifier, displayName: row.displayName, role: row.role });
    setProgressDetailProjects([]);
    await fetchProgressDetailProjects(row);
  }, [fetchProgressDetailProjects]);

  const refreshProgressDetail = useCallback(async () => {
    if (!progressDetailModal || isLoadingProgressDetail) {
      return;
    }
    await fetchProgressDetailProjects(progressDetailModal);
  }, [progressDetailModal, isLoadingProgressDetail, fetchProgressDetailProjects]);

  useEffect(() => {
    if (isAdminRole(authUser?.role) && currentView === 'admin' && !authUser?.must_complete_profile) {
      loadAdminData();
    }
  }, [authUser?.role, authUser?.must_complete_profile, currentView, loadAdminData]);

  useEffect(() => {
    if (adminTab === 'venues' && selectedMemberCampaignId && selectedMemberCampaignId !== NO_CAMPAIGN_FILTER) {
      loadMembers(selectedMemberCampaignId);
      loadVenues(selectedMemberCampaignId);
    }
  }, [adminTab, selectedMemberCampaignId, loadMembers, loadVenues]);

  useEffect(() => {
    if (!venues.length) {
      setGroupOrderVenueId('');
      setGroupOrderProjects([]);
      return;
    }
    setGroupOrderVenueId((prev) => {
      if (prev && venues.some((venue) => venue.id === prev)) {
        return prev;
      }
      return venues[0].id;
    });
  }, [venues]);

  useEffect(() => {
    if (adminTab !== 'group-order') {
      return;
    }
    if (!groupOrderVenueId || !groupOrderCampaignId) {
      setGroupOrderProjects([]);
      return;
    }
    loadVenueGroupOrder(groupOrderVenueId, groupOrderCampaignId);
  }, [adminTab, groupOrderVenueId, groupOrderCampaignId, loadVenueGroupOrder]);

  useEffect(() => {
    if (
      adminTab !== 'venues'
      || (selectedMemberCampaignId && selectedMemberCampaignId !== NO_CAMPAIGN_FILTER)
      || venueCampaignOptions.length === 0
    ) {
      return;
    }
    setSelectedMemberCampaignId(venueCampaignOptions[0].id);
  }, [adminTab, selectedMemberCampaignId, venueCampaignOptions]);

  useEffect(() => {
    if (adminTab !== 'members') {
      return;
    }
    if (isSuperAdmin(authUser?.role)) {
      if (selectedMemberCampaignId) {
        loadMembers(selectedMemberCampaignId);
        loadJudgeEvaluationStatus(selectedMemberCampaignId);
        return;
      }
      const fallbackCampaignId = activeCampaign?.id || venueCampaignOptions[0]?.id || '';
      if (fallbackCampaignId) {
        setSelectedMemberCampaignId(fallbackCampaignId);
        return;
      }
      loadMembers(NO_CAMPAIGN_FILTER);
      return;
    }
    const scopeCampaignId = selectedMemberCampaignId === NO_CAMPAIGN_FILTER
      ? ''
      : (selectedMemberCampaignId || activeCampaign?.id || '');
    if (scopeCampaignId) {
      loadMembers(scopeCampaignId);
      loadJudgeEvaluationStatus(scopeCampaignId);
      return;
    }
    setMembers([]);
  }, [adminTab, authUser?.role, selectedMemberCampaignId, activeCampaign?.id, venueCampaignOptions, loadMembers, loadJudgeEvaluationStatus]);

  useEffect(() => {
    if (adminTab !== 'members' || !selectedMemberCampaignId || selectedMemberCampaignId === NO_CAMPAIGN_FILTER) {
      if (judgeStatusRefreshIntervalRef.current) {
        clearInterval(judgeStatusRefreshIntervalRef.current);
        judgeStatusRefreshIntervalRef.current = null;
      }
      return;
    }
    
    // Initial load
    loadJudgeEvaluationStatus(selectedMemberCampaignId);
    
    // Set up 15-second auto-refresh
    judgeStatusRefreshIntervalRef.current = setInterval(() => {
      loadJudgeEvaluationStatus(selectedMemberCampaignId);
    }, 15000);
    
    return () => {
      if (judgeStatusRefreshIntervalRef.current) {
        clearInterval(judgeStatusRefreshIntervalRef.current);
        judgeStatusRefreshIntervalRef.current = null;
      }
    };
  }, [adminTab, selectedMemberCampaignId, loadJudgeEvaluationStatus]);

  useEffect(() => {
    if (!selectedMemberId) {
      return;
    }
    const member = members.find((item) => item.identifier === selectedMemberId);
    if (!member || !isInvestmentMemberRole(member.role)) {
      return;
    }
    const venueId = member.assigned_venue_id || '';
    const campaignId = member.campaign_id || selectedMemberCampaignId || activeCampaign?.id || '';
    if (!venueId || !campaignId) {
      return;
    }
    void ensureVenueProjectOptions(venueId, campaignId);
  }, [selectedMemberId, members, selectedMemberCampaignId, activeCampaign?.id, ensureVenueProjectOptions]);

  useEffect(() => {
    if (isSuperAdmin(authUser?.role)) {
      return;
    }
    if (memberSortTag === 'role' || memberSortTag === 'campaign') {
      setMemberSortTag('venue');
    }
  }, [authUser?.role, memberSortTag]);

  useEffect(() => {
    if (!message) {
      return;
    }
    setError(null);
    const timer = window.setTimeout(() => setMessage(null), 5000);
    return () => window.clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    if (!error) {
      return;
    }
    const timer = window.setTimeout(() => setError(null), 4500);
    return () => window.clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(AMBIENT_BOOKMARKS_STORAGE_KEY, JSON.stringify(ambientSoundBookmarks));
  }, [ambientSoundBookmarks]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(TEMP_ADMIN_CREDENTIAL_HISTORY_STORAGE_KEY, JSON.stringify(tempAdminCredentialHistory));
  }, [tempAdminCredentialHistory]);

  const createVenue = async () => {
    if (!authHeaders || !newVenueName.trim() || isCreatingVenue) {
      return;
    }
    try {
      setIsCreatingVenue(true);
      const venueName = newVenueName.trim();
      const response = await axios.post<Venue>(
        `${API_BASE_URL}/api/admin/venues`,
        { name: venueName, classroom: venueName },
        { headers: authHeaders, params: selectedMemberCampaignId ? { campaign_id: selectedMemberCampaignId } : undefined }
      );
      setNewVenueName('');
      const newVenue = response.data;
      setSetupVenueId(newVenue.id);
      setSetupVenueNameDraft(venueName);
      setSetupProjectDraft('');
      setSetupSelectedMemberIds([]);
      setSetupNewMemberDraft('');
      setIsVenueSetupOpen(true);
      setMessage('會場新增成功，請繼續完成會場設定');
      await loadVenues();
    } catch (err: unknown) {
      setError('新增會場失敗：' + parseAxiosError(err));
    } finally {
      setIsCreatingVenue(false);
    }
  };

  const openVenueSetup = (venueId: string) => {
    const venue = venues.find((item) => item.id === venueId);
    const projectDraft = venue?.projects?.length ? venue.projects.join('\n') : '';
    const selected = judgeMembers
      .filter((member) => member.assigned_venue_id === venueId)
      .map((member) => member.identifier);
    setSetupVenueId(venueId);
    setSetupVenueNameDraft(venue?.classroom || venue?.name || '');
    setSetupProjectDraft(projectDraft);
    setSetupSelectedMemberIds(selected);
    setSetupNewMemberDraft('');
    setIsVenueSetupOpen(true);
  };

  const downloadVenueTemplate = () => {
    const anchor = document.createElement('a');
    anchor.href = `${process.env.PUBLIC_URL}/example.xlsx`;
    anchor.download = 'example.xlsx';
    anchor.click();
  };

  const importVenueWorkbook = async (file: File) => {
    if (!authHeaders || !selectedMemberCampaignId || isBulkImporting) {
      return;
    }

    type ImportBucket = { projects: Set<string>; judges: Set<string> };
    const byClassroom = new Map<string, ImportBucket>();
    const classroomLabel = new Map<string, string>();
    const judgeToClassroom = new Map<string, string>();
    const pick = (row: Record<string, string>, keys: string[]): string => {
      for (const key of keys) {
        const value = row[key];
        if (value) {
          return value;
        }
      }
      return '';
    };
    const normalizeRow = (raw: Record<string, unknown>): Record<string, string> => {
      const normalized: Record<string, string> = {};
      Object.entries(raw).forEach(([key, value]) => {
        normalized[key.trim().toLowerCase()] = normalizeDisplayName(String(value ?? ''));
      });
      return normalized;
    };

    try {
      setIsBulkImporting(true);
      setError(null);
      setMessage(null);
      setImportStatus({ tone: 'loading', text: '正在解析與匯入資料，請稍候...' });

      let createdVenueCount = 0;
      let updatedProjectCount = 0;
      let assignedJudgeCount = 0;

      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const projectSheet = workbook.Sheets['專題組'] || workbook.Sheets[workbook.SheetNames[0]];
      const judgeSheet = workbook.Sheets['評審'] || workbook.Sheets[workbook.SheetNames[1]];

      if (!projectSheet || !judgeSheet) {
        throw new Error('找不到「專題組」與「評審」工作表');
      }

      const projectRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(projectSheet, { defval: '' });
      const judgeRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(judgeSheet, { defval: '' });

      projectRows.forEach((raw) => {
        const row = normalizeRow(raw);
        const classroomRaw = pick(row, ['classroom', '教室', '會場', 'venue']);
        const projectName = pick(row, ['project_name', 'project', '專題名稱', '專題組', '專題']);
        if (!classroomRaw || !projectName) {
          return;
        }
        const classroomKey = classroomRaw.toUpperCase();
        const bucket = byClassroom.get(classroomKey) || { projects: new Set<string>(), judges: new Set<string>() };
        bucket.projects.add(projectName);
        byClassroom.set(classroomKey, bucket);
        classroomLabel.set(classroomKey, classroomRaw);
      });

      judgeRows.forEach((raw) => {
        const row = normalizeRow(raw);
        const classroomRaw = pick(row, ['classroom', '教室', '會場', 'venue']);
        const judgeName = pick(row, ['judge_name', 'judge', '評審姓名', '評審']);
        if (!classroomRaw || !judgeName) {
          return;
        }
        const classroomKey = classroomRaw.toUpperCase();
        const bucket = byClassroom.get(classroomKey) || { projects: new Set<string>(), judges: new Set<string>() };
        const judgeIdentifier = buildNameIdentifier(judgeName);
        const previousClassroom = judgeToClassroom.get(judgeIdentifier);
        if (previousClassroom && previousClassroom !== classroomKey) {
          throw new Error(`評審「${judgeName}」重複出現在不同會場`);
        }
        judgeToClassroom.set(judgeIdentifier, classroomKey);
        bucket.judges.add(judgeName);
        byClassroom.set(classroomKey, bucket);
        classroomLabel.set(classroomKey, classroomRaw);
      });

      if (byClassroom.size === 0) {
        throw new Error('匯入檔沒有可用資料');
      }

      const venueIdByClassroom = new Map<string, string>();
      venues.forEach((venue) => {
        const key = normalizeDisplayName(venue.classroom || venue.name).toUpperCase();
        if (key) {
          venueIdByClassroom.set(key, venue.id);
        }
      });

      const classrooms = Array.from(byClassroom.keys());
      for (let classroomIndex = 0; classroomIndex < classrooms.length; classroomIndex += 1) {
        const classroomKey = classrooms[classroomIndex];
        const bucket = byClassroom.get(classroomKey);
        if (!bucket) {
          continue;
        }
        const classroom = classroomLabel.get(classroomKey) || classroomKey;
        let venueId = venueIdByClassroom.get(classroomKey);
        if (!venueId) {
          const response = await axios.post<Venue>(
            `${API_BASE_URL}/api/admin/venues`,
            { name: classroom, classroom },
            { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
          );
          venueId = response.data.id;
          venueIdByClassroom.set(classroomKey, venueId);
          createdVenueCount += 1;
        }

        if (bucket.projects.size > 0) {
          await axios.patch(
            `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(venueId)}/projects`,
            { project_names: Array.from(bucket.projects) },
            { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
          );
          updatedProjectCount += bucket.projects.size;
        }

        const judgeNames = Array.from(bucket.judges);
        for (let judgeIndex = 0; judgeIndex < judgeNames.length; judgeIndex += 1) {
          const judgeName = judgeNames[judgeIndex];
          try {
            await axios.post(
              `${API_BASE_URL}/api/admin/members`,
              { display_name: judgeName, role: 'judge' },
              { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
            );
          } catch (err: unknown) {
            const detail = parseAxiosError(err);
            if (!detail.includes('已存在')) {
              throw err;
            }
          }

          await axios.patch(
            `${API_BASE_URL}/api/admin/members/${encodeURIComponent(buildNameIdentifier(judgeName))}/status`,
            { assigned_venue_id: venueId, is_voted: false },
            { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
          );
          assignedJudgeCount += 1;
        }
      }

      await Promise.all([loadMembers(selectedMemberCampaignId), loadVenues(selectedMemberCampaignId)]);
      await Promise.all([loadMembers(selectedMemberCampaignId, true), loadVenues(selectedMemberCampaignId)]);
      setMessage(`一鍵匯入完成：${byClassroom.size} 個會場`);
      setImportStatus({
        tone: 'success',
        text: `匯入完成：新增 ${createdVenueCount} 個會場、更新 ${updatedProjectCount} 個專題、指派 ${assignedJudgeCount} 位評審。`
      });
    } catch (err: unknown) {
      setError('匯入會場資料失敗：' + parseAxiosError(err));
      const detail = err instanceof Error ? err.message : parseAxiosError(err);
      setImportStatus({ tone: 'error', text: `匯入失敗：${detail}` });
    } finally {
      setIsBulkImporting(false);
    }
  };

  const openVenueView = useCallback(async (venueId: string) => {
    setViewVenueId(venueId);
    setIsLoadingVenueView(true);
    setVenueViewProjects([]);
    try {
      const response = await axios.get<VenueProjectsResponse>(`${API_BASE_URL}/api/projects`, {
        params: { venue_id: venueId }
      });
      setVenueViewProjects(response.data.projects);
    } catch {
      setVenueViewProjects([]);
    } finally {
      setIsLoadingVenueView(false);
    }
  }, []);

  const saveVenueSetup = async () => {
    if (!authHeaders || !setupVenueId || !setupVenue) {
      return;
    }

    const projectNames = Array.from(
      new Set(
        setupProjectDraft
          .split(/\n+/)
          .map((name) => normalizeDisplayName(name))
          .filter(Boolean)
      )
    );
    if (projectNames.length === 0) {
      setError('請至少輸入 1 個專題組名稱');
      return;
    }

    const newMemberNames = Array.from(
      new Set(
        setupNewMemberDraft
          .split(/\n+/)
          .map((name) => normalizeDisplayName(name))
          .filter(Boolean)
      )
    );

    setIsSavingVenueSetup(true);
    setError(null);
    setMessage(null);

    try {
      const nameToSave = setupVenueNameDraft.trim();
      if (nameToSave && nameToSave !== (setupVenue.classroom || setupVenue.name)) {
        await axios.put(
          `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(setupVenueId)}`,
          { name: nameToSave, classroom: nameToSave },
          { headers: authHeaders, params: selectedMemberCampaignId ? { campaign_id: selectedMemberCampaignId } : undefined }
        );
      }
      await axios.patch(
        `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(setupVenueId)}/projects`,
        { project_names: projectNames },
        { headers: authHeaders, params: selectedMemberCampaignId ? { campaign_id: selectedMemberCampaignId } : undefined }
      );

      const memberIdentifiers = [...setupSelectedMemberIds];
      for (const displayName of newMemberNames) {
        try {
          await axios.post(
            `${API_BASE_URL}/api/admin/members`,
            { display_name: displayName, role: 'judge' },
            { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
          );
          memberIdentifiers.push(buildNameIdentifier(displayName));
        } catch (err: unknown) {
          const detail = parseAxiosError(err);
          if (detail.includes('已存在')) {
            memberIdentifiers.push(buildNameIdentifier(displayName));
          } else {
            throw err;
          }
        }
      }

      const uniqueIdentifiers = Array.from(new Set(memberIdentifiers));
      const currentlyAssignedIdentifiers = judgeMembers
        .filter((member) => member.assigned_venue_id === setupVenueId)
        .map((member) => member.identifier);

      const identifiersToRemove = currentlyAssignedIdentifiers.filter(
        (identifier) => !uniqueIdentifiers.includes(identifier)
      );

      await Promise.all([
        ...uniqueIdentifiers.map((identifier) =>
          axios.patch(
            `${API_BASE_URL}/api/admin/members/${encodeURIComponent(identifier)}/status`,
            { assigned_venue_id: setupVenueId, is_voted: false },
            { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
          )
        ),
        ...identifiersToRemove.map((identifier) =>
          axios.patch(
            `${API_BASE_URL}/api/admin/members/${encodeURIComponent(identifier)}/status`,
            { assigned_venue_id: '', is_voted: false },
            { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
          )
        ),
      ]);

      setIsVenueSetupOpen(false);
      setSetupVenueId('');
      setSetupVenueNameDraft('');
      setSetupProjectDraft('');
      setSetupSelectedMemberIds([]);
      setSetupNewMemberDraft('');
      setMessage(`已完成會場設定`);
      await Promise.all([loadMembers('', true), loadVenues()]);
    } catch (err: unknown) {
      setError('儲存會場設定失敗：' + parseAxiosError(err));
    } finally {
      setIsSavingVenueSetup(false);
    }
  };

  const deleteVenue = async (venueId: string) => {
    if (!authHeaders || deletingVenueId === venueId) {
      return;
    }
    try {
      setDeletingVenueId(venueId);
      await axios.delete(`${API_BASE_URL}/api/admin/venues/${venueId}`, {
        headers: authHeaders,
        params: selectedMemberCampaignId ? { campaign_id: selectedMemberCampaignId } : undefined,
      });
      setMessage('會場已刪除');
      setPendingVenueDelete(null);
      await Promise.all([loadVenues(), loadMembers('', true)]);
    } catch (err: unknown) {
      setError('刪除會場失敗：' + parseAxiosError(err));
    } finally {
      setDeletingVenueId(null);
    }
  };

  const startCampaign = async () => {
    if (!authHeaders || isStartingCampaign || !canStartAnotherCampaign) {
      return;
    }

    try {
      setIsStartingCampaign(true);
      const normalizedBudget = normalizeBudgetInput(campaignBudgetInput);
      const normalizedInvestorBudget = campaignInvestorBudgetInput.trim() ? normalizeBudgetInput(campaignInvestorBudgetInput) : null;
      const closeInHours = campaignCloseInHours === '' || campaignCloseInHours === '0'
        ? null
        : parseFloat(campaignCloseInHours) || 48;
      await axios.post(
        `${API_BASE_URL}/api/admin/system/start`,
        {
          label: campaignLabelInput.trim() || undefined,
          judge_budget: normalizedBudget,
          investor_budget: normalizedInvestorBudget,
          close_in_hours: closeInHours,
        },
        { headers: authHeaders }
      );
      setMessage(
        normalizedInvestorBudget != null
          ? `本年度專題模擬投資系統已啟動（評審 ${normalizedBudget.toLocaleString()} 元，投資客 ${normalizedInvestorBudget.toLocaleString()} 元）`
          : `本年度專題模擬投資系統已啟動（評審與投資客皆為 ${normalizedBudget.toLocaleString()} 元）`
      );
      setCampaignBudgetInput('10000');
      setCampaignInvestorBudgetInput('');
      setCampaignCloseInHours('48');
        setIsQuickstartOpen(false);
      const latestState = await loadSystemState();
      const activeId = latestState?.current_campaign?.id || '';
      if (activeId) {
        setSelectedMemberCampaignId(activeId);
      }
      await Promise.all([loadMembers(activeId, true), loadVenues(activeId)]);
      setAdminTab('venues');
    } catch (err: unknown) {
      setError('啟動場次失敗：' + parseAxiosError(err));
    } finally {
      setIsStartingCampaign(false);
    }
  };

  const updateCampaignBudget = async (targetCampaignId: string) => {
    if (!authHeaders || updatingCampaignBudgetId) {
      return;
    }

    try {
      setUpdatingCampaignBudgetId(targetCampaignId);
      const normalizedBudget = normalizeBudgetInput(campaignBudgetDrafts[targetCampaignId] ?? '10000');
      const normalizedInvestorBudget = (campaignInvestorBudgetDrafts[targetCampaignId] ?? '').trim()
        ? normalizeBudgetInput(campaignInvestorBudgetDrafts[targetCampaignId])
        : null;
      await axios.patch(
        `${API_BASE_URL}/api/admin/system/settings`,
        { judge_budget: normalizedBudget, investor_budget: normalizedInvestorBudget },
        {
          headers: authHeaders,
          params: { campaign_id: targetCampaignId },
        }
      );
      setCampaignBudgetDrafts((prev) => ({
        ...prev,
        [targetCampaignId]: String(normalizedBudget),
      }));
      setCampaignInvestorBudgetDrafts((prev) => ({
        ...prev,
        [targetCampaignId]: normalizedInvestorBudget != null ? String(normalizedInvestorBudget) : '',
      }));
      setMessage(
        normalizedInvestorBudget != null
          ? `已更新場次預算：評審 ${normalizedBudget.toLocaleString()} 元，投資客 ${normalizedInvestorBudget.toLocaleString()} 元`
          : `已更新場次預算：投資客沿用評審 ${normalizedBudget.toLocaleString()} 元`
      );
      await loadSystemState();
    } catch (err: unknown) {
      setError('更新場次預算失敗：' + parseAxiosError(err));
    } finally {
      setUpdatingCampaignBudgetId(null);
    }
  };

  const openCampaignGuideEditor = (campaign: SystemCampaign) => {
    setEditingCampaignGuide(campaign);
    setCampaignGuideTitleDraft((campaign.judge_panel_note_title || '').trim());
    setCampaignGuideContentDraft(campaign.judge_panel_note_content || '');
    setCampaignGuideFormatDraft(campaign.judge_panel_note_format === 'markdown' ? 'markdown' : 'text');
  };

  const updateCampaignGuide = async () => {
    if (!authHeaders || !editingCampaignGuide || isSavingCampaignGuide) {
      return;
    }

    try {
      setIsSavingCampaignGuide(true);
      await axios.patch(
        `${API_BASE_URL}/api/admin/campaigns/${encodeURIComponent(editingCampaignGuide.id)}/judge-panel-note`,
        {
          title: campaignGuideTitleDraft.trim(),
          content: campaignGuideContentDraft,
          content_format: campaignGuideFormatDraft,
        },
        { headers: authHeaders }
      );
      await loadSystemState();
      setMessage('已更新評審/投資說明內容');
      setEditingCampaignGuide(null);
    } catch (err: unknown) {
      setError('更新說明內容失敗：' + parseAxiosError(err));
    } finally {
      setIsSavingCampaignGuide(false);
    }
  };

  const closeCampaign = async (targetCampaignId: string) => {
    if (!authHeaders || closingCampaignId || !isSuperAdmin(authUser?.role)) {
      return;
    }
    try {
      setClosingCampaignId(targetCampaignId);
      await axios.post(
        `${API_BASE_URL}/api/admin/system/close`,
        {},
        {
          headers: authHeaders,
          params: { campaign_id: targetCampaignId },
        }
      );
      setMessage('本回專題模擬投資系統已關閉並封存');
      await loadSystemState();
      setSelectedMemberCampaignId('');
      setMembers([]);
      await loadVenues();
    } catch (err: unknown) {
      setError('關閉場次失敗：' + parseAxiosError(err));
    } finally {
      setClosingCampaignId(null);
    }
  };

  const deleteArchivedCampaign = async (campaign: SystemCampaign) => {
    if (!authHeaders) {
      return;
    }
    if (campaign.status !== 'closed') {
      setError('僅能刪除已封存場次');
      return;
    }
    try {
      setIsDeletingArchive(true);
      await axios.delete(`${API_BASE_URL}/api/admin/system/archives/${encodeURIComponent(campaign.id)}`, {
        headers: authHeaders,
        params: { year: campaign.year }
      });
      await waitForSystemState((state) => {
        const yearKey = String(campaign.year);
        const yearHistory = state.campaigns_by_year[yearKey] ?? [];
        const yearDeleted = state.recently_deleted_by_year[yearKey] ?? [];
        const stillInHistory = yearHistory.some((row) => row.id === campaign.id && row.status === 'closed');
        const movedToDeleted = yearDeleted.some((row) => row.id === campaign.id);
        return !stillInHistory && movedToDeleted;
      });
      setMessage(`已移至最近刪除：${campaign.label}`);
      setPendingArchiveDelete(null);
    } catch (err: unknown) {
      setError('刪除封存紀錄失敗：' + parseAxiosError(err));
    } finally {
      setIsDeletingArchive(false);
    }
  };

  const restoreArchivedCampaign = async (campaign: RecentlyDeletedCampaign) => {
    if (!authHeaders || restoringCampaignId === campaign.id) {
      return;
    }
    try {
      setRestoringCampaignId(campaign.id);
      await axios.post(
        `${API_BASE_URL}/api/admin/system/archives/${encodeURIComponent(campaign.id)}/restore`,
        {},
        { headers: authHeaders, params: { year: campaign.year } }
      );
      await waitForSystemState((state) => {
        const yearKey = String(campaign.year);
        const yearHistory = state.campaigns_by_year[yearKey] ?? [];
        const yearDeleted = state.recently_deleted_by_year[yearKey] ?? [];
        const inHistory = yearHistory.some((row) => row.id === campaign.id && row.status === 'closed');
        const stillDeleted = yearDeleted.some((row) => row.id === campaign.id);
        return inHistory && !stillDeleted;
      });
      setMessage(`已還原封存紀錄：${campaign.label}`);
    } catch (err: unknown) {
      setError('還原封存紀錄失敗：' + parseAxiosError(err));
    } finally {
      setRestoringCampaignId(null);
    }
  };

  const downloadArchiveReportPdf = async (campaign: SystemCampaign) => {
    if (!authHeaders || downloadingReportCampaignId === campaign.id) {
      return;
    }
    try {
      setDownloadingReportCampaignId(campaign.id);
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/system/archives/${encodeURIComponent(campaign.id)}/report-pdf`,
        {
          headers: authHeaders,
          params: { year: campaign.year },
          responseType: 'blob',
        }
      );

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = `fundthepitch-archive-${campaign.year}-${campaign.id}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
      setMessage(`已下載封存報告：${campaign.label}`);
    } catch (err: unknown) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        setError('找不到封存報告端點或封存紀錄。請重啟後端並確認已安裝 reportlab。');
      } else {
        setError('下載封存報告失敗：' + parseAxiosError(err));
      }
    } finally {
      setDownloadingReportCampaignId(null);
    }
  };

  const permanentDeleteRecentlyCampaign = async (campaign: RecentlyDeletedCampaign) => {
    if (!authHeaders || permanentDeletingCampaignId === campaign.id) {
      return;
    }
    try {
      setPermanentDeletingCampaignId(campaign.id);
      try {
        await axios.delete(
          `${API_BASE_URL}/api/admin/system/recently-deleted/${encodeURIComponent(campaign.id)}`,
          { headers: authHeaders, params: { year: campaign.year } }
        );
      } catch (primaryErr: unknown) {
        if (axios.isAxiosError(primaryErr) && primaryErr.response?.status === 404) {
          await axios.delete(
            `${API_BASE_URL}/api/admin/system/archives/${encodeURIComponent(campaign.id)}/permanent-delete`,
            { headers: authHeaders, params: { year: campaign.year } }
          );
        } else {
          throw primaryErr;
        }
      }
      await waitForSystemState((state) => {
        const yearKey = String(campaign.year);
        const yearDeleted = state.recently_deleted_by_year[yearKey] ?? [];
        return !yearDeleted.some((row) => row.id === campaign.id);
      });
      setMessage(`已永久刪除：${campaign.label}`);
      setPendingPermanentDelete(null);
    } catch (err: unknown) {
      setError('永久刪除失敗：' + parseAxiosError(err));
    } finally {
      setPermanentDeletingCampaignId(null);
    }
  };

  // 拖曳僅更新前端順序，不自動儲存
  const reorderGroupProjects = useCallback((draggedId: string, targetId: string) => {
    if (!draggedId || !targetId || draggedId === targetId || isSavingGroupOrder) {
      return;
    }

    const sourceIndex = groupOrderProjects.findIndex((project) => project.id === draggedId);
    const targetIndex = groupOrderProjects.findIndex((project) => project.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) {
      return;
    }

    const reordered = [...groupOrderProjects];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setGroupOrderProjects(reordered);
    setVenueProjectOrderCache((prev) => ({ ...prev, [groupOrderVenueId]: reordered }));
    // 不自動呼叫 saveVenueGroupOrder
  }, [groupOrderProjects, isSavingGroupOrder, groupOrderVenueId]);

  const updateMember = async (identifier: string) => {
    if (!authHeaders || updatingMemberId === identifier) {
      return;
    }
    const current = members.find((member) => member.identifier === identifier);
    const targetRole = memberEditRole[identifier] ?? current?.role ?? 'judge';
    const managerIdentifier = (memberEditManager[identifier] || '').trim();
    const startProjectId = (memberEditStartProject[identifier] || '').trim();
    try {
      setUpdatingMemberId(identifier);
      await axios.patch(
        `${API_BASE_URL}/api/admin/members/${encodeURIComponent(identifier)}`,
        {
          display_name: memberEditName[identifier],
          role: targetRole
        },
        {
          headers: authHeaders,
          params: {
            campaign_id: current?.campaign_id || selectedMemberCampaignId,
            year: current?.campaign_year,
          },
        }
      );

      if (isInvestmentMemberRole(targetRole)) {
        const payload: { manager_identifier?: string; start_project_id?: string } = {
          start_project_id: startProjectId,
        };
        if (isSuperAdmin(authUser?.role)) {
          payload.manager_identifier = managerIdentifier;
        }
        await axios.patch(
          `${API_BASE_URL}/api/admin/members/${encodeURIComponent(identifier)}/status`,
          payload,
          {
            headers: authHeaders,
            params: {
              campaign_id: current?.campaign_id || selectedMemberCampaignId,
              year: current?.campaign_year,
            },
          }
        );
      }

      setMessage('成員資料已更新');
      await loadMembers('', true);
      setIsEditingMember(false);
      setSelectedMemberId(null);
    } catch (err: unknown) {
      setError('更新成員失敗：' + parseAxiosError(err));
    } finally {
      setUpdatingMemberId(null);
    }
  };

  const assignJudgeManager = async (judge: AdminMember, managerIdentifier: string | null) => {
    if (!authHeaders || assigningMemberId === judge.identifier) {
      return;
    }
    try {
      setAssigningMemberId(judge.identifier);
      await axios.patch(
        `${API_BASE_URL}/api/admin/members/${encodeURIComponent(judge.identifier)}/status`,
        { manager_identifier: managerIdentifier || '' },
        {
          headers: authHeaders,
          params: {
            campaign_id: judge.campaign_id || selectedMemberCampaignId,
            year: judge.campaign_year,
          },
        }
      );
      setMessage(managerIdentifier
        ? `已將 ${cleanDisplayName(judge.display_name)} 指派給 ${managerNameByIdentifier[managerIdentifier] || '管理者'}`
        : `已取消 ${cleanDisplayName(judge.display_name)} 的管理者綁定`
      );
      await loadMembers('', true);
    } catch (err: unknown) {
      setError('指派管理者失敗：' + parseAxiosError(err));
    } finally {
      setAssigningMemberId(null);
    }
  };

  const executeMoveJudge = async (judge: AdminMember, managerIdentifier: string | null) => {
    setLoadingMoveJudge(judge);
    try {
      await assignJudgeManager(judge, managerIdentifier);
    } finally {
      setLoadingMoveJudge(null);
    }
  };

  const assignMemberToCampaign = async (member: AdminMember, targetCampaignId: string) => {
    if (!authHeaders || !isSuperAdmin(authUser?.role) || assigningMemberId === member.identifier) {
      return;
    }
    const currentCampaignForMember = member.role === 'admin'
      ? (member.managed_campaign_id || '')
      : (member.campaign_id || '');
    if (currentCampaignForMember === targetCampaignId) {
      return;
    }

    try {
      setAssigningMemberId(member.identifier);
      await axios.post(
        `${API_BASE_URL}/api/admin/members/${encodeURIComponent(member.identifier)}/assign-campaign`,
        { target_campaign_id: targetCampaignId },
        { headers: authHeaders }
      );
      setMessage(`已將 ${cleanDisplayName(member.display_name)} 分配到指定場次`);
      await Promise.all([
        loadMembers(selectedMemberCampaignIdRef.current || targetCampaignId, true),
        loadVenues(selectedMemberCampaignIdRef.current || targetCampaignId),
        loadSystemState(),
      ]);
    } catch (err: unknown) {
      setError('分配場次失敗：' + parseAxiosError(err));
    } finally {
      setAssigningMemberId(null);
    }
  };

  const assignJudgeToVenue = async (judge: AdminMember, venueId: string) => {
    if (!authHeaders || assigningMemberId === judge.identifier || judge.assigned_venue_id === venueId) {
      return;
    }
    try {
      setAssigningMemberId(judge.identifier);
      await axios.post(
        `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(venueId)}/assign-judge`,
        { identifier: judge.identifier },
        {
          headers: authHeaders,
          params: selectedMemberCampaignIdRef.current ? { campaign_id: selectedMemberCampaignIdRef.current } : undefined,
        }
      );
      setMessage(`已將 ${cleanDisplayName(judge.display_name)} 指派到會場`);
      await Promise.all([loadMembers(activeCampaignIdRef.current || selectedMemberCampaignIdRef.current, true), loadVenues(activeCampaignIdRef.current || selectedMemberCampaignIdRef.current)]);
    } catch (err: unknown) {
      setError('指派評審到會場失敗：' + parseAxiosError(err));
    } finally {
      setAssigningMemberId(null);
    }
  };

  const unassignJudgeFromVenue = async (judge: AdminMember) => {
    if (!authHeaders || assigningMemberId === judge.identifier || !judge.assigned_venue_id) {
      return;
    }
    try {
      setAssigningMemberId(judge.identifier);
      await axios.patch(
        `${API_BASE_URL}/api/admin/members/${encodeURIComponent(judge.identifier)}/status`,
        { assigned_venue_id: '' },
        {
          headers: authHeaders,
          params: {
            campaign_id: judge.campaign_id || selectedMemberCampaignIdRef.current,
            year: judge.campaign_year,
          },
        }
      );
      setMessage(`已將 ${cleanDisplayName(judge.display_name)} 移回未分配區`);
      await Promise.all([loadMembers(activeCampaignIdRef.current || selectedMemberCampaignIdRef.current, true), loadVenues(activeCampaignIdRef.current || selectedMemberCampaignIdRef.current)]);
    } catch (err: unknown) {
      setError('移除評審會場失敗：' + parseAxiosError(err));
    } finally {
      setAssigningMemberId(null);
    }
  };

  const deleteMember = async (identifier: string) => {
    if (!authHeaders || deletingMemberId === identifier) {
      return;
    }
    try {
      setDeletingMemberId(identifier);
      await axios.delete(`${API_BASE_URL}/api/admin/members/${encodeURIComponent(identifier)}`, {
        headers: authHeaders,
        params: { campaign_id: selectedMemberCampaignId }
      });
      setMessage('成員已刪除');
      setPendingMemberDelete(null);
      setSelectedMemberId(null);
      await loadMembers('', true);
    } catch (err: unknown) {
      setError('刪除成員失敗：' + parseAxiosError(err));
    } finally {
      setDeletingMemberId(null);
    }
  };

  const resetRound = async () => {
    if (!authHeaders || isResettingRound) return;
    try {
      setIsResettingRound(true);
      const targetCampaignId = selectedMemberCampaignId || activeCampaign?.id || undefined;
      await axios.post(
        `${API_BASE_URL}/api/admin/reset-round`,
        {},
        {
          headers: authHeaders,
          params: targetCampaignId ? { campaign_id: targetCampaignId } : undefined,
        }
      );
      setMessage('回合已重置，所有評分與投資記錄已清除');
      await Promise.all([loadMembers(selectedMemberCampaignId, true), loadVenues(selectedMemberCampaignId)]);
    } catch (err: unknown) {
      setError('重置回合失敗：' + parseAxiosError(err));
    } finally {
      setIsResettingRound(false);
    }
  };

  const lockAllMembers = async (targetCampaignId: string) => {
    if (!authHeaders || lockingAllCampaignId === targetCampaignId) {
      return;
    }
    try {
      setLockingAllCampaignId(targetCampaignId);
      const res = await axios.post<{ locked_count: number; total: number }>(
        `${API_BASE_URL}/api/admin/campaigns/${encodeURIComponent(targetCampaignId)}/lock-all`,
        {},
        { headers: authHeaders }
      );
      setMessage(`已鎖定 ${res.data.locked_count} 位成員（共 ${res.data.total} 位）`);
      await loadMembers(targetCampaignId, true);
    } catch (err: unknown) {
      setError('全部鎖定失敗：' + parseAxiosError(err));
    } finally {
      setLockingAllCampaignId(null);
    }
  };

  const unlockMember = async (identifier: string) => {
    if (!authHeaders || unlockingMemberId === identifier) {
      return;
    }
    try {
      setUnlockingMemberId(identifier);
      await axios.post(
        `${API_BASE_URL}/api/admin/members/${encodeURIComponent(identifier)}/unlock`,
        {},
        { headers: authHeaders, params: { campaign_id: selectedMemberCampaignId } }
      );
      setMessage('已解除鎖定');
      await loadMembers(undefined, true);
    } catch (err: unknown) {
      setError('解除鎖定失敗：' + parseAxiosError(err));
    } finally {
      setUnlockingMemberId(null);
    }
  };

  const createMember = async () => {
    if (!authHeaders || isCreatingMember) {
      return;
    }

    const setErrorAndScroll = (message: string) => {
      setError(message);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const displayName = normalizeDisplayName(newMemberName);
    if (!displayName) {
      setErrorAndScroll('請輸入成員姓名');
      return;
    }

    const duplicateMember = members.find(
      (member) => normalizeDisplayName(member.display_name).toLowerCase() === displayName.toLowerCase()
    );
    if (duplicateMember) {
      setErrorAndScroll('新增成員失敗：姓名重複，請使用不同姓名');
      return;
    }

    const isSuperAdminUser = isSuperAdmin(authUser?.role);
    const isGlobalAdminMode = isSuperAdminUser && !isCampaignActive;
    const normalizedNewMemberRole: 'judge' | 'investor' | 'admin' =
      !isSuperAdminUser && newMemberRole === 'admin' ? 'judge' : newMemberRole;
    const targetRole: 'judge' | 'investor' | 'admin' = isGlobalAdminMode ? 'admin' : normalizedNewMemberRole;
    const targetCampaignId = activeCampaign?.id || '';

    if (isInvestmentMemberRole(targetRole) && !targetCampaignId) {
      setErrorAndScroll('請先啟動場次才能新增成員');
      return;
    }

    try {
      setIsCreatingMember(true);
      const createResponse = await axios.post<{ member?: { identifier?: string } }>(
        `${API_BASE_URL}/api/admin/members`,
        { display_name: displayName, role: targetRole },
        {
          headers: authHeaders,
          params: targetCampaignId ? { campaign_id: targetCampaignId } : undefined,
        }
      );

      const createdMemberIdentifier = createResponse.data?.member?.identifier || buildNameIdentifier(displayName);
      if (isInvestmentMemberRole(targetRole) && newMemberVenueId) {
        await axios.post(
          `${API_BASE_URL}/api/admin/venues/${encodeURIComponent(newMemberVenueId)}/assign-judge`,
          { identifier: createdMemberIdentifier },
          {
            headers: authHeaders,
            params: targetCampaignId ? { campaign_id: targetCampaignId } : undefined,
          }
        );
      }

      setMessage(targetRole === 'admin' ? '工作人員已新增' : `${roleLabel(targetRole)}已新增`);
      setNewMemberName('');
      setNewMemberVenueId('');
      if (isSuperAdminUser) {
        setNewMemberCampaignId('');
      }
      if (!isGlobalAdminMode) {
        setNewMemberRole('judge');
      }
      await loadMembers(selectedMemberCampaignId || '', true);
      setIsCreateMemberModalOpen(false);
    } catch (err: unknown) {
      setErrorAndScroll('新增成員失敗：' + parseAxiosError(err));
    } finally {
      setIsCreatingMember(false);
    }
  };

  const loadTempSuperAdminAccounts = useCallback(async () => {
    if (!authHeaders || !authUser?.can_generate_super_admin) {
      return;
    }
    try {
      const response = await axios.get<TempSuperAdminAccount[]>(`${API_BASE_URL}/api/admin/super-admin/accounts`, {
        headers: authHeaders,
      });
      setTempSuperAdminAccounts(response.data);
      setTempAdminExtendHours((prev) => {
        const next = { ...prev };
        response.data.forEach((item) => {
          if (!next[item.identifier]) {
            next[item.identifier] = '720';
          }
        });
        return next;
      });
    } catch (err: unknown) {
      setError('讀取臨時最高管理員帳號失敗：' + parseAxiosError(err));
    }
  }, [authHeaders, authUser?.can_generate_super_admin, parseAxiosError]);

  const createTempSuperAdmin = async () => {
    if (!authHeaders || !authUser?.can_generate_super_admin || isCreatingTempAdmin) {
      return;
    }
    const validHours = Number(tempAdminValidHours);
    if (!Number.isFinite(validHours) || validHours <= 0) {
      setError('請輸入有效的小時數');
      return;
    }
    try {
      setIsCreatingTempAdmin(true);
      const response = await axios.post<TempSuperAdminCreateResult>(
        `${API_BASE_URL}/api/admin/super-admin/accounts`,
        { valid_hours: validHours },
        { headers: authHeaders },
      );
      setLatestCreatedTempAdmin(response.data);
      setTempAdminCredentialHistory((prev) => {
        const next: TempSuperAdminCredentialSnapshot = {
          ...response.data,
          created_at: new Date().toISOString(),
        };
        return [next, ...prev].slice(0, 12);
      });
      setMessage('已建立臨時最高管理員帳號');
      await loadTempSuperAdminAccounts();
    } catch (err: unknown) {
      setError('建立臨時最高管理員失敗：' + parseAxiosError(err));
    } finally {
      setIsCreatingTempAdmin(false);
    }
  };

  const extendTempSuperAdmin = async (identifier: string) => {
    if (!authHeaders || !authUser?.can_generate_super_admin || updatingTempAdminId === identifier) {
      return;
    }
    const validHours = Number(tempAdminExtendHours[identifier] || '0');
    if (!Number.isFinite(validHours) || validHours <= 0) {
      setError('請輸入有效的小時數');
      return;
    }
    try {
      setUpdatingTempAdminId(identifier);
      await axios.patch(
        `${API_BASE_URL}/api/admin/super-admin/accounts/${encodeURIComponent(identifier)}/expiry`,
        { valid_hours: validHours },
        { headers: authHeaders },
      );
      setMessage('已更新臨時帳號有效時間');
      await loadTempSuperAdminAccounts();
    } catch (err: unknown) {
      setError('更新有效時間失敗：' + parseAxiosError(err));
    } finally {
      setUpdatingTempAdminId(null);
    }
  };

  const deleteTempSuperAdmin = async (identifier: string, displayName: string) => {
    if (!authHeaders || !authUser?.can_generate_super_admin || deletingTempAdminId === identifier) {
      return;
    }
    const shouldDelete = window.confirm(`確定要刪除臨時最高管理員「${displayName}」嗎？此操作無法復原。`);
    if (!shouldDelete) {
      return;
    }
    try {
      setDeletingTempAdminId(identifier);
      await axios.delete(`${API_BASE_URL}/api/admin/super-admin/accounts/${encodeURIComponent(identifier)}`, {
        headers: authHeaders,
      });
      setTempAdminCredentialHistory((prev) => prev.filter((row) => row.identifier !== identifier));
      setLatestCreatedTempAdmin((prev) => (prev?.identifier === identifier ? null : prev));
      setMessage('已刪除臨時最高管理員帳號');
      await loadTempSuperAdminAccounts();
    } catch (err: unknown) {
      setError('刪除臨時最高管理員失敗：' + parseAxiosError(err));
    } finally {
      setDeletingTempAdminId(null);
    }
  };

  const resetTempSuperAdminPassword = async (identifier: string) => {
    if (!authHeaders || !authUser?.can_generate_super_admin || resettingTempAdminId === identifier) {
      return;
    }

    try {
      setResettingTempAdminId(identifier);
      const response = await axios.post<TempSuperAdminCreateResult>(
        `${API_BASE_URL}/api/admin/super-admin/accounts/${encodeURIComponent(identifier)}/reset-password`,
        {},
        { headers: authHeaders },
      );
      setLatestCreatedTempAdmin(response.data);
      setTempAdminCredentialHistory((prev) => {
        const next: TempSuperAdminCredentialSnapshot = {
          ...response.data,
          created_at: new Date().toISOString(),
        };
        return [next, ...prev].slice(0, 20);
      });
      setMessage(`已重設 ${response.data.username} 的密碼`);
      await loadTempSuperAdminAccounts();
    } catch (err: unknown) {
      setError('重設密碼失敗：' + parseAxiosError(err));
    } finally {
      setResettingTempAdminId(null);
    }
  };

  useEffect(() => {
    if (!authUser?.can_generate_super_admin || !authHeaders || currentView !== 'admin') {
      return;
    }
    loadTempSuperAdminAccounts();
  }, [authUser?.can_generate_super_admin, authHeaders, currentView, loadTempSuperAdminAccounts]);

  const renderTempSuperAdminPanel = () => {
    if (!authUser?.can_generate_super_admin) {
      return null;
    }
    return (
      <section className="section judge-form" style={{ marginBottom: 12 }}>
        <h3>生成最高管理員臨時帳號</h3>
        <p style={{ marginBottom: 10 }}>僅主控贊助者可建立。帳號首次登入必須設定自己的帳密、姓名與系所。</p>
        <div className="input-group temp-admin-create-row" style={{ marginBottom: 10 }}>
          <input
            className="investment-input temp-admin-hours-input"
            type="number"
            min="1"
            value={tempAdminValidHours}
            onChange={(e) => setTempAdminValidHours(e.target.value)}
            aria-label="帳號有效時數（小時）"
            placeholder="帳號有效時數（小時，例如 720 = 30 天）"
          />
          <button className="submit-button temp-admin-create-button" onClick={createTempSuperAdmin} disabled={isCreatingTempAdmin}>
            {isCreatingTempAdmin ? '建立中...' : '生成臨時帳號'}
          </button>
        </div>
        <p style={{ marginTop: -2, marginBottom: 10, fontSize: 13, color: '#5b6476' }}>
          預設 720 小時，約等於 30 天。
        </p>
        {latestCreatedTempAdmin && (
          <div className="investment-item" style={{ marginBottom: 10 }}>
            <p><strong>最新生成帳號：</strong>{latestCreatedTempAdmin.username}</p>
            <p><strong>最新生成密碼：</strong>{latestCreatedTempAdmin.password}</p>
            <p><strong>到期時間：</strong>{new Date(latestCreatedTempAdmin.access_until).toLocaleString('zh-Hant-TW')}</p>
          </div>
        )}
        {tempAdminCredentialHistory.length > 0 && (
          <div className="investment-item" style={{ marginBottom: 10 }}>
            <p style={{ marginBottom: 8 }}><strong>近期生成密碼（本機暫存，重新整理後保留）</strong></p>
            <div className="member-list-table">
              {tempAdminCredentialHistory.map((item) => (
                <div key={`${item.identifier}-${item.created_at}`} className="investment-item" style={{ marginBottom: 8 }}>
                  <p><strong>帳號：</strong>{item.username}</p>
                  <p><strong>密碼：</strong>{item.password}</p>
                  <p><strong>建立時間：</strong>{new Date(item.created_at).toLocaleString('zh-Hant-TW')}</p>
                  <div style={{ marginTop: 6 }}>
                    <button
                      type="button"
                      className="temp-admin-copy-password-button"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(item.password);
                          setMessage(`已複製 ${item.username} 的密碼`);
                        } catch {
                          setError('複製密碼失敗，請手動複製');
                        }
                      }}
                    >
                      複製密碼
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="member-list-table">
          {tempSuperAdminAccounts.map((account) => (
            <div key={account.identifier} className="investment-item" style={{ marginBottom: 8 }}>
              <p><strong>{cleanDisplayName(account.display_name)}</strong>（{account.username}）</p>
              <p>系所：{account.department || '未設定'}；狀態：{account.is_active ? '有效' : '已過期'}；首次登入：{account.first_login_completed ? '已完成' : '未完成'}</p>
              <p>到期：{new Date(account.access_until).toLocaleString('zh-Hant-TW')}</p>
              <div className="input-group temp-admin-extend-row" style={{ marginTop: 8 }}>
                <input
                  className="investment-input temp-admin-extend-hours-input"
                  type="number"
                  min="1"
                  value={tempAdminExtendHours[account.identifier] || '720'}
                  onChange={(e) => setTempAdminExtendHours((prev) => ({ ...prev, [account.identifier]: e.target.value }))}
                  aria-label="續期時數（小時）"
                  placeholder="續期時數（小時，例如 720 = 30 天）"
                />
                <button
                  className="temp-admin-extend-button"
                  onClick={() => extendTempSuperAdmin(account.identifier)}
                  disabled={updatingTempAdminId === account.identifier || deletingTempAdminId === account.identifier || resettingTempAdminId === account.identifier}
                >
                  {updatingTempAdminId === account.identifier ? '更新中...' : '更新有效時間'}
                </button>
                <button
                  type="button"
                  className="temp-admin-reset-password-button"
                  onClick={() => setPendingTempPasswordReset(account)}
                  disabled={resettingTempAdminId === account.identifier || updatingTempAdminId === account.identifier || deletingTempAdminId === account.identifier}
                >
                  {resettingTempAdminId === account.identifier ? '生成中...' : '重新生成密碼'}
                </button>
                <button
                  type="button"
                  className="temp-admin-delete-button"
                  onClick={() => deleteTempSuperAdmin(account.identifier, account.display_name)}
                  disabled={deletingTempAdminId === account.identifier || updatingTempAdminId === account.identifier || resettingTempAdminId === account.identifier}
                >
                  {deletingTempAdminId === account.identifier ? '刪除中...' : '刪除帳號'}
                </button>
              </div>
            </div>
          ))}
          {tempSuperAdminAccounts.length === 0 && <p>目前尚未建立臨時最高管理員帳號。</p>}
        </div>
      </section>
    );
  };

  const renderLogin = () => {
    const canMemberLogin = Boolean(inviteCampaign);
    return (
    <div className="container" style={{ maxWidth: 720, marginTop: 36 }}>
      <section className="section">
        <h2>登入</h2>
        <p>
          {inviteCampaign
            ? `你正在進入 ${inviteCampaign.year} 年 ${inviteCampaign.label} 專題會，請輸入員編或姓名登入。評審、投資客與工作人員需先由贊助者或工作人員新增後才能進入。`
            : '評審、投資客與工作人員請使用對應場次連結進入。工作人員應由使用者登入入口進入。'}
        </p>
        {inviteCampaign && (
          <div className="invite-banner">
            <strong>專題會：</strong> {inviteCampaign.year} / {inviteCampaign.label}
          </div>
        )}
      </section>

      <section className="section judge-form">
        <div className="nav-buttons" style={{ marginBottom: 14 }}>
          {canMemberLogin ? (
            <>
              <button className={loginMode === 'member' ? 'active' : ''} onClick={() => setLoginMode('member')} type="button">使用者登入</button>
              <button className={loginMode === 'admin' ? 'active' : ''} onClick={() => setLoginMode('admin')} type="button">贊助者登入</button>
            </>
          ) : (
            <button className="active" type="button">贊助者登入</button>
          )}
        </div>

        {canMemberLogin && loginMode === 'member' ? (
          <>
            <div className="form-group">
              <label>員編或姓名</label>
              <input
                className="investment-input"
                value={displayNameInput}
                onChange={(e) => setDisplayNameInput(e.target.value)}
                placeholder="例如：1234 或 王評審"
              />
            </div>
            <button className="submit-button" onClick={loginWithName} disabled={isLoggingIn || backendStatus !== 'ready'}>
              {isLoggingIn ? (
                <span className="button-loading">
                  <span className="button-spinner" aria-hidden="true"></span>
                  正在登入中...
                </span>
              ) : '進入系統'}
            </button>
          </>
        ) : (
          <>
            <div className="form-group">
              <label>贊助者帳號</label>
              <input
                className="investment-input"
                value={adminLoginUsername}
                onChange={(e) => {
                  const value = e.target.value;
                  setAdminLoginUsername(value);
                  localStorage.setItem(LAST_ADMIN_USERNAME_STORAGE_KEY, value);
                }}
                placeholder="請輸入贊助者帳號"
              />
            </div>
            <div className="form-group">
              <label>贊助者密碼</label>
              <div className="password-input-wrapper">
                <input
                  className="investment-input"
                  type={isShowAdminPassword ? 'text' : 'password'}
                  value={adminLoginPassword}
                  onChange={(e) => setAdminLoginPassword(e.target.value)}
                  placeholder="請輸入密碼"
                />
                <button
                  type="button"
                  className="password-eye-btn"
                  onClick={() => setIsShowAdminPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={isShowAdminPassword ? '隱藏密碼' : '顯示密碼'}
                >
                  {isShowAdminPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <button className="submit-button" onClick={loginAsAdmin} disabled={isLoggingIn || backendStatus !== 'ready'}>
              {isLoggingIn ? (
                <span className="button-loading">
                  <span className="button-spinner" aria-hidden="true"></span>
                  正在登入中...
                </span>
              ) : '贊助者登入'}
            </button>
          </>
        )}
      </section>
    </div>
  );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderLobby = () => (
    <div className="container" style={{ maxWidth: 820, marginTop: 24 }}>
      <section className="section">
        <h2>選擇專題發表廳</h2>
        <p>
          {canBrowseVenues
            ? '先看每個會場資訊再選擇加入，包含教室、評審評審與該會場專題組別。'
            : '目前尚未啟動專題會，尚無會場資料可選擇。'}
        </p>
      </section>

      <section className="section judge-form">
        <div className="form-group">
          <label>目前登入者</label>
          <p><strong>{cleanDisplayName(authUser?.display_name)}</strong></p>
        </div>

        {canBrowseVenues && selectedVenue && (
          <div className="investment-item">
            <h3 style={{ marginBottom: 8 }}>{selectedVenue.name}</h3>
            <p style={{ marginBottom: 8 }}>會場：<strong>{selectedVenue.classroom}</strong></p>
            <p style={{ marginBottom: 8 }}>
              目前評審：
              <strong>{selectedVenue.judges.length > 0 ? ` ${selectedVenue.judges.join('、')}` : ' 尚無評審加入'}</strong>
            </p>
            <p style={{ marginBottom: 8 }}>專題組別：</p>
            <div className="lobby-tags">
              {selectedVenue.projects.map((project) => (
                <span key={project} className="lobby-tag">{project}</span>
              ))}
            </div>
          </div>
        )}

        {canBrowseVenues && <div className="lobby-grid">
          {venues.map((venue) => {
            const joinedVenueId = judgeStatus?.assigned_venue_id || authUser?.venue_id || null;
            const isLockedToAnotherVenue = Boolean(joinedVenueId && joinedVenueId !== venue.id);
            return (
              <button
                key={venue.id}
                type="button"
                className={`lobby-card ${selectedVenueId === venue.id ? 'active' : ''}`}
                disabled={isLockedToAnotherVenue}
                onClick={() => setSelectedVenueId(venue.id)}
              >
                <h3>{venue.name}</h3>
                <p>會議廳：{venue.classroom}</p>
                <p>評審人數：{venue.judges.length}</p>
                <p>專題組數：{venue.projects.length}</p>
              </button>
            );
          })}
        </div>}

        {canBrowseVenues && !isSuperAdminUser && <div className="nav-buttons" style={{ marginTop: 12 }}>
          {!judgeStatus?.assigned_venue_id && (
            <button className="active" onClick={joinVenue} disabled={isJoiningVenue}>
              {isJoiningVenue ? '加入中...' : '加入這個會場'}
            </button>
          )}
        </div>}
      </section>
    </div>
  );

  const renderAdmin = () => (
    <div className="container admin-shell">
      {isLoadingAdminData && (
        <section className="section admin-loading-section">
          <span className="admin-loading-spinner" aria-hidden="true"></span>
          <h3>資料載入中...</h3>
          <p>正在同步場次、成員與會場，請稍候。</p>
        </section>
      )}

      <section className="section admin-top-panel">
        <button
          type="button"
          className="admin-collapse-header"
          onClick={() => setIsHeroPanelOpen((prev) => !prev)}
          aria-expanded={isHeroPanelOpen}
        >
          <span>專題概況與管理</span>
          <span className={`admin-collapse-chevron${isHeroPanelOpen ? ' open' : ''}`}>▾</span>
        </button>
        {isHeroPanelOpen && (
          <div className="admin-top-row">
            <div className="admin-hero">
              <div className="admin-hero-copy">
                <span className="admin-kicker">ADMIN CONSOLE</span>
                <h2>{displayedActiveCampaigns.length > 0 ? '進行中的專題會' : '尚未啟動專題會'}</h2>
                <p>
                  {displayedActiveCampaigns.length > 0
                    ? `目前共有 ${displayedActiveCampaigns.length} 個啟動中的專題會。可直接在下方查看邀請網址、管理會議廳與拖拉分配評審。`
                    : '請先建立新場次，再設定會場與成員。'}
                </p>
                <div className="admin-hero-metrics">
                  {adminHeroMetrics.map((metric) => (
                    <div key={metric.label} className="admin-hero-metric">
                      <span>{metric.label}</span>
                      <strong>{metric.value}</strong>
                    </div>
                  ))}
                </div>
                {shouldShowAdminProgressDashboard && (
                  <div className="admin-progress-dashboard">
                    <div className="admin-progress-header-row">
                      <button
                        type="button"
                        className="admin-progress-dashboard-toggle"
                        onClick={() => setIsAdminProgressOpen((prev) => !prev)}
                        aria-expanded={isAdminProgressOpen}
                      >
                        <span className="admin-progress-dashboard-title">評分與投資進度</span>
                        <span className={`admin-collapse-chevron${isAdminProgressOpen ? ' open' : ''}`}>▾</span>
                      </button>
                      <button
                        type="button"
                        className="admin-progress-refresh-btn"
                        onClick={refreshAdminProgressDashboard}
                        disabled={!progressDashboardCampaignId || isAdminProgressRefreshing}
                        title="立即刷新進度資料"
                      >
                        {isAdminProgressRefreshing ? '刷新中...' : '↻ 立即刷新'}
                      </button>
                    </div>
                    {isAdminProgressOpen && (
                      <>
                        <div className="admin-progress-role-grid">
                          <div className="admin-progress-role-card">
                            <div className="admin-progress-role-head">
                              <strong>評審進度</strong>
                              <span>{adminJudgeProgressRows.length} 人</span>
                            </div>
                            {adminJudgeProgressRows.length === 0 ? (
                              <p className="admin-progress-empty">目前沒有評審資料。</p>
                            ) : (
                              <div className="admin-progress-member-list">
                                {adminJudgeProgressRows.map((row) => (
                                  <div
                                    key={row.identifier}
                                    className="admin-progress-member-row admin-progress-member-row--clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => void openProgressDetail(row)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void openProgressDetail(row); }}
                                    title="點擊查看各組別評分投資詳情"
                                  >
                                    <div className="admin-progress-member-head">
                                      <span>評審：{row.displayName}</span>
                                      <span className="admin-progress-member-status">
                                        <span>{row.progress}%</span>
                                        {row.isLocked ? (
                                          <button
                                            type="button"
                                            className="admin-progress-lock-icon locked admin-progress-lock-btn"
                                            aria-label="已鎖定，點擊解除鎖定"
                                            title="已鎖定，點擊解除鎖定"
                                            onClick={(e) => { e.stopPropagation(); setPendingProgressUnlock({ identifier: row.identifier, displayName: row.displayName, role: row.role }); }}
                                          >
                                            🔒
                                          </button>
                                        ) : (
                                          <span
                                            className="admin-progress-lock-icon unlocked"
                                            aria-label="未鎖定"
                                            title="未鎖定"
                                          >
                                            🔓
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                    <div className="admin-progress-bar-track">
                                      <div
                                        className={`admin-progress-bar-fill ${row.progressTone}`}
                                        style={{ width: `${row.progress}%` }}
                                      />
                                    </div>
                                    <div className="admin-progress-member-meta">
                                      <span>{row.progressDetail}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          <div className="admin-progress-role-card">
                            <div className="admin-progress-role-head">
                              <strong>投資客進度</strong>
                              <span>{adminInvestorProgressRows.length} 人</span>
                            </div>
                            {adminInvestorProgressRows.length === 0 ? (
                              <p className="admin-progress-empty">目前沒有投資客資料。</p>
                            ) : (
                              <div className="admin-progress-member-list">
                                {adminInvestorProgressRows.map((row) => (
                                  <div
                                    key={row.identifier}
                                    className="admin-progress-member-row admin-progress-member-row--clickable"
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => void openProgressDetail(row)}
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') void openProgressDetail(row); }}
                                    title="點擊查看各組別投資詳情"
                                  >
                                    <div className="admin-progress-member-head">
                                      <span>投資客：{row.displayName}</span>
                                      <span className="admin-progress-member-status">
                                        <span>{row.progress}%</span>
                                        {row.isLocked ? (
                                          <button
                                            type="button"
                                            className="admin-progress-lock-icon locked admin-progress-lock-btn"
                                            aria-label="已鎖定，點擊解除鎖定"
                                            title="已鎖定，點擊解除鎖定"
                                            onClick={(e) => { e.stopPropagation(); setPendingProgressUnlock({ identifier: row.identifier, displayName: row.displayName, role: row.role }); }}
                                          >
                                            🔒
                                          </button>
                                        ) : (
                                          <span
                                            className="admin-progress-lock-icon unlocked"
                                            aria-label="未鎖定"
                                            title="未鎖定"
                                          >
                                            🔓
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                    <div className="admin-progress-bar-track">
                                      <div
                                        className={`admin-progress-bar-fill ${row.progressTone}`}
                                        style={{ width: `${row.progress}%` }}
                                      />
                                    </div>
                                    <div className="admin-progress-member-meta">
                                      <span>{row.progressDetail}</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="admin-campaign-section">
              {canStartAnotherCampaign && (
                <div className="admin-campaign-quickstart">
                  <button
                    type="button"
                    className="admin-quickstart-toggle"
                    onClick={() => setIsQuickstartOpen((prev) => !prev)}
                    aria-expanded={isQuickstartOpen}
                  >
                    <span className="admin-quickstart-head-left">
                      <span className="admin-quickstart-indicator" aria-hidden="true"></span>
                      <span className="admin-quickstart-title">快速建立新場次</span>
                    </span>
                    <span className={`admin-collapse-chevron${isQuickstartOpen ? ' open' : ''}`}>▾</span>
                  </button>
                  {isQuickstartOpen && (
                    <>
                      <p className="admin-top-panel-sub">啟動後會自動產生邀請網址。</p>
                      <div className="admin-campaign-create-col">
                        <div className="admin-campaign-field">
                          <input
                            className="investment-input"
                            value={campaignLabelInput}
                            onChange={(e) => setCampaignLabelInput(e.target.value)}
                            placeholder="場次名稱（選填）"
                            disabled={!canStartAnotherCampaign}
                          />
                          <p className="admin-note">不填則自動命名。</p>
                        </div>
                        <div className="admin-campaign-field">
                          <input
                            className="investment-input"
                            type="number"
                            min="1000"
                            step="500"
                            value={campaignBudgetInput}
                            onChange={(e) => setCampaignBudgetInput(e.target.value)}
                            placeholder="每位評審預算（元）"
                            disabled={!canStartAnotherCampaign}
                          />
                          <p className="admin-note">可依系所需求調整。</p>
                        </div>
                        <div className="admin-campaign-field">
                          <input
                            className="investment-input"
                            type="number"
                            min="1000"
                            step="500"
                            value={campaignInvestorBudgetInput}
                            onChange={(e) => setCampaignInvestorBudgetInput(e.target.value)}
                            placeholder="投資客預算（留白 = 跟評審相同）"
                            disabled={!canStartAnotherCampaign}
                          />
                          <p className="admin-note">可留白，留白時投資客預算會跟評審相同。</p>
                        </div>
                        <div className="admin-campaign-field">
                          <select
                            className="investment-input"
                            value={campaignCloseInHours}
                            onChange={(e) => setCampaignCloseInHours(e.target.value)}
                            disabled={!canStartAnotherCampaign}
                          >
                            <option value="24">自動封存：24 小時後</option>
                            <option value="48">自動封存：48 小時後（預設）</option>
                            <option value="72">自動封存：72 小時後</option>
                            <option value="168">自動封存：1 週後</option>
                            <option value="">無期限（不自動封存）</option>
                          </select>
                          <p className="admin-note">時間到自動封存結果。</p>
                        </div>
                        <button className="submit-button admin-campaign-start-btn" onClick={startCampaign} disabled={isStartingCampaign || !canStartAnotherCampaign}>
                          {isStartingCampaign ? (
                            <span className="button-loading-content">
                              <span className="button-spinner" aria-hidden="true"></span>
                              啟動中...
                            </span>
                          ) : '啟動新場次'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="admin-campaign-grid">
                {displayedActiveCampaigns.length === 0 && (
                  <div className="admin-history-empty admin-campaign-empty">
                    <p>目前沒有進行中的專題會。</p>
                    {!isSuperAdmin(authUser?.role) && (
                      <p className="admin-note" style={{ marginTop: 6 }}>
                        提示：只有贊助者帳號可啟動與封存場次；工作人員可編輯預算、複製分享連結與 QRCode。
                      </p>
                    )}
                  </div>
                )}
                {displayedActiveCampaigns.map((campaign) => {
                  const inviteUrl = campaign.invite_token
                    ? `${window.location.origin}${window.location.pathname}?c=${encodeURIComponent(campaign.invite_token)}`
                    : '';
                  return (
                    <div key={campaign.id} className="campaign-card">
                      <div className="campaign-card-header">
                        <div>
                          <div className="admin-status-badge">進行中 {campaign.year}</div>
                          <h4>{campaign.label}</h4>
                        </div>
                        {isSuperAdmin(authUser?.role) && (
                          <button onClick={() => setPendingCloseCampaign(campaign)} disabled={Boolean(closingCampaignId)}>
                            {closingCampaignId === campaign.id ? '封存中...' : '關閉並封存'}
                          </button>
                        )}
                      </div>
                      <p className="admin-note">啟動時間：{new Date(campaign.started_at).toLocaleString('zh-Hant-TW')}</p>
                      {campaign.close_at ? (
                        <p className="admin-note campaign-close-at-note">
                          ⏰ 自動封存時間：{new Date(campaign.close_at).toLocaleString('zh-Hant-TW')}
                        </p>
                      ) : (
                        <p className="admin-note campaign-close-at-note">⏳ 無期限（需手動封存）</p>
                      )}
                      <div className="campaign-budget-editor">
                        <label>請輸入評審預算</label>
                        <div className="admin-campaign-create-row campaign-budget-row">
                          <input
                            className="investment-input admin-budget-input"
                            type="number"
                            min="1000"
                            step="500"
                            value={campaignBudgetDrafts[campaign.id] ?? String(campaign.judge_budget ?? 10000)}
                            onChange={(e) => setCampaignBudgetDrafts((prev) => ({ ...prev, [campaign.id]: e.target.value }))}
                          />
                          <input
                            className="investment-input admin-budget-input"
                            type="number"
                            min="1000"
                            step="500"
                            value={campaignInvestorBudgetDrafts[campaign.id] ?? ''}
                            onChange={(e) => setCampaignInvestorBudgetDrafts((prev) => ({ ...prev, [campaign.id]: e.target.value }))}
                            placeholder="投資客預算（留白 = 同評審）"
                          />
                        </div>
                        <div className="campaign-budget-actions-row campaign-budget-actions-row--primary">
                          <button
                            type="button"
                            className="submit-button admin-budget-update-btn"
                            onClick={() => updateCampaignBudget(campaign.id)}
                            disabled={updatingCampaignBudgetId === campaign.id}
                          >
                            {updatingCampaignBudgetId === campaign.id ? (
                              <span className="button-loading-content">
                                <span className="button-spinner" aria-hidden="true"></span>
                                更新中...
                              </span>
                            ) : '更新預算'}
                          </button>
                        </div>

                        <div className="campaign-control-panel">
                          <h5 className="campaign-control-title">會議控制</h5>
                          <div className="campaign-budget-actions-row campaign-budget-actions-row--control">
                          <button
                            type="button"
                            className="submit-button admin-lock-all-btn"
                            onClick={() => setPendingLockAllCampaignId(campaign.id)}
                            disabled={
                              lockingAllCampaignId === campaign.id ||
                              members.filter((m) => isInvestmentMemberRole(m.role) && m.campaign_id === campaign.id).every((m) => m.is_voted)
                            }
                          >
                            {lockingAllCampaignId === campaign.id ? (
                              <span className="button-loading-content">
                                <span className="button-spinner" aria-hidden="true"></span>
                                鎖定中...
                              </span>
                            ) : (
                              <>
                                <span className="admin-action-icon" aria-hidden="true">🔒</span>
                                全員鎖定
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="submit-button admin-reset-round-btn"
                            onClick={() => setPendingResetRound(true)}
                            disabled={isResettingRound}
                          >
                            {isResettingRound ? (
                              <span className="button-loading-content">
                                <span className="button-spinner" aria-hidden="true"></span>
                                重置中...
                              </span>
                            ) : (
                              <>
                                <span className="admin-action-icon" aria-hidden="true">↻</span>
                                重置回合
                              </>
                            )}
                          </button>
                          <button
                            type="button"
                            className="submit-button admin-guide-edit-btn"
                            onClick={() => openCampaignGuideEditor(campaign)}
                            disabled={isSavingCampaignGuide && editingCampaignGuide?.id === campaign.id}
                          >
                            {isSavingCampaignGuide && editingCampaignGuide?.id === campaign.id ? (
                              <span className="button-loading-content">
                                <span className="button-spinner" aria-hidden="true"></span>
                                儲存中...
                              </span>
                            ) : (
                              <>
                                <span className="admin-action-icon" aria-hidden="true">✎</span>
                                {(campaign.judge_panel_note_content || '').trim().length > 0 ? '編輯說明' : '新增說明'}
                              </>
                            )}
                          </button>
                        </div>
                        </div>
                        <p className="admin-note">
                          目前評審預算 {Number(campaign.judge_budget ?? 10000).toLocaleString()} 元，
                          投資客預算 {campaign.investor_budget != null ? Number(campaign.investor_budget).toLocaleString() : '跟評審相同'}。
                        </p>
                      </div>
                      {inviteUrl && (
                        <div className="admin-invite-box">
                          <label>專題會邀請網址</label>
                          <div className="admin-invite-row">
                            <input className="investment-input" value={inviteUrl} readOnly />
                            <button
                              type="button"
                              onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(inviteUrl);
                                  setMessage('已複製專題會邀請網址');
                                } catch {
                                  setError('複製失敗，請手動複製網址');
                                }
                              }}
                            >
                              複製
                            </button>
                            <button
                              type="button"
                              className="admin-invite-qr-btn"
                              onClick={() => setQrInviteCampaign(campaign)}
                            >
                              QR
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </section>

      <div className="section admin-tab-strip admin-unified-nav">
        <button
          type="button"
          className="admin-tabs-toggle"
          aria-expanded={isAdminTabsOpen}
          aria-label={isAdminTabsOpen ? '收合管理功能選單' : '展開管理功能選單'}
          title={isAdminTabsOpen ? '收合管理功能選單' : '展開管理功能選單'}
          onClick={() => setIsAdminTabsOpen((prev) => !prev)}
        >
          <span className="nav-toggle-icon" aria-hidden="true">{isAdminTabsOpen ? '✕' : '☰'}</span>
        </button>
        <div className={`nav-buttons admin-tabs admin-tabs-buttons ${isAdminTabsOpen ? 'open' : ''}`}>
          {authUser?.can_generate_super_admin && (
            <button
              className={adminTab === 'temp-admin' ? 'active' : ''}
              data-testid="admin-tab-temp-admin"
              onClick={() => {
                setAdminTab('temp-admin');
                setIsAdminTabsOpen(false);
              }}
            >
              臨時帳號
            </button>
          )}
          <button
            className={adminTab === 'venues' ? 'active' : ''}
            data-testid="admin-tab-venues"
            onClick={() => {
              setAdminTab('venues');
              setIsAdminTabsOpen(false);
            }}
            disabled={!canManageVenues}
            title={canManageVenues ? '管理會場與拖拉分派' : '請先啟動場次'}
          >
            會場管理
          </button>
          <button
            className={adminTab === 'group-order' ? 'active' : ''}
            data-testid="admin-tab-group-order"
            onClick={() => {
              setAdminTab('group-order');
              setIsAdminTabsOpen(false);
            }}
            disabled={!canManageVenues}
            title={canManageVenues ? '拖移設定組別巡迴順序' : '請先啟動場次'}
          >
            組別排序
          </button>
          <button
            className={adminTab === 'members' ? 'active' : ''}
            data-testid="admin-tab-members"
            onClick={() => {
              setAdminTab('members');
              setIsAdminTabsOpen(false);
            }}
          >
            成員管理
          </button>

          <button
            className={adminTab === 'archives' ? 'active' : ''}
            data-testid="admin-tab-archives"
            onClick={() => {
              setAdminTab('archives');
              setIsAdminTabsOpen(false);
            }}
          >
            封存紀錄
          </button>
          <button
            className={adminTab === 'deleted' ? 'active' : ''}
            onClick={() => {
              setAdminTab('deleted');
              setIsAdminTabsOpen(false);
            }}
          >
            最近刪除
          </button>
        </div>

      </div>

      {adminTab === 'temp-admin' && renderTempSuperAdminPanel()}
      {false && <section className="section judge-form">
        <div className="admin-section-heading admin-member-toolbar">
          <div>
            <h3>場次管理</h3>
            <p>在此以拖拉方式將成員分配到不同場次。此頁不使用下拉選單切換場次。</p>
          </div>
        </div>

        <div className="form-group">
          <label>場次成員分配（拖拉卡片到目標場次）</label>
          <div className="campaign-assignment-board">
              {(displayedActiveCampaigns || []).map((campaign) => {
                const campaignManagers = members.filter((member) => member.role === 'admin' && member.managed_campaign_id === campaign.id);
                const campaignJudges = members.filter((member) => isInvestmentMemberRole(member.role) && member.campaign_id === campaign.id);
                const merged = [...campaignManagers, ...campaignJudges].sort((a, b) => a.display_name.localeCompare(b.display_name, 'zh-Hant'));
                const stats = campaignMemberStats[campaign.id] || { managers: 0, judges: 0 };
                return (
                  <div
                    key={`campaign-assignment-${campaign.id}`}
                    className="campaign-assignment-col"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (!draggingCampaignMemberId) return;
                      const member = members.find((row) => row.identifier === draggingCampaignMemberId);
                      setDraggingCampaignMemberId(null);
                      if (!member) return;
                      assignMemberToCampaign(member, campaign.id);
                    }}
                  >
                    <div className="campaign-assignment-header">
                      <strong>{campaign.year} / {campaign.label}</strong>
                      <span>{stats.managers} 管理者 / {stats.judges} 投資成員</span>
                    </div>
                    <div className="campaign-assignment-body">
                      {merged.map((member, idx) => (
                        <div
                          key={`campaign-member-${campaign.id}-${member.identifier}-${idx}`}
                          className={`venue-judge-card ${member.role === 'admin' ? 'campaign-role-admin' : 'campaign-role-judge'} ${assigningMemberId === member.identifier ? 'loading' : ''}`}
                          draggable={assigningMemberId !== member.identifier}
                          onDragStart={() => setDraggingCampaignMemberId(member.identifier)}
                          onDragEnd={() => setDraggingCampaignMemberId(null)}
                          onClick={() => {
                            setSelectedMemberId(member.identifier);
                            setIsEditingMember(false);
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="member-name">{formatMemberIdentity(member.identifier, member.display_name)}</div>
                          <div className="venue-judge-meta">{member.role === 'admin' ? '工作人員' : roleLabel(member.role)}</div>
                        </div>
                      ))}
                      {merged.length === 0 && <p className="manager-empty">拖拉成員到此場次</p>}
                    </div>
                  </div>
                );
              })}

              <div
                className="campaign-assignment-col campaign-assignment-col-unassigned"
                onDragOver={(e) => e.preventDefault()}
              >
                <div className="campaign-assignment-header">
                  <strong>未分配場次</strong>
                  <span>
                    {
                      members.filter((member) => (member.role === 'admin'
                        ? !member.managed_campaign_id
                        : !member.campaign_id)).length
                    } 位成員
                  </span>
                </div>
                <div className="campaign-assignment-body">
                  {members
                    .filter((member) => (member.role === 'admin' ? !member.managed_campaign_id : !member.campaign_id))
                    .sort((a, b) => a.display_name.localeCompare(b.display_name, 'zh-Hant'))
                    .map((member) => (
                      <div
                        key={`campaign-member-unassigned-${member.identifier}`}
                        className={`venue-judge-card ${member.role === 'admin' ? 'campaign-role-admin' : 'campaign-role-judge'} ${assigningMemberId === member.identifier ? 'loading' : ''}`}
                        draggable={assigningMemberId !== member.identifier}
                        onDragStart={() => setDraggingCampaignMemberId(member.identifier)}
                        onDragEnd={() => setDraggingCampaignMemberId(null)}
                        onClick={() => {
                          setSelectedMemberId(member.identifier);
                          setIsEditingMember(false);
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="member-name">{formatMemberIdentity(member.identifier, member.display_name)}</div>
                        <div className="venue-judge-meta">{member.role === 'admin' ? '工作人員' : '評審'}</div>
                      </div>
                    ))}
                  {!members.some((member) => (member.role === 'admin' ? !member.managed_campaign_id : !member.campaign_id)) && (
                    <p className="manager-empty">目前沒有未分配場次的成員</p>
                  )}
                </div>
              </div>
            </div>
          </div>
      </section>}

      {adminTab === 'group-order' && (
        <section className="section judge-form admin-group-order-section">
          <div className="admin-section-heading admin-member-toolbar" style={{ justifyContent: 'space-between', display: 'flex', alignItems: 'center' }}>
            <div>
              <h3>組別巡迴排序</h3>
              <p>拖移下方組別即可調整巡迴順序，評審/投資客按下一組時會依此循環。</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="admin-year-picker" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label htmlFor="group-order-campaign" style={{ marginBottom: 0, fontSize: 13 }}>場次</label>
                <input
                  id="group-order-campaign"
                  className="investment-input"
                  value={campaignOptions.find((campaign) => campaign.id === groupOrderCampaignId)
                    ? `${campaignOptions.find((campaign) => campaign.id === groupOrderCampaignId)?.year} / ${campaignOptions.find((campaign) => campaign.id === groupOrderCampaignId)?.label}`
                    : '未指定'}
                  readOnly
                  disabled
                  style={{ minWidth: 220, fontSize: 13 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label htmlFor="group-order-venue" style={{ marginBottom: 0, fontSize: 13 }}>會場</label>
                <select
                  id="group-order-venue"
                  className="investment-input"
                  value={groupOrderVenueId}
                  onChange={(e) => {
                    const nextVenueId = e.target.value;
                    setGroupOrderVenueId(nextVenueId);
                    if (nextVenueId && groupOrderCampaignId) {
                      void loadVenueGroupOrder(nextVenueId, groupOrderCampaignId);
                    }
                  }}
                  style={{ minWidth: 220, fontSize: 13 }}
                >
                  {venues.map((venue) => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name}{venue.classroom && venue.classroom !== venue.name ? `（${venue.classroom}）` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* 儲存按鈕：僅有異動時顯示，緊靠場次/會場右側 */}
            {groupOrderProjects.length > 0 &&
              JSON.stringify(groupOrderProjects.map(p => p.id)) !== JSON.stringify(originalGroupOrderProjects.map(p => p.id)) && (
                <button
                  style={{
                    padding: '6px 18px',
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 8,
                    border: 'none',
                    background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                    color: '#fff',
                    cursor: isSavingGroupOrder ? 'not-allowed' : 'pointer',
                    opacity: isSavingGroupOrder ? 0.7 : 1,
                    boxShadow: '0 2px 6px rgba(37,99,235,0.25)',
                    whiteSpace: 'nowrap',
                    transition: 'opacity 0.2s',
                  }}
                  disabled={isSavingGroupOrder}
                  onClick={() => saveVenueGroupOrder(groupOrderProjects)}
                >
                  {isSavingGroupOrder ? '儲存中...' : '✓ 儲存順序'}
                </button>
              )}
            </div>
          </div>

          {!activeGroupOrderVenue ? (
            <div className="investment-item">
              <p>請先在會場管理建立會場，才能設定組別排序。</p>
            </div>
          ) : isLoadingGroupOrder ? (
            <div className="investment-item">
              <p>載入組別順序中...</p>
            </div>
          ) : groupOrderProjects.length === 0 ? (
            <div className="investment-item">
              <p>目前沒有可排序的組別。</p>
            </div>
          ) : (
            <div>
              <div className="admin-group-order-list" aria-label="可拖移組別排序清單">
                {groupOrderProjects.map((project, index) => (
                  <button
                    key={`group-order-${project.id}`}
                    type="button"
                    className="admin-group-order-item"
                    draggable={!isSavingGroupOrder}
                    onDragStart={() => setDraggingGroupOrderProjectId(project.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (!draggingGroupOrderProjectId) {
                        return;
                      }
                      reorderGroupProjects(draggingGroupOrderProjectId, project.id);
                      setDraggingGroupOrderProjectId(null);
                    }}
                    onDragEnd={() => setDraggingGroupOrderProjectId(null)}
                    disabled={isSavingGroupOrder}
                  >
                    <span className="admin-group-order-index">{index + 1}</span>
                    <span className="admin-group-order-name">{project.name}</span>
                    <span className="admin-group-order-drag" aria-hidden="true">↕</span>
                  </button>
                ))}
              </div>
              {/* 儲存按鈕已移至右上角，這裡移除 */}
            </div>
          )}
        </section>
      )}

      {adminTab === 'members' && <section className="section judge-form" id="admin-members-section" ref={adminMembersSectionRef}>
        <div className="admin-section-heading admin-member-toolbar">
          <div>
            <h3>成員管理</h3>
            <p>顯示所有可管理成員，可依標籤排序後快速檢視與操作。</p>
          </div>
          <div className="admin-year-picker" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {campaignOptions.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <label htmlFor="member-campaign-filter" style={{ marginBottom: 0, fontSize: 13, whiteSpace: 'nowrap' }}>場次</label>
                <select
                  id="member-campaign-filter"
                  className="investment-input"
                  style={{ minWidth: 120, fontSize: 13 }}
                  value={selectedMemberCampaignId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedMemberCampaignId(id);
                    loadMembers(id, true);
                  }}
                >
                  <option value="">請選擇場次</option>
                  {isSuperAdmin(authUser?.role) && (
                    <option value={NO_CAMPAIGN_FILTER}>無場次</option>
                  )}
                  {campaignOptions.map((campaign) => (
                    <option key={campaign.id} value={campaign.id}>
                      {campaign.year} / {campaign.label}{campaign.status === 'closed' ? '（已封存）' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label htmlFor="member-sort-cycle" style={{ marginBottom: 0, fontSize: 13, whiteSpace: 'nowrap' }}>標籤排序</label>
              <div className="admin-sort-cycle-inline">
                <span className="admin-sort-current-label">{memberSortLabelMap[memberSortTag]}</span>
                <button
                  id="member-sort-cycle"
                  type="button"
                  className="admin-sort-cycle-icon-btn"
                  onClick={cycleMemberSortTag}
                  aria-label="切換下一個標籤排序"
                  title={`目前：${memberSortLabelMap[memberSortTag]}，點擊切換到：${memberSortLabelMap[nextMemberSortTag]}`}
                >
                  <span aria-hidden="true">⇄</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="member-create-launcher">
          <div>
            <strong>新增成員</strong>
          </div>
          <button
            type="button"
            className="member-create-launcher-btn"
            onClick={() => setIsCreateMemberModalOpen(true)}
            disabled={!isCampaignActive}
            title={!isCampaignActive ? '請先啟動場次才能新增成員' : ''}
          >
            +
          </button>
        </div>

        {sortedMembersByTag.length === 0 ? (
          <div className="investment-item">
            <p>{!isCampaignActive ? '請先啟動場次才能新增成員' : '目前沒有可顯示的成員資料。'}</p>
          </div>
        ) : (
          <div className="member-list-table">
            {sortedMembersByTag.map((member, idx) => {
              const campaignId = member.role === 'admin' ? member.managed_campaign_id : member.campaign_id;
              const campaignInfo = campaignOptions.find((campaign) => campaign.id === campaignId);
              const isAdminMember = member.role === 'admin';
              const campaignTag = campaignInfo
                ? `${campaignInfo.year} / ${campaignInfo.label}`
                : '未分配場次';
              const venueTag = member.assigned_venue_id || '未分配會場';
              const lockTag = member.is_voted ? '已鎖定' : '可編輯';
              
              // Get judge evaluation status
              const judgeStatus = judgeEvaluationStatusMap[member.identifier];
              const statusColor = judgeStatus ? {
                red: '#ef4444',
                yellow: '#eab308', 
                green: '#22c55e'
              }[judgeStatus.status] : null;
              const statusText = judgeStatus ? {
                red: '未操作',
                yellow: '部分操作',
                green: '已完成'
              }[judgeStatus.status] : '載入中...';
              const statusTitle = judgeStatus ? {
                red: '未投資未評分任何組',
                yellow: `部分投資/評分：${judgeStatus.invested_projects}/${judgeStatus.total_projects} 已投資，${judgeStatus.scored_projects}/${judgeStatus.total_projects} 已評分`,
                green: '全部已評分已投資'
              }[judgeStatus.status] : '載入中...';
              
              return (
                <button
                  key={`member-row-${member.identifier}-${idx}`}
                  type="button"
                  className="member-list-row"
                  onClick={() => {
                    setSelectedMemberId(member.identifier);
                    setIsEditingMember(false);
                  }}
                  style={{
                    borderLeft: !isAdminMember && statusColor ? `4px solid ${statusColor}` : undefined
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
                    <span className="member-list-name">{formatMemberIdentity(member.identifier, member.display_name)}</span>
                    {!isAdminMember && statusColor && (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          flexShrink: 0,
                          cursor: 'help'
                        }}
                        title={statusTitle}
                      >
                        <div
                          style={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                          }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 500, color: statusColor }}>
                          {statusText}
                        </span>
                      </div>
                    )}
                  </div>
                  <span className="member-list-tags">
                    <span className="member-tag role">{isAdminMember ? '工作人員' : roleLabel(member.role)}</span>
                    {!(isAdminMember && campaignTag === '未分配場次') && (
                      <span className="member-tag campaign">{campaignTag}</span>
                    )}
                    {!isAdminMember && (
                      <span className="member-tag venue">{venueTag}</span>
                    )}
                    <span className={`member-tag lock ${member.is_voted ? 'locked' : 'editable'}`}>{lockTag}</span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>}

      {isCreateMemberModalOpen && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setIsCreateMemberModalOpen(false)}>
          <div className="admin-modal admin-member-create-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-member-create-head">
              <div>
                <h3>新增成員</h3>
                <p className="admin-modal-note">建立後可於成員清單立即查看與編輯。</p>
              </div>
              <button
                type="button"
                className="member-create-close"
                onClick={() => setIsCreateMemberModalOpen(false)}
                aria-label="關閉新增成員視窗"
              >
                ×
              </button>
            </div>

            <div className="admin-member-create-form">
              <div className="form-group">
                <label>姓名</label>
                <input
                  className="investment-input"
                  value={newMemberName}
                  onChange={(e) => setNewMemberName(e.target.value)}
                  placeholder={isSuperAdmin(authUser?.role) && !isCampaignActive
                    ? `例如：${(authUser?.department && authUser.department !== '未設定') ? authUser.department : ''}系秘`
                    : '例如：王評審'}
                />
              </div>

              {(isSuperAdmin(authUser?.role) || authUser?.role === 'admin') && (
                <div className="form-group">
                  <label>角色</label>
                  <select
                    className="investment-input"
                    value={!isSuperAdmin(authUser?.role) && newMemberRole === 'admin' ? 'judge' : newMemberRole}
                    onChange={(e) => {
                      const nextRole = e.target.value as 'judge' | 'investor' | 'admin';
                      setNewMemberRole(nextRole);
                      if (!isInvestmentMemberRole(nextRole)) {
                        setNewMemberVenueId('');
                      }
                    }}
                  >
                    <option value="judge">評審</option>
                    <option value="investor">投資者</option>
                    {isSuperAdmin(authUser?.role) && <option value="admin">工作人員</option>}
                  </select>
                </div>
              )}

              {activeCampaign && ((!isSuperAdmin(authUser?.role) && newMemberRole === 'admin') || newMemberRole === 'judge' || newMemberRole === 'investor') && (
                <div className="form-group">
                  <label>指定會場</label>
                  <select
                    className="investment-input"
                    value={newMemberVenueId}
                    onChange={(e) => setNewMemberVenueId(e.target.value)}
                  >
                    <option value="">先不指定（稍後再分配）</option>
                    {venues.map((venue) => (
                      <option key={venue.id} value={venue.id}>
                        {venue.name}{venue.classroom && venue.classroom !== venue.name ? `（${venue.classroom}）` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {activeCampaign && (
                <div className="form-group">
                  <label>場次</label>
                  <input
                    className="investment-input"
                    value={`${activeCampaign.year} / ${activeCampaign.label}`}
                    readOnly
                    disabled
                  />
                </div>
              )}
            </div>

            <div className="admin-modal-actions">
              <button
                className="modal-btn modal-btn-neutral"
                type="button"
                onClick={() => setIsCreateMemberModalOpen(false)}
                disabled={isCreatingMember}
              >
                取消
              </button>
              <button
                className="modal-btn modal-btn-success"
                type="button"
                onClick={createMember}
                disabled={isCreatingMember || !newMemberName.trim()}
              >
                {isCreatingMember ? '新增中...' : '確認新增'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingCampaignGuide && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setEditingCampaignGuide(null);
          }
        }}>
          <div className="admin-modal admin-guide-edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-member-create-head">
              <div>
                <h3>編輯評審/投資說明</h3>
                <p className="admin-modal-note">內容留白時，評審與投資介面將不顯示說明按鈕。</p>
              </div>
              <button
                type="button"
                className="member-create-close"
                onClick={() => setEditingCampaignGuide(null)}
                aria-label="關閉說明編輯視窗"
              >
                ×
              </button>
            </div>

            <div className="admin-member-create-form">
              <div className="form-group">
                <label>標題（顯示在按鈕與彈窗標題）</label>
                <input
                  className="investment-input"
                  value={campaignGuideTitleDraft}
                  onChange={(e) => setCampaignGuideTitleDraft(e.target.value)}
                  placeholder="例如：專題成果整體評分尺規"
                />
              </div>

              <div className="form-group">
                <label>內容格式</label>
                <div className="admin-guide-format-row">
                  <label className="admin-guide-format-option">
                    <input
                      type="radio"
                      name="campaign-guide-format"
                      value="text"
                      checked={campaignGuideFormatDraft === 'text'}
                      onChange={() => setCampaignGuideFormatDraft('text')}
                    />
                    <span>TEXT</span>
                  </label>
                  <label className="admin-guide-format-option">
                    <input
                      type="radio"
                      name="campaign-guide-format"
                      value="markdown"
                      checked={campaignGuideFormatDraft === 'markdown'}
                      onChange={() => setCampaignGuideFormatDraft('markdown')}
                    />
                    <span>Markdown</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>內容</label>
                <textarea
                  className="investment-input admin-modal-textarea"
                  value={campaignGuideContentDraft}
                  onChange={(e) => setCampaignGuideContentDraft(e.target.value)}
                  placeholder={campaignGuideFormatDraft === 'markdown'
                    ? '# 標題\n\n- 支援清單\n- 支援粗體 **文字**\n- 支援表格'
                    : '輸入要顯示在評審/投資介面的說明內容'}
                  rows={12}
                />
              </div>
            </div>

            <div className="admin-modal-actions">
              <button
                className="modal-btn modal-btn-neutral"
                type="button"
                onClick={() => setEditingCampaignGuide(null)}
                disabled={isSavingCampaignGuide}
              >
                取消
              </button>
              <button
                className="modal-btn modal-btn-success"
                type="button"
                onClick={updateCampaignGuide}
                disabled={isSavingCampaignGuide}
              >
                {isSavingCampaignGuide ? '儲存中...' : '儲存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {adminTab === 'archives' && (
        <section className="section admin-history-section">
          <div className="admin-section-heading">
            <div>
              <h3>歷年封存紀錄</h3>
              <p>每年度的投資總額與場次狀態會保留在這裡。</p>
            </div>
          </div>
          <div className="admin-history-grid">
            {Object.values(adminSystemState.campaigns_by_year).every(
              (campaigns) => !campaigns.some((campaign) => campaign.status === 'closed')
            ) && (
              <div className="investment-item admin-history-empty">
                <p>尚無場次紀錄</p>
              </div>
            )}
            {Object.entries(adminSystemState.campaigns_by_year)
              .sort((a, b) => Number(b[0]) - Number(a[0]))
              .filter(([, campaigns]) => campaigns.some((campaign) => campaign.status === 'closed'))
              .map(([year, campaigns]) => (
                <div key={year} className="investment-item admin-history-card">
                  <p className="admin-history-year">{year}</p>
                  {campaigns
                    .filter((campaign) => campaign.status === 'closed')
                    .map((campaign) => (
                    <div key={campaign.id} className="admin-history-row">
                      <strong>{campaign.label}</strong>
                      <span>已封存</span>
                      <span>
                        {campaign.summary?.overall_total_investment !== undefined
                          ? `總投資 ${campaign.summary.overall_total_investment.toLocaleString()} 元`
                          : '尚無統計'}
                      </span>
                      <div className="admin-history-actions">
                        <button onClick={() => setSelectedHistoryCampaign(campaign)}>
                          查看戰況
                        </button>
                        <button
                          onClick={() => downloadArchiveReportPdf(campaign)}
                          disabled={downloadingReportCampaignId === campaign.id}
                        >
                          {downloadingReportCampaignId === campaign.id ? '下載中...' : '下載 PDF'}
                        </button>
                        <button onClick={() => setPendingArchiveDelete(campaign)} disabled={isDeletingArchive}>
                          {isDeletingArchive && pendingArchiveDelete?.id === campaign.id ? '刪除中...' : '刪除封存紀錄'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </section>
      )}

      {adminTab === 'deleted' && (
        <section className="section admin-history-section">
          <div className="admin-section-heading">
            <div>
              <h3>最近刪除（30 天可還原）</h3>
              <p>避免誤刪除，封存紀錄會先進入最近刪除，超過 30 天才會自動清除。</p>
            </div>
          </div>
          <div className="admin-history-grid">
            {Object.keys(adminSystemState.recently_deleted_by_year).length === 0 && (
              <div className="investment-item admin-history-empty">
                <p>目前沒有最近刪除的封存紀錄。</p>
              </div>
            )}
            {Object.entries(adminSystemState.recently_deleted_by_year)
              .sort((a, b) => Number(b[0]) - Number(a[0]))
              .map(([year, campaigns]) => (
                <div key={`deleted-${year}`} className="investment-item admin-history-card">
                  <p className="admin-history-year">{year}</p>
                  {campaigns.map((campaign) => (
                    <div key={`${campaign.id}-${campaign.deleted_at}`} className="admin-history-row admin-history-row-deleted">
                      <strong>{campaign.label}</strong>
                      <span>刪除時間：{new Date(campaign.deleted_at).toLocaleString('zh-Hant-TW')}</span>
                      <span>剩餘可還原天數：{campaign.days_remaining} 天</span>
                      <div className="admin-history-actions">
                        <button onClick={() => restoreArchivedCampaign(campaign)} disabled={restoringCampaignId === campaign.id || permanentDeletingCampaignId === campaign.id}>
                          {restoringCampaignId === campaign.id ? '還原中...' : '還原紀錄'}
                        </button>
                        <button
                          onClick={() => setPendingPermanentDelete(campaign)}
                          disabled={permanentDeletingCampaignId === campaign.id || restoringCampaignId === campaign.id}
                          style={{ marginLeft: 8, background: '#dc2626', color: '#fff', border: 'none' }}
                        >
                          {permanentDeletingCampaignId === campaign.id ? '刪除中...' : '立即刪除'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
          </div>
        </section>
      )}

      {selectedMemberId && (() => {
        const member = members.find((m) => m.identifier === selectedMemberId);
        if (!member) return null;

        return (
          <div className="admin-modal-backdrop" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setSelectedMemberId(null);
            }
          }}>
            <div className="admin-modal member-detail-modal" onClick={(e) => e.stopPropagation()}>
              <h3>成員資料</h3>
              <div className="member-detail-info">
                <div className="info-row">
                  <label>員編-姓名：</label>
                  <span>{formatMemberIdentity(member.identifier, member.display_name)}</span>
                </div>
                <div className="info-row">
                  <label>角色：</label>
                  <span>{roleLabel(member.role)}</span>
                </div>
                <div className="info-row">
                  <label>場次：</label>
                  <span>{member.campaign_id ? (campaignOptions.find((c) => c.id === member.campaign_id)?.label || member.campaign_id) : '未綁定場次'}</span>
                </div>
                <div className="info-row">
                  <label>會場：</label>
                  <span>{member.assigned_venue_id ? venues.find((v) => v.id === member.assigned_venue_id)?.name || member.assigned_venue_id : '尚未加入'}</span>
                </div>
                {isInvestmentMemberRole(member.role) && (
                <div className="info-row">
                  <label>投資送出狀態：</label>
                  <span>{member.is_voted ? '已鎖定送出' : '尚未送出'}</span>
                </div>
                )}
                {isInvestmentMemberRole(member.role) && (
                <div className="info-row">
                  <label>起始組別：</label>
                  <span>
                    {(venueProjectOrderCache[member.assigned_venue_id || ''] || []).find((project) => project.id === member.start_project_id)?.name
                      || (member.start_project_id || '未指定')}
                  </span>
                </div>
                )}
              </div>

              {isEditingMember && (
                <div className="member-edit-form">
                  <div className="form-group">
                    <label>姓名</label>
                    <input
                      type="text"
                      className="investment-input"
                      value={memberEditName[member.identifier] ?? member.display_name}
                      onChange={(e) => setMemberEditName((prev) => ({ ...prev, [member.identifier]: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>角色</label>
                    <select
                      className="investment-input"
                      value={memberEditRole[member.identifier] ?? member.role}
                      onChange={(e) => setMemberEditRole((prev) => ({ ...prev, [member.identifier]: e.target.value as Role }))}
                    >
                      <option value="judge">評審</option>
                      <option value="investor">投資客</option>
                      {isSuperAdmin(authUser?.role) && <option value="admin">工作人員</option>}
                    </select>
                  </div>
                  {isInvestmentMemberRole(memberEditRole[member.identifier] ?? member.role) && isSuperAdmin(authUser?.role) && (
                    <div className="form-group">
                      <label>綁定管理者（員編-姓名）</label>
                      <select
                        className="investment-input"
                        value={memberEditManager[member.identifier] ?? member.manager_identifier ?? ''}
                        onChange={(e) => setMemberEditManager((prev) => ({ ...prev, [member.identifier]: e.target.value }))}
                      >
                        <option value="">未指派</option>
                        {[...adminMembers]
                          .sort((a, b) => a.display_name.localeCompare(b.display_name, 'zh-Hant'))
                          .map((admin) => (
                            <option key={admin.identifier} value={admin.identifier}>
                              {formatMemberIdentity(admin.identifier, admin.display_name)}
                            </option>
                          ))}
                      </select>
                    </div>
                  )}
                    {isInvestmentMemberRole(memberEditRole[member.identifier] ?? member.role) && (
                      <div className="form-group">
                        <label>起始組別</label>
                        <select
                          className="investment-input"
                          value={memberEditStartProject[member.identifier] ?? member.start_project_id ?? ''}
                          onChange={(e) => setMemberEditStartProject((prev) => ({ ...prev, [member.identifier]: e.target.value }))}
                          disabled={!member.assigned_venue_id}
                        >
                          <option value="">{member.assigned_venue_id ? '未指定（使用巡迴第一組）' : '請先分配會場'}</option>
                          {(venueProjectOrderCache[member.assigned_venue_id || ''] || []).map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  {member.is_voted && (
                    <div className="form-note">
                      ⚠️ 此成員已鎖定投資。如要修改，需先解除鎖定。
                    </div>
                  )}
                </div>
              )}

              <div className="admin-modal-actions">
                {isEditingMember ? (
                  <>
                    <button className="modal-btn modal-btn-neutral" onClick={() => setIsEditingMember(false)}>取消</button>
                    <button
                      className="modal-btn modal-btn-success"
                      onClick={() => updateMember(member.identifier)}
                      disabled={updatingMemberId === member.identifier}
                    >
                      {updatingMemberId === member.identifier ? '儲存中...' : '儲存'}
                    </button>
                  </>
                ) : (
                  <>
                    <button className="modal-btn modal-btn-neutral" onClick={() => setSelectedMemberId(null)}>關閉</button>
                    {member.is_voted && (
                      <button
                        className="modal-btn modal-btn-warning"
                        onClick={() => unlockMember(member.identifier)}
                        disabled={unlockingMemberId === member.identifier}
                      >
                        {unlockingMemberId === member.identifier ? '解除中...' : '解除鎖定'}
                      </button>
                    )}
                    <button className="modal-btn modal-btn-info" onClick={() => setIsEditingMember(true)}>
                      修改個人資料
                    </button>
                    <button
                      className="modal-btn modal-btn-danger"
                      onClick={() => {
                        setPendingMemberDelete(member);
                        setSelectedMemberId(null);
                      }}
                      disabled={deletingMemberId === member.identifier}
                    >
                      {deletingMemberId === member.identifier ? '刪除中...' : '刪除'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {adminTab === 'venues' && !canManageVenues && (
        <section className="section judge-form venue-tutorial-simulated">
          <h3>會場管理（模擬畫面）</h3>
          <p style={{ marginBottom: 12 }}>
            目前場次尚未啟動，正式會場資料區會在啟動後出現。先用此模擬畫面熟悉「匯入、分派、會場卡片」的位置。
          </p>
          <div className="venue-sim-shell">
            <div className="venue-sim-import">
              <strong>一鍵匯入會場（Excel）</strong>
              <div className="venue-sim-import-row">
                <span>下載範本</span>
                <span>選擇檔案</span>
                <span>未選擇任何檔案</span>
              </div>
            </div>
            <div className="venue-sim-board">
              <div className="venue-sim-col">會議廳 A（範例）</div>
              <div className="venue-sim-col">會議廳 B（範例）</div>
              <div className="venue-sim-col">未分配評審（範例）</div>
            </div>
          </div>
        </section>
      )}

      {canManageVenues && (
        <>
          {adminTab === 'venues' && <section className="section judge-form" id="admin-venues-section" ref={adminVenuesSectionRef}>
            <h3>會場管理</h3>
            <p style={{ marginBottom: 12 }}>
              {isCampaignActive
                ? '啟動後才需要處理會場、教室與各會場評審鎖定狀態。可直接拖拉評審卡片到對應會議廳。'
                : '目前為 工作人員 預覽模式，可直接檢視會場管理介面與可拖拉區塊。'}
            </p>

            {isSuperAdmin(authUser?.role) && (
              <div className="form-group">
                <label>選擇場次</label>
                <div className="input-group">
                  <select
                    className="investment-input"
                    value={selectedMemberCampaignId}
                    onChange={(e) => setSelectedMemberCampaignId(e.target.value)}
                  >
                    {venueCampaignOptions.length === 0 && <option value="">尚無進行中場次</option>}
                    {venueCampaignOptions.map((campaign) => (
                      <option key={`venue-campaign-${campaign.id}`} value={campaign.id}>
                        {campaign.year} / {campaign.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="apply-campaign-button"
                    onClick={() => {
                      if (!selectedMemberCampaignId) return;
                      loadMembers(selectedMemberCampaignId, true);
                      loadVenues(selectedMemberCampaignId);
                    }}
                    disabled={!selectedMemberCampaignId || isLoadingMembers}
                  >
                    {isLoadingMembers ? '讀取中...' : '套用場次'}
                  </button>
                </div>
              </div>
            )}

            <div className="form-group">
              <label>一鍵匯入會場（Excel）</label>
              <div className="input-group venue-import-row">
                <button type="button" className="venue-template-button" onClick={downloadVenueTemplate}>
                  下載範本
                </button>
                <label
                  className={`venue-file-picker ${isBulkImporting || !selectedMemberCampaignId ? 'is-disabled' : ''}`}
                  aria-disabled={isBulkImporting || !selectedMemberCampaignId}
                >
                  <span className="venue-file-picker-trigger">
                    {isBulkImporting ? (
                      <span className="button-loading-content">
                        <span className="button-spinner" aria-hidden="true"></span>
                        匯入中...
                      </span>
                    ) : '選擇檔案'}
                  </span>
                  <span className="venue-file-picker-name">{isBulkImporting ? '正在解析與匯入資料，請稍候...' : venueImportFileLabel}</span>
                  <input
                    type="file"
                    className="venue-file-input"
                    accept=".xlsx,.xls"
                    disabled={isBulkImporting || !selectedMemberCampaignId}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      e.currentTarget.value = '';
                      if (!file) {
                        setVenueImportFileLabel('未選擇任何檔案');
                        return;
                      }
                      setVenueImportFileLabel(file.name);
                      await importVenueWorkbook(file);
                    }}
                  />
                </label>
              </div>
              <div className={`venue-import-status ${importStatus.tone}`} role="status" aria-live="polite">
                {importStatus.text}
              </div>
              <p className="admin-modal-note" style={{ marginTop: 8 }}>
                請使用「專題組」與「評審」兩個工作表，欄位分別為 classroom + project_name、classroom + judge_name。
              </p>
            </div>

            <div className="form-group">
              <label>尚未分派會場的評審（可拖拉到下方會議廳）</label>
              <div className="unassigned-drag-pool">
                {(venueJudgeGroups.__unassigned__ || []).map((judge) => (
                  <div
                    key={`pool-${judge.identifier}`}
                    className={`venue-judge-card ${assigningMemberId === judge.identifier ? 'loading' : ''}`}
                    draggable={assigningMemberId !== judge.identifier}
                    onDragStart={() => setDraggingJudgeId(judge.identifier)}
                    onDragEnd={() => setDraggingJudgeId(null)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="member-name">{formatMemberIdentity(judge.identifier, judge.display_name)}</div>
                    <div className="venue-judge-meta">
                      {assigningMemberId === judge.identifier ? (
                        <span className="venue-loading-inline">
                          <span className="venue-loading-spinner" aria-hidden="true"></span>
                          分派中，等待系統處理...
                        </span>
                      ) : '拖拉到任一會議廳以分派'}
                    </div>
                  </div>
                ))}
                {!(venueJudgeGroups.__unassigned__ || []).length && (
                  <div className="manager-empty-block">
                    <p className="manager-empty">目前沒有尚未分派會場的評審</p>
                  </div>
                )}
              </div>
            </div>

            <div className="venue-board">
              {[...venues]
                .sort((a, b) => (a.classroom || a.name).localeCompare((b.classroom || b.name), 'zh-Hant', { numeric: true, sensitivity: 'base' }))
                .map((venue) => (
                  <div
                    key={`board-${venue.id}`}
                    className="venue-col"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (!draggingJudgeId) return;
                      const judge = judgeMembers.find((member) => member.identifier === draggingJudgeId);
                      setDraggingJudgeId(null);
                      if (!judge) return;
                      assignJudgeToVenue(judge, venue.id);
                    }}
                  >
                    <div className="venue-col-header">
                      <strong>{venue.classroom || venue.name}</strong>
                      <span>{venueMemberStats[venue.id] || 0} 位評審</span>
                    </div>
                    <div className="venue-col-subtitle">{venue.projects.length} 個專題組</div>
                    <div className="venue-col-body">
                      {(venueJudgeGroups[venue.id] || []).map((judge) => (
                        <div
                          key={judge.identifier}
                          className={`venue-judge-card ${assigningMemberId === judge.identifier ? 'loading' : ''}`}
                          draggable={assigningMemberId !== judge.identifier}
                          onDragStart={() => setDraggingJudgeId(judge.identifier)}
                          onDragEnd={() => setDraggingJudgeId(null)}
                          onClick={() => {
                            setSelectedMemberId(judge.identifier);
                            setIsEditingMember(false);
                          }}
                          role="button"
                          tabIndex={0}
                        >
                          <div className="member-name">{formatMemberIdentity(judge.identifier, judge.display_name)}</div>
                          <div className="venue-judge-meta">
                            {assigningMemberId === judge.identifier ? (
                              <span className="venue-loading-inline">
                                <span className="venue-loading-spinner" aria-hidden="true"></span>
                                移動中，等待系統處理...
                              </span>
                            ) : (judge.is_voted ? '已鎖定' : '可移動')}
                          </div>
                        </div>
                      ))}
                      {!(venueJudgeGroups[venue.id] || []).length && (
                        <p className="manager-empty">拖拉評審到這裡</p>
                      )}
                    </div>
                  </div>
                ))}

              <div
                className="venue-col venue-unassigned-col"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (!draggingJudgeId) return;
                  const judge = judgeMembers.find((member) => member.identifier === draggingJudgeId);
                  setDraggingJudgeId(null);
                  if (!judge) return;
                  unassignJudgeFromVenue(judge);
                }}
              >
                <div className="venue-col-header">
                  <strong>未分配評審</strong>
                  <span>{(venueJudgeGroups.__unassigned__ || []).length} 位評審</span>
                </div>
                <div className="venue-col-subtitle">拖到這裡可取消會議廳指派</div>
                <div className="venue-col-body">
                  {(venueJudgeGroups.__unassigned__ || []).map((judge) => (
                    <div
                      key={judge.identifier}
                      className={`venue-judge-card ${assigningMemberId === judge.identifier ? 'loading' : ''}`}
                      draggable={assigningMemberId !== judge.identifier}
                      onDragStart={() => setDraggingJudgeId(judge.identifier)}
                      onDragEnd={() => setDraggingJudgeId(null)}
                      onClick={() => {
                        setSelectedMemberId(judge.identifier);
                        setIsEditingMember(false);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="member-name">{formatMemberIdentity(judge.identifier, judge.display_name)}</div>
                      <div className="venue-judge-meta">
                        {assigningMemberId === judge.identifier ? (
                          <span className="venue-loading-inline">
                            <span className="venue-loading-spinner" aria-hidden="true"></span>
                            更新中，等待系統處理...
                          </span>
                        ) : '尚未分配'}
                      </div>
                    </div>
                  ))}
                  {!(venueJudgeGroups.__unassigned__ || []).length && (
                    <p className="manager-empty">目前沒有未分配評審</p>
                  )}
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>新增會場</label>
              <div className="input-group">
                <input
                  className="investment-input"
                  value={newVenueName}
                  onChange={(e) => setNewVenueName(e.target.value)}
                  placeholder="例如：LM503"
                />
                <button onClick={createVenue} disabled={isCreatingVenue}>{isCreatingVenue ? '新增中...' : '新增'}</button>
              </div>
            </div>

            <div className="venue-grid">
              {[...venues]
                .sort((a, b) => (a.classroom || a.name).localeCompare((b.classroom || b.name), 'zh-Hant', { numeric: true, sensitivity: 'base' }))
                .map((venue) => (
                <div
                  key={venue.id}
                  className="venue-grid-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => openVenueView(venue.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openVenueView(venue.id);
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="venue-card-header">
                    <span className="venue-card-name">{venue.classroom || venue.name}</span>
                    <div className="venue-card-actions" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => openVenueSetup(venue.id)}>修改</button>
                      <button onClick={() => setPendingVenueDelete(venue)} disabled={deletingVenueId === venue.id}>
                        {deletingVenueId === venue.id ? '刪除中...' : '刪除'}
                      </button>
                    </div>
                  </div>
                  <div className="venue-project-tags">
                    {venue.projects.length === 0 ? (
                      <p className="venue-no-member">尚無專題</p>
                    ) : (
                      venue.projects.map((project) => (
                        <span key={project} className="venue-project-tag">{project}</span>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>}
        </>
      )}

      {viewVenueId && (() => {
        const viewVenue = venues.find((v) => v.id === viewVenueId);
        const venueJudges = members.filter((m) => isInvestmentMemberRole(m.role) && m.assigned_venue_id === viewVenueId);
        const totalInvestment = venueViewProjects.reduce((s, p) => s + p.total_investment, 0);
        return (
          <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
            <div className="admin-modal venue-view-modal">
              <div className="venue-view-title-row">
                <h3>{viewVenue?.classroom || viewVenue?.name || viewVenueId}</h3>
                <span className="venue-view-classroom">會場：{viewVenue?.classroom || '未設定'}</span>
              </div>

              <div className="form-group">
                <label>專題組別與投資戰況</label>
                {isLoadingVenueView ? (
                  <p className="admin-modal-note">載入中...</p>
                ) : venueViewProjects.length === 0 ? (
                  <p className="admin-modal-note">尚無專題組別資料</p>
                ) : (
                  <div className="venue-view-projects">
                    {venueViewProjects.map((project) => {
                      const pct = totalInvestment > 0 ? (project.total_investment / totalInvestment) * 100 : 0;
                      return (
                        <div key={project.id} className="venue-view-project-row">
                          <span className="venue-view-project-name">{project.name}</span>
                          <div className="venue-view-bar-wrap">
                            <div className="venue-view-bar" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="venue-view-project-amount">${project.total_investment.toLocaleString()}</span>
                        </div>
                      );
                    })}
                    <div className="venue-view-total-row">
                      <span>合計</span>
                      <span>${totalInvestment.toLocaleString()}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>投資成員狀態</label>
                <div className="venue-member-list">
                  {venueJudges.length === 0 ? (
                    <p className="venue-no-member">尚無投資成員</p>
                  ) : (
                    venueJudges.map((member) => (
                      <div key={member.identifier} className={`venue-member-badge ${member.is_voted ? 'locked' : 'editable'}`}>
                        <span className="venue-member-dot" />
                        <span className="venue-member-name">{formatMemberIdentity(member.identifier, member.display_name)}</span>
                        <span className="venue-member-status">{member.is_voted ? '已鎖定' : '可編輯'}</span>
                        {member.is_voted && (
                          <div className="venue-member-btns">
                            <button
                              onClick={() => unlockMember(member.identifier)}
                              disabled={unlockingMemberId === member.identifier}
                            >
                              {unlockingMemberId === member.identifier ? '...' : '解除鎖定'}
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="admin-modal-actions">
                <button onClick={() => setViewVenueId(null)}>關閉</button>
                <button
                  className="submit-button"
                  onClick={() => {
                    const targetId = viewVenueId;
                    setViewVenueId(null);
                    openVenueSetup(targetId);
                  }}
                >
                  編輯設定
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {isVenueSetupOpen && setupVenue && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal">
            <h3>修改會場</h3>
            <p className="admin-modal-note">完成這一步後，評審登入會自動帶入指定會場，不需自己手動加入。</p>

            <div className="form-group">
              <label>教室名稱</label>
              <input
                className="investment-input"
                value={setupVenueNameDraft}
                onChange={(e) => setSetupVenueNameDraft(e.target.value)}
                placeholder="例如：LM503"
              />
            </div>

            <div className="form-group">
              <label>專題組（每行一筆）</label>
              <textarea
                className="investment-input admin-modal-textarea"
                value={setupProjectDraft}
                onChange={(e) => setSetupProjectDraft(e.target.value)}
                placeholder={'例如：\n智慧製造組\n互動媒體組\n資料科學組'}
              />
              <div className="input-group" style={{ marginTop: 10 }}>
                <p className="admin-modal-note" style={{ margin: 0 }}>
                  建議改用「會場管理」頁面的「一鍵匯入會場（Excel）」完成批次建立。
                </p>
              </div>
            </div>

            <div className="form-group">
              <label>從今年度成員加入評審</label>
              <select
                className="investment-input"
                value=""
                onChange={(e) => {
                  const id = e.target.value;
                  if (!id) return;
                  setSetupSelectedMemberIds((prev) => Array.from(new Set([...prev, id])));
                }}
              >
                <option value="">- 點選選擇評審加入 -</option>
                {judgeMembers.filter((m) => !m.assigned_venue_id).length > 0 && (
                  <optgroup label="尚未指定會場">
                    {judgeMembers
                      .filter((m) => !m.assigned_venue_id)
                      .map((m) => (
                        <option key={m.identifier} value={m.identifier}>
                          {formatMemberIdentity(m.identifier, m.display_name)}{setupSelectedMemberIds.includes(m.identifier) ? ' ✓' : ''}
                        </option>
                      ))}
                  </optgroup>
                )}
                {judgeMembers.filter((m) => m.assigned_venue_id && m.assigned_venue_id !== setupVenueId).length > 0 && (
                  <optgroup label="已指定其他會場">
                    {judgeMembers
                      .filter((m) => m.assigned_venue_id && m.assigned_venue_id !== setupVenueId)
                      .map((m) => (
                        <option key={m.identifier} value={m.identifier}>
                          {formatMemberIdentity(m.identifier, m.display_name)}（目前：{m.assigned_venue_id}）{setupSelectedMemberIds.includes(m.identifier) ? ' ✓' : ''}
                        </option>
                      ))}
                  </optgroup>
                )}
                {judgeMembers.filter((m) => m.assigned_venue_id === setupVenueId).length > 0 && (
                  <optgroup label="已在此會場">
                    {judgeMembers
                      .filter((m) => m.assigned_venue_id === setupVenueId)
                      .map((m) => (
                        <option key={m.identifier} value={m.identifier}>
                          {formatMemberIdentity(m.identifier, m.display_name)}（此會場）{setupSelectedMemberIds.includes(m.identifier) ? ' ✓' : ''}
                        </option>
                      ))}
                  </optgroup>
                )}
                {judgeMembers.length === 0 && <option disabled value="">目前沒有可選成員</option>}
              </select>

              <div className="setup-selected-chips">
                {setupSelectedMemberIds.length === 0 ? (
                  <p className="admin-modal-note" style={{ margin: 0, fontSize: 12 }}>尚未選擇任何評審</p>
                ) : (
                  setupSelectedMemberIds.map((id) => {
                    const m = judgeMembers.find((mem) => mem.identifier === id);
                    return (
                      <span key={id} className="setup-member-chip">
                        {m ? formatMemberIdentity(m.identifier, m.display_name) : id}
                        <button
                          type="button"
                          onClick={() => setSetupSelectedMemberIds((prev) => prev.filter((x) => x !== id))}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })
                )}
              </div>
            </div>

            <div className="form-group">
              <label>新增成員並直接加入這個會場（每行一位姓名）</label>
              <textarea
                className="investment-input admin-modal-textarea"
                value={setupNewMemberDraft}
                onChange={(e) => setSetupNewMemberDraft(e.target.value)}
                placeholder={'例如：\n王小明\n陳評審'}
              />
            </div>

            <div className="admin-modal-actions">
              <button
                onClick={() => {
                  setIsVenueSetupOpen(false);
                  setSetupVenueId('');
                  setSetupVenueNameDraft('');
                  setSetupProjectDraft('');
                  setSetupSelectedMemberIds([]);
                  setSetupNewMemberDraft('');
                }}
                disabled={isSavingVenueSetup}
              >
                稍後再設
              </button>
              <button className="submit-button" onClick={saveVenueSetup} disabled={isSavingVenueSetup}>
                {isSavingVenueSetup ? '儲存中...' : '完成會場設定'}
              </button>
            </div>
          </div>
        </div>
      )}

      {authUser?.must_complete_profile && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>首次登入設定</h3>
            <p className="admin-modal-note">請先完成帳號、密碼、姓名與系所設定，完成前無法繼續操作。</p>
            <div className="form-group">
              <label>新帳號</label>
              <input
                className="investment-input"
                value={firstLoginSetupUsername}
                onChange={(e) => setFirstLoginSetupUsername(e.target.value)}
                placeholder="4-32 位英數字"
              />
            </div>
            <div className="form-group">
              <label>新密碼</label>
              <div className="password-input-wrapper">
                <input
                  className="investment-input"
                  type={isShowFirstLoginPassword ? 'text' : 'password'}
                  value={firstLoginSetupPassword}
                  onChange={(e) => setFirstLoginSetupPassword(e.target.value)}
                  placeholder="至少 8 碼"
                />
                <button
                  type="button"
                  className="password-eye-btn"
                  onClick={() => setIsShowFirstLoginPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={isShowFirstLoginPassword ? '隱藏密碼' : '顯示密碼'}
                >
                  {isShowFirstLoginPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>確認密碼</label>
              <div className="password-input-wrapper">
                <input
                  className="investment-input"
                  type={isShowFirstLoginConfirmPassword ? 'text' : 'password'}
                  value={firstLoginSetupConfirmPassword}
                  onChange={(e) => setFirstLoginSetupConfirmPassword(e.target.value)}
                  placeholder="再次輸入密碼"
                />
                <button
                  type="button"
                  className="password-eye-btn"
                  onClick={() => setIsShowFirstLoginConfirmPassword(v => !v)}
                  tabIndex={-1}
                  aria-label={isShowFirstLoginConfirmPassword ? '隱藏密碼' : '顯示密碼'}
                >
                  {isShowFirstLoginConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label>姓名（選填）</label>
              <input
                className="investment-input"
                value={firstLoginSetupDisplayName}
                onChange={(e) => setFirstLoginSetupDisplayName(e.target.value)}
                placeholder="選填，留空則沿用目前名稱"
              />
            </div>
            <div className="form-group">
              <label>系所（選填）</label>
              <input
                className="investment-input"
                value={firstLoginSetupDepartment}
                onChange={(e) => setFirstLoginSetupDepartment(e.target.value)}
                placeholder="選填，留空則跳過系所設定"
              />
            </div>
            <div className="admin-modal-actions">
              <button className="submit-button first-login-save-button" onClick={submitFirstLoginSetup} disabled={isSavingFirstLoginSetup}>
                {isSavingFirstLoginSetup ? '儲存中...' : '儲存並繼續'}
              </button>
            </div>
          </div>
        </div>
      )}

      {authUser && canEditOwnProfile && isProfileModalOpen && !authUser.must_complete_profile && (
        <div
          className="admin-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="編輯個人資料"
          onMouseDown={(e) => {
            profileBackdropPressStartedRef.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            const isStrictBackdropClick = profileBackdropPressStartedRef.current && e.target === e.currentTarget;
            profileBackdropPressStartedRef.current = false;
            if (isStrictBackdropClick && !isSavingProfile) {
              setIsProfileModalOpen(false);
            }
          }}
        >
          <div
            className="admin-modal admin-confirm-modal"
            onMouseDown={() => {
              profileBackdropPressStartedRef.current = false;
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>編輯個人資料</h3>
            <p className="admin-modal-note">可修改你的系所與顯示名稱，更新後會立即套用在右上角歡迎區塊。</p>
            <div className="profile-modal-form">
              <div className="form-group">
                <label>名字</label>
                <input
                  className="investment-input"
                  value={profileDisplayNameDraft}
                  onChange={(e) => setProfileDisplayNameDraft(e.target.value)}
                  placeholder="請輸入名字"
                  disabled={isSavingProfile}
                />
              </div>
              <div className="form-group">
                <label>系所</label>
                <input
                  className="investment-input"
                  value={profileDepartmentDraft}
                  onChange={(e) => setProfileDepartmentDraft(e.target.value)}
                  placeholder="例如：商管系"
                  disabled={isSavingProfile}
                />
              </div>
            </div>
            <div className="admin-modal-actions">
              <button onClick={() => setIsProfileModalOpen(false)} disabled={isSavingProfile}>取消</button>
              <button className="submit-button profile-save-button" onClick={submitProfileUpdate} disabled={isSavingProfile}>
                {isSavingProfile ? '儲存中...' : '儲存變更'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingLockAllCampaignId && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>確認全員鎖定？</h3>
            <p className="admin-modal-note">
              即將鎖定場次中所有評審與投資者的評分與投資，鎖定後成員將無法再修改。
              如需解鎖，可至成員管理個別操作。
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => setPendingLockAllCampaignId(null)}
                disabled={lockingAllCampaignId === pendingLockAllCampaignId}
              >
                取消
              </button>
              <button
                className="submit-button admin-lock-all-btn"
                onClick={async () => {
                  const id = pendingLockAllCampaignId;
                  setPendingLockAllCampaignId(null);
                  await lockAllMembers(id);
                }}
                disabled={lockingAllCampaignId === pendingLockAllCampaignId}
              >
                {lockingAllCampaignId === pendingLockAllCampaignId ? (
                  <span className="button-loading-content">
                    <span className="button-spinner" aria-hidden="true"></span>
                    鎖定中...
                  </span>
                ) : '確認全員鎖定'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingResetRound && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>確認重置回合？</h3>
            <p className="admin-modal-note">
              此操作將清除目前場次所有評審的評分與投資記錄，並重置所有成員的投票狀態。<br />
              <strong>此操作無法復原。</strong>
            </p>
            <div className="admin-modal-actions">
              <button onClick={() => setPendingResetRound(false)} disabled={isResettingRound}>
                取消
              </button>
              <button
                className="submit-button admin-reset-round-btn"
                onClick={async () => {
                  setPendingResetRound(false);
                  await resetRound();
                }}
                disabled={isResettingRound}
              >
                確認重置
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingTempPasswordReset && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal temp-admin-reset-confirm-modal">
            <h3>確認重新生成密碼？</h3>
            <p className="admin-modal-note">
              你即將重新生成「{pendingTempPasswordReset.username}」的登入密碼。
              生成後舊密碼會立即失效，請確認後再執行。
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => setPendingTempPasswordReset(null)}
                disabled={resettingTempAdminId === pendingTempPasswordReset.identifier}
              >
                取消
              </button>
              <button
                className="submit-button temp-admin-reset-confirm-btn"
                onClick={async () => {
                  const id = pendingTempPasswordReset.identifier;
                  setPendingTempPasswordReset(null);
                  await resetTempSuperAdminPassword(id);
                }}
                disabled={resettingTempAdminId === pendingTempPasswordReset.identifier}
              >
                {resettingTempAdminId === pendingTempPasswordReset.identifier ? '生成中...' : '確認重新生成密碼'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingPermanentDelete && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>確認永久刪除？</h3>
            <p className="admin-modal-note">
              你即將永久刪除「{pendingPermanentDelete.label}」。
              此操作無法復原，紀錄不會進入最近刪除。
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => setPendingPermanentDelete(null)}
                disabled={permanentDeletingCampaignId === pendingPermanentDelete.id}
              >
                取消
              </button>
              <button
                className="submit-button"
                style={{ background: '#dc2626' }}
                onClick={() => permanentDeleteRecentlyCampaign(pendingPermanentDelete)}
                disabled={permanentDeletingCampaignId === pendingPermanentDelete.id}
              >
                {permanentDeletingCampaignId === pendingPermanentDelete.id ? '刪除中...' : '確認永久刪除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingCloseCampaign && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>確認關閉並封存？</h3>
            <p className="admin-modal-note">
              你即將關閉並封存「{pendingCloseCampaign.label}」。
              封存後本場次會從進行中列表移除，請再次確認。
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => setPendingCloseCampaign(null)}
                disabled={closingCampaignId === pendingCloseCampaign.id}
              >
                取消
              </button>
              <button
                className="submit-button"
                onClick={async () => {
                  const targetCampaignId = pendingCloseCampaign.id;
                  setPendingCloseCampaign(null);
                  await closeCampaign(targetCampaignId);
                }}
                disabled={closingCampaignId === pendingCloseCampaign.id}
              >
                {closingCampaignId === pendingCloseCampaign.id ? '封存中...' : '確認封存'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingMemberDelete && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>確認刪除成員？</h3>
            <p className="admin-modal-note">
              你即將刪除「{formatMemberIdentity(pendingMemberDelete.identifier, pendingMemberDelete.display_name)}」。
              刪除後將無法復原，請再次確認。
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => setPendingMemberDelete(null)}
                disabled={deletingMemberId === pendingMemberDelete.identifier}
              >
                取消
              </button>
              <button
                className="submit-button"
                style={{ background: '#dc2626' }}
                onClick={() => deleteMember(pendingMemberDelete.identifier)}
                disabled={deletingMemberId === pendingMemberDelete.identifier}
              >
                {deletingMemberId === pendingMemberDelete.identifier ? '刪除中...' : '確認刪除'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingProgressUnlock && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setPendingProgressUnlock(null); }}>
          <div className="admin-modal admin-confirm-modal">
            <h3>確認解除鎖定？</h3>
            <p className="admin-modal-note">
              你即將解除「{pendingProgressUnlock.displayName}」的鎖定狀態，解鎖後該{pendingProgressUnlock.role === 'judge' ? '評審' : '投資客'}可再次修改並重新上傳。
            </p>
            <div className="admin-modal-actions">
              <button onClick={() => setPendingProgressUnlock(null)} disabled={unlockingMemberId === pendingProgressUnlock.identifier}>
                取消
              </button>
              <button
                className="submit-button"
                onClick={async () => {
                  const id = pendingProgressUnlock.identifier;
                  setPendingProgressUnlock(null);
                  await unlockMember(id);
                }}
                disabled={unlockingMemberId === pendingProgressUnlock.identifier}
              >
                {unlockingMemberId === pendingProgressUnlock.identifier ? '解鎖中...' : '確認解除鎖定'}
              </button>
            </div>
          </div>
        </div>
      )}

      {progressDetailModal && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true" onClick={(e) => { if (e.target === e.currentTarget) setProgressDetailModal(null); }}>
          <div className="admin-modal admin-progress-detail-modal">
            <div className="admin-progress-detail-head">
              <h3>{progressDetailModal.displayName} 的{progressDetailModal.role === 'judge' ? '評分投資' : '投資'}狀況</h3>
              <div className="admin-progress-detail-actions">
                <button
                  type="button"
                  className="admin-progress-detail-refresh"
                  onClick={() => void refreshProgressDetail()}
                  disabled={isLoadingProgressDetail}
                >
                  <span className={`admin-refresh-rotate${isLoadingProgressDetail ? ' spinning' : ''}`} aria-hidden="true">↻</span>
                  <span>{isLoadingProgressDetail ? '刷新中...' : '刷新'}</span>
                </button>
                <button
                  type="button"
                  className="admin-progress-detail-close"
                  onClick={() => setProgressDetailModal(null)}
                  aria-label="關閉"
                >✕</button>
              </div>
            </div>
            {isLoadingProgressDetail ? (
              <p className="admin-progress-empty">載入中...</p>
            ) : progressDetailProjects.length === 0 ? (
              <p className="admin-progress-empty">尚無專題資料（可能尚未分配會場）。</p>
            ) : (
              <>
                <div className="admin-progress-detail-list">
                  {progressDetailProjects.map((p) => {
                    const hasInvest = p.invested > 0;
                    const hasScore = progressDetailModal.role === 'judge' ? p.score > 0 : true;
                    const isJudge = progressDetailModal.role === 'judge';
                    const statusClass = hasInvest && hasScore ? 'done' : isJudge && hasScore && !hasInvest ? 'partial' : 'missing';
                    return (
                      <div key={p.id} className={`admin-progress-detail-item ${statusClass}`}>
                        <span className="admin-progress-detail-name">{p.name}</span>
                        <span className="admin-progress-detail-tags">
                          {isJudge && (
                            <span className={`admin-progress-detail-tag ${p.score > 0 ? 'tag-done' : 'tag-missing'}`}>
                              評分：{p.score > 0 ? p.score : '未評'}
                            </span>
                          )}
                          <span className={`admin-progress-detail-tag ${p.invested > 0 ? 'tag-done' : 'tag-missing'}`}>
                            投資：{p.invested > 0 ? p.invested.toLocaleString() : '未投'}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {selectedHistoryCampaign && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-history-detail-modal">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <h3 style={{ margin: 0 }}>{selectedHistoryCampaign.label}｜封存戰況</h3>
              <button
                onClick={() => setSelectedHistoryCampaign(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px', color: '#6b7280' }}
                aria-label="關閉"
              >✕</button>
            </div>
            <p className="admin-modal-note">
              場次年度：{selectedHistoryCampaign.year} /
              封存時間：{selectedHistoryCampaign.closed_at ? new Date(selectedHistoryCampaign.closed_at).toLocaleString('zh-Hant-TW') : '未記錄'}
            </p>

            {selectedHistoryCampaign.summary?.venues && selectedHistoryCampaign.summary.venues.length > 0 ? (
              <>
                <div className="history-overall-section">
                  <h4>全部組別評分總排名</h4>
                  {selectedHistoryCampaign.summary?.overall_score_ranking && selectedHistoryCampaign.summary.overall_score_ranking.length > 0 ? (
                    <div className="history-overall-list">
                      {selectedHistoryCampaign.summary.overall_score_ranking.map((project, index) => (
                        <div key={`score-${project.venue_id}-${project.project_id}-${index}`} className="history-overall-row">
                          <span>第 {project.score_rank ?? index + 1} 名</span>
                          <span>{project.project_name}</span>
                          <span>{project.venue_name || '未指定'}</span>
                          <span>
                            {Number(project.total_score ?? 0).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                            {' '}分（均分 {Number(project.avg_score ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}）
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="admin-modal-note">此封存紀錄尚未包含全部組別評分總排名。</p>
                  )}
                </div>

                <div className="history-overall-section">
                  <h4>全部組別投資金總排名</h4>
                  {selectedHistoryCampaign.summary?.overall_project_ranking && selectedHistoryCampaign.summary.overall_project_ranking.length > 0 ? (
                    <div className="history-overall-list">
                      {selectedHistoryCampaign.summary.overall_project_ranking.map((project, index) => (
                        <div key={`${project.venue_id}-${project.project_id}-${index}`} className="history-overall-row">
                          <span>第 {project.rank ?? index + 1} 名</span>
                          <span>{project.project_name}</span>
                          <span>{project.venue_name || '未指定'}</span>
                          <span>${project.total_investment.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="admin-modal-note">此封存紀錄尚未包含全部組別總排名（可用新封存場次查看）。</p>
                  )}
                </div>

                <div className="history-venue-list">
                  {selectedHistoryCampaign.summary.venues
                    .slice()
                    .sort((a, b) => b.total_investment - a.total_investment)
                    .map((venue) => (
                      <div key={venue.venue_id} className="history-venue-card">
                        <div className="history-venue-header">
                          <strong>{venue.venue_name}</strong>
                          <span>${venue.total_investment.toLocaleString()}</span>
                        </div>
                        <p className="admin-modal-note" style={{ marginTop: 6 }}>
                          {(() => {
                            const judgeLocked = venue.judge_locked_count ?? venue.locked_count ?? 0;
                            const judgeTotal = venue.judge_member_count ?? venue.judge_count ?? 0;
                            const investorLocked = venue.investor_locked_count ?? 0;
                            const investorTotal = venue.investor_member_count ?? 0;
                            return `評審鎖定：${judgeLocked}/${judgeTotal}，投資客鎖定：${investorLocked}/${investorTotal}`;
                          })()}
                        </p>
                        {venue.projects && venue.projects.length > 0 ? (
                          <div className="history-project-list">
                            {venue.projects.map((project, index) => (
                              <div key={project.project_id} className="history-project-row">
                                <span>第 {project.rank ?? index + 1} 名</span>
                                <span>{project.project_name}</span>
                                <span>${project.total_investment.toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="admin-modal-note" style={{ marginTop: 8 }}>此封存紀錄未包含專題明細。</p>
                        )}
                      </div>
                    ))}
                </div>
              </>
            ) : (
              <p className="admin-modal-note">此封存紀錄僅有總額，尚無會場/專題戰況細節。</p>
            )}

          </div>
        </div>
      )}

      {qrInviteCampaign && (() => {
        const qrInviteUrl = qrInviteCampaign.invite_token
          ? `${window.location.origin}${window.location.pathname}?c=${encodeURIComponent(qrInviteCampaign.invite_token)}`
          : '';
        if (!qrInviteUrl) {
          return null;
        }
        return (
          <div className="admin-modal-backdrop" role="dialog" aria-modal="true" onClick={() => setQrInviteCampaign(null)}>
            <div className="admin-modal admin-qr-modal" onClick={(e) => e.stopPropagation()}>
              <h3>邀請 QRCode</h3>
              <p className="admin-modal-note">
                場次：{qrInviteCampaign.year} / {qrInviteCampaign.label}
              </p>
              <div className="admin-qr-wrap">
                <QRCodeCanvas value={qrInviteUrl} size={280} includeMargin level="M" />
              </div>
              <div className="admin-qr-url">{qrInviteUrl}</div>
              <div className="admin-modal-actions">
                <button onClick={() => setQrInviteCampaign(null)}>關閉</button>
                <button className="submit-button" onClick={() => window.print()}>列印 QRCode</button>
              </div>
            </div>
          </div>
        );
      })()}

      {pendingMoveJudge && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>{pendingMoveJudge.managerIdentifier ? '確認移動評審' : '確認移出群組'}</h3>
            <p className="admin-modal-note">
              {pendingMoveJudge.managerIdentifier
                ? <>將「{pendingMoveJudge.judge.display_name}」移至「{pendingMoveJudge.managerName}」的群組？</>
                : <>將「{pendingMoveJudge.judge.display_name}」從「{pendingMoveJudge.managerName}」群組移出？</>}
            </p>
            <div className="admin-modal-actions">
              <button onClick={() => setPendingMoveJudge(null)}>取消</button>
              <button
                className="submit-button"
                style={pendingMoveJudge.managerIdentifier
                  ? { background: 'linear-gradient(135deg, #22c55e, #16a34a)' }
                  : { background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
                onClick={() => {
                  const { judge, managerIdentifier } = pendingMoveJudge;
                  setPendingMoveJudge(null);
                  executeMoveJudge(judge, managerIdentifier);
                }}
              >
                確認
              </button>
            </div>
          </div>
        </div>
      )}

      {loadingMoveJudge && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal move-loading-modal">
            <span className="move-confirm-spinner move-confirm-spinner-lg" />
            <p className="move-loading-text">正在移動「{loadingMoveJudge.display_name}」...</p>
          </div>
        </div>
      )}

      {pendingVenueDelete && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>確認刪除會場？</h3>
            <p className="admin-modal-note">
              你即將刪除「{pendingVenueDelete.classroom || pendingVenueDelete.name}」。
              此操作會自動將該會場的評審移出並解除鎖定。
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => setPendingVenueDelete(null)}
                disabled={deletingVenueId === pendingVenueDelete.id}
              >
                取消
              </button>
              <button
                className="submit-button"
                style={{ background: '#dc2626' }}
                onClick={() => deleteVenue(pendingVenueDelete.id)}
                disabled={deletingVenueId === pendingVenueDelete.id}
              >
                {deletingVenueId === pendingVenueDelete.id ? '刪除中...' : '確認刪除會場'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingArchiveDelete && (
        <div className="admin-modal-backdrop" role="dialog" aria-modal="true">
          <div className="admin-modal admin-confirm-modal">
            <h3>確認刪除封存紀錄？</h3>
            <p className="admin-modal-note">
              你即將刪除「{pendingArchiveDelete.label}」。
              紀錄會先移到最近刪除，30 天內仍可還原。
            </p>
            <div className="admin-modal-actions">
              <button
                onClick={() => setPendingArchiveDelete(null)}
                disabled={isDeletingArchive}
              >
                取消
              </button>
              <button
                className="submit-button"
                onClick={() => deleteArchivedCampaign(pendingArchiveDelete)}
                disabled={isDeletingArchive}
              >
                {isDeletingArchive ? '刪除中...' : '確認刪除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | undefined;
    const check = async () => {
      try {
        await axios.get(`${API_BASE_URL}/`, { timeout: 10000 });
        if (!cancelled) setBackendStatus('ready');
      } catch {
        if (!cancelled) {
          setBackendStatus('error');
          retryTimer = window.setTimeout(() => {
            if (cancelled) return;
            setBackendStatus('checking');
            void check();
          }, 2500);
        }
      }
    };
    void check();
    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasVenue = Boolean(authUser?.venue_id);
  const effectiveVenueId = authUser?.venue_id || selectedVenueId || dashboardVenueId || venues[0]?.id || '';
  const canSeeDashboard = (isCampaignActive || isSuperAdminUser) && Boolean(dashboardVenueId || venues[0]?.id);
  const joinedVenueId = judgeStatus?.assigned_venue_id || authUser?.venue_id || null;
  const canAccessJudgeScreens = !isAdminRole(authUser?.role);
  const showLoginLoadingModal = !authUser && backendStatus !== 'ready';
  const canEditOwnProfile = isAdminRole(authUser?.role);

  return (
    <div className="app-container">
      <div className="navigation">
        <div className="navigation-top-row">
          <h1>
            FundThePitch
            <br />
            專題模擬投資評分系統
          </h1>
          {authUser && (
            <div className="navigation-meta">
              {canEditOwnProfile ? (
                <button
                  className="navigation-welcome welcome-edit-trigger"
                  type="button"
                  onClick={openProfileModal}
                  title="編輯個人資料"
                  aria-label="編輯個人資料"
                >
                  <span className="welcome-label">歡迎</span>
                  <strong className="welcome-name">{authUser.display_name}</strong>
                  <span className="welcome-role">{roleLabel(authUser.role)}</span>
                  <span className="welcome-edit-hint">編輯</span>
                </button>
              ) : (
                <div className="navigation-welcome" role="status" aria-live="polite">
                  <span className="welcome-label">歡迎</span>
                  <strong className="welcome-name">{authUser.display_name}</strong>
                  <span className="welcome-role">{roleLabel(authUser.role)}</span>
                </div>
              )}
              {isAdminRole(authUser.role) ? (
                <button
                  className="nav-toggle"
                  type="button"
                  onClick={() => setIsMobileNavOpen((prev) => !prev)}
                  aria-expanded={isMobileNavOpen}
                  aria-label={isMobileNavOpen ? '收合選單' : '展開選單'}
                  title={isMobileNavOpen ? '收合選單' : '展開選單'}
                >
                  <span className="nav-toggle-icon" aria-hidden="true">{isMobileNavOpen ? '✕' : '☰'}</span>
                </button>
              ) : (
                <button 
                  className="nav-toggle nav-logout-btn" 
                  type="button" 
                  onClick={logout} 
                  disabled={isLoggingOut}
                  title="登出"
                  aria-label="登出"
                >
                  {isLoggingOut ? '登出中...' : '登出'}
                </button>
              )}
            </div>
          )}
        </div>
        {authUser && isAdminRole(authUser.role) && (
          <div className={`top-navbar-shell ${isMobileNavOpen ? 'open' : ''}`}>
            <div className={`top-navbar-pill ${!isAdminRole(authUser.role) ? 'judge-nav-grid' : ''}`}>
              {isAdminRole(authUser.role) && (
                <button
                  onClick={() => {
                    setCurrentView('admin');
                    setIsMobileNavOpen(false);
                  }}
                  className={`top-navbar-link ${currentView === 'admin' ? 'active' : ''}`}
                >
                  後台管理
                </button>
              )}

              {canAccessJudgeScreens && (
                <button
                  disabled={disableTopCampaignTabs || (!hasVenue && !isSuperAdminUser)}
                  onClick={() => {
                    setCurrentView('judge');
                    setIsMobileNavOpen(false);
                  }}
                  className={`top-navbar-link ${currentView === 'judge' ? 'active' : ''}`}
                >
                  評審投資
                </button>
              )}
              {isAdminRole(authUser.role) && (
                <>
                  <button
                    disabled={disableTopCampaignTabs}
                    onClick={() => {
                      setCurrentView('dashboard');
                      setIsMobileNavOpen(false);
                    }}
                    className={`top-navbar-link ${currentView === 'dashboard' ? 'active' : ''}`}
                  >
                    會場投資戰況
                  </button>
                  <button
                    disabled={disableTopCampaignTabs}
                    onClick={() => {
                      setCurrentView('score-ranking');
                      setIsMobileNavOpen(false);
                    }}
                    className={`top-navbar-link ${currentView === 'score-ranking' ? 'active' : ''}`}
                  >
                    最終評分排名
                  </button>
                </>
              )}
              <button className="top-navbar-link top-navbar-logout" onClick={logout} disabled={isLoggingOut}>{isLoggingOut ? '登出中...' : '登出'}</button>
            </div>
          </div>
        )}
      </div>

      {message && (
        <div className="system-toast" role="status" aria-live="polite">
          {message}
        </div>
      )}

      {error && (
        <div className="error-toast" role="alert" aria-live="assertive">
          {error}
        </div>
      )}

      <div className="content">
        {!authUser && renderLogin()}

        {showLoginLoadingModal && (
          <div className="backend-health-backdrop" role="status" aria-live="polite" aria-busy="true">
            <div className="backend-health-modal">
              <div className="backend-health-spinner" aria-hidden="true"></div>
              <h3>{backendStatus === 'error' ? '正在重新連線' : '系統啟動中'}</h3>
              <p>
                {backendStatus === 'error'
                  ? '後端服務暫時不可用，系統正在自動重試。'
                  : '正在確認後端連線，請稍候片刻。'}
              </p>
            </div>
          </div>
        )}

        {authUser && currentView === 'judge' && authToken && ((hasVenue && isCampaignActive) || (isSuperAdminUser && Boolean(effectiveVenueId))) && (
          <div>
            <JudgeUI
              authToken={authToken}
              authUser={authUser}
              venueId={effectiveVenueId}
              venueName={venues.find((v) => v.id === effectiveVenueId)?.name}
              isLocked={isSuperAdminUser ? true : Boolean(judgeStatus?.is_voted)}
              guideTitle={judgeStatus?.judge_panel_note_title || ''}
              guideContent={judgeStatus?.judge_panel_note_content || ''}
              guideFormat={judgeStatus?.judge_panel_note_format === 'markdown' ? 'markdown' : 'text'}
              onSubmitted={refreshJudgeStatus}
            />
          </div>
        )}

        {authUser && currentView === 'judge' && !isCampaignActive && !isSuperAdminUser && (
          <div className="container" style={{ maxWidth: 920 }}>
            <section className="section">
              <h3>評審投資</h3>
              <p>目前尚未啟動專題會，暫無投資與會場資料。</p>
            </section>
          </div>
        )}

        {authUser && currentView === 'judge' && isSuperAdminUser && !effectiveVenueId && (
          <div className="container" style={{ maxWidth: 920 }}>
            <section className="section">
              <h3>評審投資（super_admin 預覽）</h3>
              <p>目前尚無可預覽的會場，請先新增會場後即可檢視評審介面。</p>
            </section>
          </div>
        )}

        {authUser && isAdminRole(authUser.role) && currentView === 'dashboard' && canSeeDashboard && (
          <div className="dashboard-page-flow">
            {!isPresentationMode && venues.length > 1 && (
              <div className="container dashboard-venue-picker-shell" style={{ width: 'min(95vw, 1440px)' }}>
                <section className="section">
                  <h3>跨會場投資戰況</h3>
                  <p style={{ marginBottom: 12 }}>所有人都可查看所有會場，即時切換觀察投資走勢。</p>
                  <div className="lobby-grid dashboard-venue-grid">
                    {venues.map((venue) => {
                      const isOwnVenue = joinedVenueId === venue.id;
                      return (
                        <button
                          key={venue.id}
                          type="button"
                          className={`lobby-card dashboard-venue-card ${dashboardVenueId === venue.id ? 'active' : ''} ${isOwnVenue ? 'my-venue' : ''}`}
                          onClick={() => setDashboardVenueId(venue.id)}
                        >
                          <h3>{venue.name}</h3>
                          <p>會場：{venue.classroom}</p>
                          <p>評審數：{venue.judges.length}</p>
                          <p>專題數：{venue.projects.length}</p>
                          {isOwnVenue && <p className="my-venue-label">我的會場</p>}
                        </button>
                      );
                    })}
                  </div>
                </section>
              </div>
            )}
            <Dashboard 
              venueId={dashboardVenueId}
              isPresentationMode={isPresentationMode}
              onPresentationModeChange={setIsPresentationMode}
              isAdmin={isAdminRole(authUser?.role)}
            />
          </div>
        )}

        {authUser && isAdminRole(authUser.role) && currentView === 'dashboard' && !canSeeDashboard && (
          <div className="container" style={{ width: 'min(95vw, 1440px)' }}>
            <section className="section">
              <h3>跨會場投資戰況</h3>
              <p>目前尚未建立可查看的會場，請先由工作人員新增會場。</p>
            </section>
          </div>
        )}

        {authUser && isAdminRole(authUser.role) && currentView === 'score-ranking' && canSeeDashboard && (
          <div className="dashboard-page-flow">
            {!isScorePresentationMode && venues.length > 1 && (
              <div className="container dashboard-venue-picker-shell" style={{ width: 'min(95vw, 1440px)' }}>
                <section className="section">
                  <h3>跨會場最終評分排名</h3>
                  <p style={{ marginBottom: 12 }}>所有人都可查看各會場的最終評分排名，隨時切換。</p>
                  <div className="lobby-grid dashboard-venue-grid">
                    {venues.map((venue) => {
                      const isOwnVenue = joinedVenueId === venue.id;
                      return (
                        <button
                          key={venue.id}
                          type="button"
                          className={`lobby-card dashboard-venue-card ${dashboardVenueId === venue.id ? 'active' : ''} ${isOwnVenue ? 'my-venue' : ''}`}
                          onClick={() => setDashboardVenueId(venue.id)}
                        >
                          <h3>{venue.name}</h3>
                          <p>會場：{venue.classroom}</p>
                          <p>評審數：{venue.judges.length}</p>
                          <p>專題數：{venue.projects.length}</p>
                          {isOwnVenue && <p className="my-venue-label">我的會場</p>}
                        </button>
                      );
                    })}
                  </div>
                </section>
              </div>
            )}
            <ScoreRanking
              venueId={dashboardVenueId}
              isPresentationMode={isScorePresentationMode}
              onPresentationModeChange={setIsScorePresentationMode}
              isAdmin={isAdminRole(authUser?.role)}
              judgeIdentifierAllowlist={scoreRankingJudgeIdentifiers}
            />
          </div>
        )}

        {authUser && isAdminRole(authUser.role) && currentView === 'score-ranking' && !canSeeDashboard && (
          <div className="container" style={{ width: 'min(95vw, 1440px)' }}>
            <section className="section">
              <h3>跨會場最終評分排名</h3>
              <p>目前尚未建立可查看的會場，請先由工作人員新增會場。</p>
            </section>
          </div>
        )}

        {authUser && currentView === 'admin' && isAdminRole(authUser.role) && renderAdmin()}
      </div>
    </div>
  );
}

export default App;


